/*
 * app.js - Gang Sheet Builder for DTF Transfers Now (v3, oct-2026)
 *
 * Portado del v3 de DTF Transfers Miami (selector por producto, entrada con
 * ?variant= desde la ficha y "Add to cart" con el arte adjunto) y adaptado a Now:
 * catalogo en vivo escrito por Liquid (window.GSB_STOCK), cortes de Same-Day de Now
 * (12 PM ET para enviar, 3 PM ET para recoger en Kendall), plazos por producto,
 * aprobacion "Print As Is" y enlaces de archivo en el formato de UploadKit.
 *
 * Architecture notes for the implementing developer:
 *
 * - World units on the Konva stage are INCHES. The stage scale is the
 *   zoom factor in screen pixels per inch. This keeps every position and
 *   size in real print units, so exporting is just "multiply by 300".
 * - The on-screen preview uses a downscaled raster of each upload (max
 *   1600 px) so dragging stays smooth with large files. The export always
 *   reads from the original full-resolution image element.
 * - Export never builds one giant canvas (a 22x120in sheet at 300 DPI is
 *   237 megapixels, above Firefox/Safari canvas limits). It renders
 *   horizontal strips of 512 rows and streams them into PngEncoder
 *   (js/png-encoder.js), which writes a PNG with real 300 DPI metadata.
 * - Placement (new uploads, copies, Fill sheet) and Auto-arrange all run on
 *   the MaxRects engine in js/packing.js: bottom-left scoring with optional
 *   90-degree rotation, so leftover holes get used and pieces may lie
 *   sideways when that fits better. Locked designs become obstacles.
 * - Editing model: multi-selection (Shift+click / drag a marquee box),
 *   logical groups (shared groupId attr, NOT Konva.Group, so the export and
 *   the sidebar cards stay unchanged), copy/paste via an internal clipboard,
 *   and snapshot-based undo/redo. A snapshot is just the list of item attrs
 *   (inches), so 50 history entries cost almost nothing; the heavy bitmaps
 *   live once in the `sources` map, garbage-collected when neither the
 *   canvas, the history, nor the clipboard references them.
 * - The project autosaves to IndexedDB (js/autosave.js): small meta record
 *   on every change, artwork blobs only once each. On load a banner offers
 *   to restore. No network calls; everything stays in the browser.
 */
(function () {
  'use strict';

  // RAIZ (21-oct-2026): el builder puede vivir en su pagina (raiz = document) o dentro de un
  // contenedor aislado (Shadow DOM) en el pop-up de la landing, que pone window.GSB_RAIZ antes de
  // cargar este archivo. En el segundo caso los elementos se buscan en esa raiz, el foco se lee
  // de ella y los eventos que llegan a document se miran por su objetivo REAL (desde fuera del
  // contenedor todos parecen venir de el). Con el pop-up cerrado o minimizado (GSB_PAUSADO) el
  // builder no atiende teclado, pegar ni soltar archivos: la landing sigue siendo una pagina normal.
  var RAIZ = window.GSB_RAIZ || document;
  var CAPA = RAIZ.host || document.body;   // donde se marca "arrastrando archivos"
  function activo() { return (RAIZ !== document && RAIZ.activeElement) || document.activeElement; }
  function objetivo(e) { return (e.composedPath && e.composedPath()[0]) || e.target; }
  function enPausa() { return !!window.GSB_PAUSADO; }

  // ------------------------------------------------------------ constants
  // SHEET_W_IN is the CURRENT sheet width. It is DERIVED from the chosen sheet type
  // (see TYPES below), never picked on its own: each product is sold at one width.
  var SHEET_W_IN = 22;
  // Sheet lengths offered for the CURRENT type. Filled from GSB_BRAND.types
  // (the live store ladder, see index.html); this list is only the fallback for
  // a brand copy that has no catalog yet.
  var SHEET_HEIGHTS_FALLBACK = [12, 24, 36, 48, 60, 72, 84, 96, 120];
  var SHEET_HEIGHTS = SHEET_HEIGHTS_FALLBACK.slice();
  var DEFAULT_H_IN = 24;                      // starting sheet height (not the shortest option)
  var EXPORT_DPI = 300;
  var PAD_IN = 0.7;                          // stage padding around the sheet (for ruler labels)
  var MIN_ITEM_IN = 0.25;                    // smallest allowed design size
  var PREVIEW_MAX_PX = 1600;                 // preview raster cap (screen only)
  var THUMB_PX = 96;
  var SNAP_STEP_IN = 0.25;
  var MARGIN_IN = 0.2;                       // safety margin kept from every sheet edge by automatic placement
  var EXPORT_STRIP_ROWS = 512;               // 6600 x 512 x 4 bytes = ~13.5 MB per strip
  var ZOOM_MIN = 6, ZOOM_MAX = 100;
  var HISTORY_MAX = 50;                      // undo depth (snapshots are tiny)
  var FILL_MAX = 300;                        // hard cap for "Fill sheet"
  var OVERLAP_EPS = 0.004;                   // inches of real penetration before two designs count as overlapping

  // Brand hooks: each store's index.html can define window.GSB_BRAND before
  // this script loads:
  //   accent    canvas accent color
  //   software  PNG "Software" metadata
  //   types     [{ id, label, handle, url, widthIn, lengths, sameDay?, mejoraDe? }]
  // Colors of the HTML UI are themed via CSS variables in the same file.
  var BRAND = window.GSB_BRAND || {};

  // Interruptor del quitador de fondo. Existe SOLO para poder apagarlo: si la marca
  // no dice nada, la herramienta esta puesta. Se apaga con bgRemover: false en la
  // plantilla, sin tocar codigo ni quitar archivos, para que volver a encenderla sea
  // cambiar esa palabra y subir la plantilla.
  // Apagado el 2-oct-2026 en el tema publicado: el jefe aun no ha confirmado la
  // funcion. El tema de pruebas la mantiene encendida para que pueda evaluarla.
  var QUITAR_FONDO = BRAND.bgRemover !== false;

  // BRAND.types: un TIPO por producto que se puede armar y comprar aqui.
  //
  // Antes esto era BRAND.sheets indexado por ancho, y era una trampa: al bajar de 24"
  // a 22" el cliente creia cambiar una MEDIDA y en realidad cambiaba de PRODUCTO, a
  // otra linea y otro precio, sin que nada se lo dijera. Ahora elige el producto y el
  // ancho sale de el.
  //
  // lengths = [[pulgadas, precio, variantId, disponible], ...]. Cada tipo tiene su
  // propia escalera, asi que el selector de largos se reconstruye en cada cambio de tipo.
  //
  // En Now la escalera NO se copia a mano ni con un script de sincronizar: la plantilla
  // escribe con Liquid window.GSB_STOCK (variantes, precio, disponibilidad y etiquetas de
  // cada producto) cada vez que Shopify sirve la pagina, y aqui se convierte. Asi el
  // builder no puede quedarse con precios viejos (el v2 los tenia del 4-sep) y una
  // variante agotada en la tienda sale agotada aqui.
  // Largo maximo por HOJA: 168" (14 ft), lo mismo que los builders de las fichas (decision
  // del 19-oct-2026; confirmado para este builder el 20-oct). Lo que no cabe pasa a otra hoja.
  var MAX_LARGO_IN = 168;
  // Compra minima por hoja (20-oct-2026, pedido del usuario): 12". Los largos mas cortos de la
  // ficha (las hojas de 6") no se ofrecen aqui; los productos que ya empiezan mas largos
  // (Glitter 24", Max 24 24", Wide 30 36") conservan su minimo. Se ajusta en la plantilla.
  var MIN_LARGO_IN = BRAND.minLargo || 0;
  var STOCK = window.GSB_STOCK || null;

  // '6 in', '1 ft - 12 in', '25 ft - 300 in'. 'Custom Size' no es una medida: null.
  function pulgadasDe(opcion) {
    var m = /(\d+(?:\.\d+)?)\s*in\b/i.exec(String(opcion || ''));
    return m ? parseFloat(m[1]) : null;
  }

  function tiposDesdeStock(tipos) {
    if (!STOCK) return tipos.filter(function (t) { return t.lengths && t.lengths.length; });
    return tipos.map(function (t) {
      var p = STOCK[t.handle];
      if (!p || !p.variants) return null;   // producto borrado o sin publicar: fuera
      if (t.mayoreo) return tipoMayoreo(t, p);
      if (t.porTamano) return tipoPorTamano(t, p);
      var filas = [];
      // t.efecto = POSICION del valor de la 2.a opcion (9 Colors UV: 0 Neon, 1 Standard),
      // no su nombre: en /es/ Shopify sirve los valores traducidos ("Reactivo UV Neon") y
      // el nombre en ingles no casaria. Las posiciones salen del orden de aparicion, que
      // es el orden de los valores en la ficha y no cambia con el idioma.
      var valores2 = [];
      p.variants.forEach(function (v) {
        if (v[4] != null && valores2.indexOf(v[4]) === -1) valores2.push(v[4]);
      });
      var customSize = null;
      p.variants.forEach(function (v) {
        // v = [variantId, precio en centavos, disponible, opcion1, opcion2]
        if (typeof t.efecto === 'number' && valores2.indexOf(v[4]) !== t.efecto) return;
        // 'Custom Size': variante de $0 a la que Easify pone el precio del largo exacto. No
        // es un largo de la lista; se guarda aparte (en 9 Colors UV, la de su efecto).
        if (/^custom size$/i.test(String(v[3] || '').trim())) { customSize = { id: v[0], available: v[2] !== false }; return; }
        var pulg = pulgadasDe(v[3]);
        if (!pulg || pulg > MAX_LARGO_IN || pulg < MIN_LARGO_IN) return;
        filas.push([pulg, v[1] / 100, v[0], v[2] !== false]);
      });
      if (!filas.length) return null;
      filas.sort(function (a, b) { return a[0] - b[0]; });
      var copia = {};
      Object.keys(t).forEach(function (k) { copia[k] = t[k]; });
      copia.lengths = filas;
      copia.agotado = filas.every(function (f) { return !f[3]; });
      // ROLLING CANVAS (largo exacto, 23-oct-2026): se activa SOLO si el producto tiene la variante
      // Custom Size disponible y su conjunto de Easify (BRAND.easify, generado por herramientas/easify.py
      // leyendo la tienda: conjunto, minimo, paso y cada tramo con su precio por pulgada y su id). Son los
      // mismos precios que las variantes de la ficha. Si falta algo, la hoja se redondea al largo de la
      // lista, como antes.
      copia.customSize = customSize;
      var ez = (BRAND.easify || {})[t.handle];
      copia.exacto = (ez && ez.set && ez.tramos && ez.tramos.length && customSize && customSize.available)
        ? { campo: ez.campo || 'sheet-length-in', set: String(ez.set), paso: ez.paso || 1,
            min: ez.min, max: ez.max, tramos: ez.tramos }
        : null;
      // UploadKit nombra el campo del archivo segun la etiqueta del producto: con
      // 'Upload req' la subida es obligatoria y el campo se llama 'Uploaded required';
      // con 'uploader', 'Uploaded file'. Se copia igual para que el taller no note nada.
      var tags = (p.tags || []).map(function (x) { return String(x).toLowerCase(); });
      copia.subida = tags.indexOf('upload req') !== -1 ? 'required' : 'file';
      return copia;
    }).filter(Boolean);
  }

  // WHOLESALE (21-oct-2026): no se vende por hoja sino por el largo TOTAL del pedido (30, 40,
  // 50, 62.5, 75 o 100 ft). Cada hoja sigue siendo de hasta MAX_LARGO_IN y se ajusta sola; el
  // total de todas se redondea a la variante siguiente (precio por variante fija, decision del
  // usuario: el Custom Size de Easify sale mas caro que la variante en largos bajos) y entra al
  // carrito como UNA linea con todas las hojas adjuntas, como los pedidos Wholesale de la ficha.
  function tipoMayoreo(t, p) {
    var esc = [], customSize = null;
    p.variants.forEach(function (v) {
      if (/^custom size$/i.test(String(v[3] || '').trim())) { customSize = { id: v[0], available: v[2] !== false }; return; }
      var pulg = pulgadasDe(v[3]);
      if (pulg) esc.push([pulg, v[1] / 100, v[0], v[2] !== false]);
    });
    if (!esc.length) return null;
    esc.sort(function (a, b) { return a[0] - b[0]; });
    var copia = {};
    Object.keys(t).forEach(function (k) { copia[k] = t[k]; });
    copia.escalaTotal = esc;
    copia.minTotal = esc[0][0];
    copia.maxTotal = esc[esc.length - 1][0];
    copia.lengths = [];   // largos POR HOJA para la lista (el precio va por el total)
    for (var h = Math.max(12, MIN_LARGO_IN); h <= MAX_LARGO_IN; h += 12) copia.lengths.push([h, null, null, true]);
    copia.agotado = esc.every(function (r) { return !r[3]; });
    copia.customSize = customSize;
    copia.exacto = null;
    var tags = (p.tags || []).map(function (x) { return String(x).toLowerCase(); });
    copia.subida = tags.indexOf('upload req') !== -1 ? 'required' : 'file';
    return copia;
  }

  // BY SIZE (21-oct-2026, demo): se cobra POR DISENO (area x piezas) y no por hoja; el builder
  // arma las hojas igual para el taller. Variantes fijas por tramo de piezas DEL MISMO DISENO
  // ("Per 10 sq in", "Per 10 sq in · 15+ pieces"...; Shopify solo admite centimos y -10 % de $0.05
  // seria $0.045, por eso la unidad es 10 in2) y "Precut · per piece". Tarifas = Paquete 10.
  function tipoPorTamano(t, p) {
    var tramos = [], corte = null, unidad = 0;
    p.variants.forEach(function (v) {
      var txt = String(v[3] || '');
      if (/precut/i.test(txt)) { corte = { price: v[1] / 100, variantId: v[0], available: v[2] !== false }; return; }
      var u = /per\s+(\d+(?:\.\d+)?)\s*sq\s*in/i.exec(txt);
      if (!u) return;
      unidad = parseFloat(u[1]);
      var m = /(\d+)\s*\+\s*pieces/i.exec(txt);
      tramos.push({ min: m ? parseInt(m[1], 10) : 1, price: v[1] / 100, variantId: v[0], available: v[2] !== false });
    });
    if (!tramos.length || !unidad) return null;
    tramos.sort(function (a, b) { return a.min - b.min; });
    var copia = {};
    Object.keys(t).forEach(function (k) { copia[k] = t[k]; });
    copia.tramosTam = tramos;
    copia.unidadIn2 = unidad;
    copia.corte = corte;
    copia.lengths = [];   // largos POR HOJA solo para la lista: el precio no depende de la hoja
    for (var h = Math.max(12, MIN_LARGO_IN); h <= MAX_LARGO_IN; h += 12) copia.lengths.push([h, null, null, true]);
    copia.agotado = !tramos[0].available;
    copia.exacto = null;
    var tags = (p.tags || []).map(function (x) { return String(x).toLowerCase(); });
    copia.subida = tags.indexOf('upload req') !== -1 ? 'required' : 'file';
    return copia;
  }

  // Precio by size de un conjunto de disenos agrupados ({ s, w, h, n }): cada diseno cae en el tramo
  // de SUS piezas y se cobra en unidades enteras de 10 in2. Una linea por diseno (como Kixxl, que crea
  // una variante por diseno): asi lo que se ve junto a cada diseno es exactamente lo que se cobra.
  function precioPorTamano(t, grupos, conCorte) {
    var base = t.tramosTam[0].price;
    var lineas = [], total = 0, piezas = 0;
    grupos.forEach(function (g) {
      var tr = t.tramosTam[0];
      t.tramosTam.forEach(function (x) { if (g.n >= x.min) tr = x; });
      var unidades = Math.max(1, Math.ceil(g.w * g.h * g.n / t.unidadIn2 - 1e-9));
      g.tramo = tr;
      g.desc = Math.round((1 - tr.price / base) * 100);
      g.unidades = unidades;
      g.precio = Math.round(unidades * tr.price * 100) / 100;
      piezas += g.n;
      total += g.precio;
      lineas.push({ tramo: tr, unidades: unidades, total: g.precio, disenos: [g] });
    });
    var corte = conCorte && t.corte ? { piezas: piezas, total: Math.round(piezas * t.corte.price * 100) / 100 } : null;
    if (corte) total += corte.total;
    var disponible = lineas.every(function (l) { return l.tramo.available; }) && (!corte || t.corte.available);
    return { lineas: lineas, corte: corte, piezas: piezas, total: Math.round(total * 100) / 100, disponible: disponible };
  }

  // La variante de mayoreo que cubre totalIn pulgadas (la siguiente que lo contiene).
  function ofertaMayoreo(t, totalIn) {
    if (!t || !t.escalaTotal) return null;
    for (var i = 0; i < t.escalaTotal.length; i++) {
      var r = t.escalaTotal[i];
      if (r[0] >= totalIn - 1e-9) {
        return { typeId: t.id, label: t.label, price: r[1], variantId: r[2], available: r[3],
                 subida: t.subida || 'file', approve: t.approve || APROBACION_ESTANDAR, facturadoIn: r[0],
                 mayoreo: true, url: (BRAND.store || 'https://dtftransfersnow.com') + t.url + '?variant=' + r[2] };
      }
    }
    return null;   // mas largo que la variante mayor
  }

  var TYPES = (BRAND.types && BRAND.types.length) ? tiposDesdeStock(BRAND.types.slice()) : [];
  // Se abre en el primer producto que se puede comprar, no en uno agotado.
  var typeId = TYPES.length ? TYPES[0].id : null;
  for (var iTipo = 0; iTipo < TYPES.length; iTipo++) {
    if (!TYPES[iTipo].agotado) { typeId = TYPES[iTipo].id; break; }
  }

  function typeById(id) {
    for (var i = 0; i < TYPES.length; i++) {
      if (TYPES[i].id === id) return TYPES[i];
    }
    return null;
  }

  function currentType() { return typeById(typeId); }

  // Entrada desde una ficha de producto. El boton de alla trae ?variant=<id>, asi que
  // el builder abre con ese producto y esa medida ya puestos y el cliente no tiene que
  // volver a elegir lo que ya eligio. Si el id no esta en el catalogo (una variante
  // nueva, un producto que el builder no arma, o alguien jugando con la URL) no se
  // fuerza nada: se abre con el tipo por defecto.
  function tipoYLargoDeVariante(id) {
    if (!id) return null;
    for (var i = 0; i < TYPES.length; i++) {
      var t = TYPES[i];
      for (var j = 0; j < t.lengths.length; j++) {
        if (String(t.lengths[j][2]) === String(id)) {
          return { type: t, lenIn: t.lengths[j][0] };
        }
      }
      if (t.escalaTotal) {
        for (var k = 0; k < t.escalaTotal.length; k++) {
          if (String(t.escalaTotal[k][2]) === String(id)) return { type: t, lenIn: null };
        }
      }
      if (t.tramosTam) {
        var ids = t.tramosTam.map(function (x) { return String(x.variantId); });
        if (t.corte) ids.push(String(t.corte.variantId));
        if (ids.indexOf(String(id)) !== -1) return { type: t, lenIn: null };
      }
    }
    // Una variante del producto que no esta en la lista del builder (la Custom Size, o un largo de mas
    // de 168" o de menos de 12"): se abre ESE producto en largo automatico, no otro (auditoria 23-oct;
    // antes abria Custom y quien venia de Glitter podia acabar pidiendo Custom). En 9 Colors UV, el tipo
    // del efecto de esa variante (posicion del valor de la 2.a opcion).
    for (var a = 0; a < TYPES.length; a++) {
      var tt = TYPES[a], pr = STOCK && STOCK[tt.handle];
      if (!pr || !pr.variants) continue;
      var valores2 = [], suya = null;
      pr.variants.forEach(function (v) {
        if (v[4] != null && valores2.indexOf(v[4]) === -1) valores2.push(v[4]);
        if (String(v[0]) === String(id)) suya = v;
      });
      if (!suya) continue;
      if (typeof tt.efecto === 'number' && valores2.indexOf(suya[4]) !== tt.efecto) continue;
      return { type: tt, lenIn: null };
    }
    return null;
  }

  function varianteDeLaURL() {
    var q = (window.location && window.location.search) || '';
    var m = /[?&]variant=(\d+)/.exec(q);
    return m ? m[1] : null;
  }

  function heightsForType(t) {
    if (t && t.lengths && t.lengths.length) {
      return t.lengths.map(function (row) { return row[0]; });
    }
    return SHEET_HEIGHTS_FALLBACK.slice();
  }

  if (currentType()) SHEET_W_IN = currentType().widthIn || SHEET_W_IN;

  // Producto con cobro exacto y la hoja en automatico: la hoja mide lo justo.
  function largoExacto() { var t = currentType(); return !!(largoAuto && t && (t.exacto || t.mayoreo || t.porTamano)); }

  function maxAltura() {
    // con cobro exacto la hoja llega al maximo por hoja (168") aunque la lista de largos acabe antes,
    // pero nunca pasa del maximo que el producto vende en Easify (auditoria 23-oct: Glow, ORGB y
    // 9 Colors UV llegan a 96"; Fluorescent y Glitter a 120"). Lo que no cabe pasa a otra hoja.
    if (!largoExacto()) return SHEET_HEIGHTS[SHEET_HEIGHTS.length - 1];
    var t = currentType();
    return (t.exacto && t.exacto.max) ? Math.min(MAX_LARGO_IN, t.exacto.max) : MAX_LARGO_IN;
  }

  // Alto en el que se COLOCA (subidas, copias, acomodo): en automatico, todo el largo que
  // vende el producto, para que nada caiga "fuera de la hoja"; luego la hoja se ajusta.
  function alturaTrabajo() { return largoAuto ? maxAltura() : sheetHIn; }

  // Zona extra que se dibuja bajo la hoja en automatico (si aun puede crecer).
  function extraAuto() { return (largoAuto && sheetHIn < maxAltura()) ? EXTRA_AUTO_IN : 0; }

  // El largo que toca ahora en automatico. Vacia, la hoja mas corta del producto (lo mismo
  // que hace Kixxl con su altura inicial).
  function largoQueToca() {
    var nodos = itemNodes();
    var t = currentType();
    if (largoExacto()) {
      // la regla del deslizador de Easify: minimo, paso (de 2 en 2 en Same-Day) y tope por hoja
      var e = t.exacto || { paso: 2, min: SHEET_HEIGHTS[0] || 12 };
      var necesita = nodos.length ? contentBottom() + MARGIN_IN : 0;
      var L = Math.ceil((necesita - 1e-9) / e.paso) * e.paso;
      return Math.min(maxAltura(), Math.max(e.min, MIN_LARGO_IN, L));
    }
    if (!nodos.length) return SHEET_HEIGHTS[0];
    return nearestHeight(contentBottom() + MARGIN_IN);
  }

  function textoOpcionAuto() { return 'Auto: ' + sheetHIn + '"'; }

  // Ajusta la hoja al contenido si esta en automatico. Devuelve true si cambio.
  function ajustarLargo() {
    if (!largoAuto || !SHEET_HEIGHTS.length) return false;
    var h = largoQueToca();
    var cambia = h !== sheetHIn;
    sheetHIn = h;
    var opt = el.sheetSelect && el.sheetSelect.querySelector('option[value="auto"]');
    if (opt) opt.textContent = textoOpcionAuto();
    if (cambia) applyZoom(zoom);
    return cambia;
  }

  // Closest offered length that still holds what the customer already placed:
  // the first one >= h, or the longest sheet when nothing is long enough.
  function nearestHeight(h) {
    for (var i = 0; i < SHEET_HEIGHTS.length; i++) {
      if (SHEET_HEIGHTS[i] >= h - 1e-9) return SHEET_HEIGHTS[i];
    }
    return SHEET_HEIGHTS[SHEET_HEIGHTS.length - 1];
  }

  // Precio y variante exactos de la hoja que el cliente acaba de construir. Salen de
  // la MISMA escalera que alimenta el selector de largos, asi que lo que ensena el
  // builder es lo que cobra el checkout. Si la medida no esta en el catalogo no se
  // inventa nada: devuelve null y el modal no ensena precio.
  function offerFor(t, h) {
    if (!t || !t.lengths) return null;
    for (var i = 0; i < t.lengths.length; i++) {
      if (t.lengths[i][0] === h) {
        return {
          typeId: t.id,
          label: t.label,
          price: t.lengths[i][1],
          variantId: t.lengths[i][2],
          // agotada en la tienda: se puede armar y descargar, no pedir
          available: t.lengths[i][3] !== false,
          // lo que el pedido necesita para que el taller lo lea como siempre
          subida: t.subida || 'file',
          approve: t.approve || APROBACION_ESTANDAR,
          url: (BRAND.store || 'https://dtftransfersnow.com') + t.url +
               '?variant=' + t.lengths[i][2]
        };
      }
    }
    return null;
  }

  // Precio del LARGO EXACTO por la variante Custom Size, con los mismos datos que manda la
  // ficha a Easify (leidos del formulario de Same-Day el 20-oct-2026): Easify toma el precio
  // de _po:items:addon y lo pone en la linea. OJO: Easify NO comprueba ese precio (probado
  // en un carrito de prueba: 162 in con price 1 entro a $1.00), asi que aqui se calcula con
  // la misma regla que tiene configurada Easify y nunca se acepta un precio de fuera.
  function ofertaExacta(t, h) {
    if (!t || !t.exacto || !t.customSize) return null;
    var e = t.exacto;
    if (h < e.min - 1e-9 || (e.max && h > e.max + 1e-9)) return null;   // fuera de lo que vende la ficha
    var tr = tramoDe(e, h);
    var precio = Math.round(h * tr[2] * 100) / 100;
    var texto = h + ' in ($' + precio.toFixed(2) + ')';
    var props = {};
    props[e.campo] = texto;
    props['_tpo_add_by'] = e.set;
    var vista = {};
    vista[e.campo] = texto;
    props['_po:items:properties'] = JSON.stringify(vista);
    props['_po:items:addon'] = JSON.stringify([[{ price: precio, variantQty: 1,
      properties: [{ key: '_tpo_option_value_id', value: String(tr[3]) }] }]]);
    return {
      typeId: t.id, label: t.label, price: precio,
      variantId: t.customSize.id, available: t.customSize.available,
      subida: t.subida || 'file', approve: t.approve || APROBACION_ESTANDAR,
      exacto: true, propsEasify: props,
      url: (BRAND.store || 'https://dtftransfersnow.com') + t.url + '?variant=' + t.customSize.id
    };
  }

  // Tramos POR VOLUMEN, como Easify (pricingLogic "tiered") y el Rolling Canvas de Kixxl: todo el largo
  // se cobra a la tarifa del tramo en el que cae. tramos = [[desde, hasta, $ por pulgada, id del tramo]];
  // el id va en el carrito (_tpo_option_value_id): con el de otro tramo Easify cobraria otra cosa.
  function tramoDe(e, h) {
    for (var i = 0; i < e.tramos.length; i++) {
      if (h <= e.tramos[i][1] + 1e-9) return e.tramos[i];
    }
    return e.tramos[e.tramos.length - 1];
  }

  // La oferta de una hoja de h pulgadas: la variante de ese largo si existe; si no (largo
  // exacto), la Custom Size. Sin ninguna de las dos no hay precio y no se inventa.
  function ofertaHoja(t, h) {
    return offerFor(t, h) || ofertaExacta(t, h);
  }

  function sheetOffer(h) { return ofertaHoja(currentType(), h); }

  // Same-Day es un PRODUCTO propio con su ficha y sus variantes, NO un extra que se
  // añade: elegirlo cambia que producto entra al carrito, no suma una linea. Por eso
  // esta en el desplegable de tipos como uno mas.
  //
  // Esta funcion es para el otro camino: al que ya eligio un tipo normal se le ofrece
  // al final cambiarse. Solo aplica si el tipo actual esta en la lista mejoraDe del
  // same-day (en Now, Custom y Hot Peel, las dos hojas estandar de 22") y si existe el
  // mismo largo en su escalera. Si el cliente ya eligio same-day, devuelve null: no se
  // ofrece lo que ya tiene. Y no cuesta lo mismo (a $6,00 el pie plano: a 12" sale mas
  // barato que la estandar y de 24" en adelante mas caro), por eso el modal ensena el
  // precio de verdad en vez de prometer que sale gratis.
  function sameDayType() {
    for (var i = 0; i < TYPES.length; i++) {
      var t = TYPES[i];
      if (t.sameDay && !t.agotado && t.mejoraDe && t.mejoraDe.indexOf(typeId) !== -1) return t;
    }
    return null;
  }

  function sameDayOffer(h) {
    var o = ofertaHoja(sameDayType(), h);
    return (o && o.available) ? o : null;   // agotado no se ofrece como alternativa
  }

  function money(n) { return '$' + n.toFixed(2); }

  // Lo que el cliente aprueba antes de pedir, y lo que viaja en la linea del pedido
  // (propiedad 'Approve'). La hoja armada aqui se imprime TAL CUAL (decision del
  // 20-oct-2026, tambien en Same-Day), igual que el "My file is print-ready." de las
  // fichas. El taller clasifica la linea por esas palabras: no meter "review" aqui.
  var APROBACION_ESTANDAR = 'My sheet is print-ready. Print it exactly as I built it.';
  // By size no tiene hoja que aprobar: se aprueban los disenos tal como se subieron (sin "review":
  // el script del taller lo leeria como revision).
  var APROBACION_TAMANO = 'My designs are print-ready. Print them exactly as I uploaded them.';

  // Momento actual en ET: minutos desde medianoche y dia de la semana (0 = domingo).
  // El taller vive en ET todo el ano y los cortes del same-day se miden ahi, no en la
  // hora del cliente: alguien en California a las 10 AM ya paso el corte de las 12 PM ET.
  function ahoraET() {
    var DIAS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    try {
      var partes = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', weekday: 'short',
        hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(new Date());
      var h = NaN, m = NaN, dia = null;
      partes.forEach(function (p) {
        if (p.type === 'hour') h = parseInt(p.value, 10) % 24;
        if (p.type === 'minute') m = parseInt(p.value, 10);
        if (p.type === 'weekday') dia = DIAS[p.value];
      });
      if (dia === null || isNaN(h) || isNaN(m)) return null;
      return { min: h * 60 + m, dia: dia };
    } catch (err) {
      return null;   // sin zona horaria fiable no se promete nada
    }
  }

  var CORTE_ENVIO = 12 * 60;     // 12 PM ET: sale ese dia por UPS
  var CORTE_RECOGIDA = 15 * 60;  // 3 PM ET: recogida gratis en Kendall ese dia
  var NOMBRE_DIA = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Proximo dia habil desde 'dia' sin contar hoy. Solo lunes a viernes: el same-day
  // no corre en fin de semana. No sabe de festivos (los cierres del taller los avisa la
  // ficha con el metaobjeto cierre_taller).
  function proximoHabil(dia) {
    var d = (dia + 1) % 7;
    for (var i = 0; i < 7; i++) {
      if (d >= 1 && d <= 5) return d;
      d = (d + 1) % 7;
    }
    return 1;
  }

  // Que se le puede prometer al same-day AHORA MISMO, sin mentir.
  //
  // Reglas de Now (brand-qa, regla 5): CORTE DOBLE, de lunes a viernes. Antes de las
  // 12 PM ET sale ese dia; antes de las 3 PM ET se recoge gratis en Kendall ese dia.
  // El v2 no miraba el dia de la semana: un sabado a las 10 AM prometia "Ships today".
  function estadoSameDay() {
    var t = ahoraET();
    var generico = {
      titulo: 'Same-day',
      nota: 'Order by 12 PM ET, Monday to Friday, to ship the same day, or by 3 PM ET for free pickup in Kendall.'
    };
    if (!t) return generico;

    var habil = t.dia >= 1 && t.dia <= 5;
    if (habil && t.min < CORTE_ENVIO) {
      var faltan = CORTE_ENVIO - t.min;
      var hh = Math.floor(faltan / 60), mm = faltan % 60;
      var queda = hh ? (hh + 'h ' + mm + 'm') : (mm + ' min');
      return {
        titulo: 'Ships today',
        nota: 'The 12 PM ET shipping cutoff is ' + queda + ' away. Free pickup in Kendall until 3 PM ET.'
      };
    }
    if (habil && t.min < CORTE_RECOGIDA) {
      return {
        titulo: 'Pick it up today in Kendall',
        nota: 'The 12 PM ET shipping cutoff has passed, but free pickup in Kendall is open until 3 PM ET.'
      };
    }

    var siguiente = NOMBRE_DIA[proximoHabil(t.dia)];
    return {
      titulo: 'Same-day',
      nota: (habil ? 'Today\'s cutoffs have passed. ' : 'Same-day runs Monday to Friday. ') +
        'Order by 12 PM ET on ' + siguiente + ' to ship that day, or by 3 PM ET for free pickup in Kendall.'
    };
  }

  // Plazo del producto elegido, tal como lo dice su ficha (brand-qa, regla 7).
  function plazoDe(t) {
    if (t && t.sameDay) return estadoSameDay().nota;
    return (t && t.plazo) || 'Ships within 24 hours, Monday to Friday.';
  }

  SHEET_HEIGHTS = heightsForType(currentType());
  var ACCENT = BRAND.accent || '#029fda';
  var DANGER = '#dc2626';
  var WARN = '#ea580c';                      // overlap stroke
  var GUIDE = '#e11d8f';                     // smart alignment guides

  // iPadOS reports platform 'MacIntel'; the touch-points check catches it
  var IS_IOS = /iP(hone|ad|od)/.test(navigator.platform) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // ---------------------------------------------------------------- state
  var sheetHIn = DEFAULT_H_IN;
  // LARGO AUTOMATICO ("rolling", decidido el 20-oct-2026): la hoja no se elige, se ajusta
  // sola al largo mas corto que VENDE el producto y que contiene todos los disenos. Crece
  // al anadir y se encoge al quitar. El precio es el de esa variante, el mismo que en su
  // ficha (no se cobra la pulgada exacta: eso necesitaria productos o precios nuevos).
  // Si el cliente elige un largo en la lista, o entra desde una ficha con ?variant=, ese
  // largo manda y el modo automatico se apaga hasta que vuelva a elegir "Auto".
  var largoAuto = true;
  // Producto con el que se entro desde una ficha (?variant=). Si el cliente recupera una hoja
  // guardada, manda ESTE producto y no el de la hoja guardada (21-oct-2026: el usuario entro por el
  // enlace de by size, recupero su hoja anterior y el builder volvio a Custom).
  var tipoDeEntrada = null;
  var EXTRA_AUTO_IN = 12;   // zona bajo la hoja donde soltar un diseno para alargarla
  var zoom = 20;                             // px per inch, recalculated on load (fit)
  var padIn = PAD_IN;                        // current stage padding, grows at low zoom
  var seq = 0;                               // shared id counter (sources, items, groups)
  var sources = new Map();                   // sourceId -> source record
  var selection = [];                        // selected Konva nodes (locked ones never enter)
  var overlapSet = new Set();                // nodes currently overlapping another design
  var clipboard = null;                      // internal clipboard: { items: [snapshots] }
  var undoStack = [];                        // top = current state
  var redoStack = [];
  var exporting = false;
  var exportCancelled = false;

  // ------------------------------------------------------------- elements
  var el = {
    scroll: RAIZ.getElementById('canvas-scroll'),
    holder: RAIZ.getElementById('stage-holder'),
    fileInput: RAIZ.getElementById('file-input'),
    uploadBtn: RAIZ.getElementById('upload-btn'),
    itemList: RAIZ.getElementById('item-list'),
    emptyState: RAIZ.getElementById('empty-state'),
    typeSelect: RAIZ.getElementById('type-select'),
    sheetTabs: RAIZ.getElementById('sheet-tabs'),
    sheetWidth: RAIZ.getElementById('sheet-width'),
    appHeader: RAIZ.getElementById('app-header'),
    appMain: RAIZ.getElementById('app-main'),
    modeNote: RAIZ.getElementById('mode-note'),
    sheetSelect: RAIZ.getElementById('sheet-select'),
    sheetInfoBtn: RAIZ.getElementById('sheet-info-btn'),
    sheetInfoPop: RAIZ.getElementById('sheet-info-pop'),
    selectionBar: RAIZ.getElementById('selection-bar'),

    // dialogo de quitar fondo blanco (portado del lab, 10-sep-2026)
    bgOverlay: RAIZ.getElementById('bg-overlay'),
    bgTitle: RAIZ.getElementById('bg-title'),
    bgPreview: RAIZ.getElementById('bg-preview'),
    bgTol: RAIZ.getElementById('bg-tol'),
    bgTolVal: RAIZ.getElementById('bg-tol-val'),
    bgCancel: RAIZ.getElementById('bg-cancel'),
    bgApply: RAIZ.getElementById('bg-apply'),
    bgClose: RAIZ.getElementById('bg-close'),
    bgZoomIn: RAIZ.getElementById('bg-zoom-in'),
    bgZoomOut: RAIZ.getElementById('bg-zoom-out'),
    bgZoomFit: RAIZ.getElementById('bg-zoom-fit'),
    bgZoomVal: RAIZ.getElementById('bg-zoom-val'),
    bgModePan: RAIZ.getElementById('bg-mode-pan'),
    bgModePick: RAIZ.getElementById('bg-mode-pick'),
    bgAi: RAIZ.getElementById('bg-ai'),
    bgColorChip: RAIZ.getElementById('bg-color-chip'),
    bgColorHex: RAIZ.getElementById('bg-color-hex'),
    bgModeKeep: RAIZ.getElementById('bg-mode-keep'),
    bgModeRemove: RAIZ.getElementById('bg-mode-remove'),
    bgModeErase: RAIZ.getElementById('bg-mode-erase'),
    bgBrushSize: RAIZ.getElementById('bg-brush-size'),
    bgBrushVal: RAIZ.getElementById('bg-brush-val'),
    bgClearMarks: RAIZ.getElementById('bg-clear-marks'),
    bgUndo: RAIZ.getElementById('bg-undo'),
    bgRedo: RAIZ.getElementById('bg-redo'),
    bgShowMarks: RAIZ.getElementById('bg-show-marks'),
    garmentBtn: RAIZ.getElementById('garment-btn'),
    garmentChip: RAIZ.getElementById('garment-chip'),
    garmentPop: RAIZ.getElementById('garment-pop'),
    garmentSwatches: RAIZ.getElementById('garment-swatches'),
    garmentCustom: RAIZ.getElementById('garment-custom'),
    addedOverlay: RAIZ.getElementById('added-overlay'),
    addedWhat: RAIZ.getElementById('added-what'),
    addedCart: RAIZ.getElementById('added-cart'),
    addedMore: RAIZ.getElementById('added-more'),
    addedCheckout: RAIZ.getElementById('added-checkout'),
    arrangeSettingsBtn: RAIZ.getElementById('arrange-settings-btn'),
    arrangePop: RAIZ.getElementById('arrange-pop'),
    sheetInfoList: RAIZ.getElementById('sheet-info-list'),
    sizeGuideBtn: RAIZ.getElementById('size-guide-btn'),
    sizeGuidePop: RAIZ.getElementById('size-guide-pop'),
    sizeGuideList: RAIZ.getElementById('size-guide-list'),
    spacingInput: RAIZ.getElementById('spacing-input'),
    snapCheck: RAIZ.getElementById('snap-check'),
    arrangeBtn: RAIZ.getElementById('arrange-btn'),
    undoBtn: RAIZ.getElementById('undo-btn'),
    redoBtn: RAIZ.getElementById('redo-btn'),
    dupBtn: RAIZ.getElementById('dup-btn'),
    fillBtn: RAIZ.getElementById('fill-btn'),
    alignLeftBtn: RAIZ.getElementById('align-left-btn'),
    alignTopBtn: RAIZ.getElementById('align-top-btn'),
    centerBtn: RAIZ.getElementById('center-btn'),
    distHBtn: RAIZ.getElementById('dist-h-btn'),
    distVBtn: RAIZ.getElementById('dist-v-btn'),
    helpBtn: RAIZ.getElementById('help-btn'),
    helpOverlay: RAIZ.getElementById('help-overlay'),
    helpClose: RAIZ.getElementById('help-close'),
    groupBtn: RAIZ.getElementById('group-btn'),
    deleteBtn: RAIZ.getElementById('delete-btn'),
    clearBtn: RAIZ.getElementById('clear-btn'),
    rotateBtn: RAIZ.getElementById('rotate-btn'),
    zoomInBtn: RAIZ.getElementById('zoom-in'),
    zoomOutBtn: RAIZ.getElementById('zoom-out'),
    zoomFitBtn: RAIZ.getElementById('zoom-fit'),
    zoomLevelBtn: RAIZ.getElementById('zoom-level'),
    exportBtn: RAIZ.getElementById('export-btn'),
    statusbar: RAIZ.getElementById('statusbar'),
    statusText: RAIZ.getElementById('status-text'),
    statusHint: RAIZ.getElementById('status-hint'),
    overlay: RAIZ.getElementById('export-overlay'),
    overlayTitle: RAIZ.getElementById('overlay-title'),
    overlaySub: RAIZ.getElementById('overlay-sub'),
    progressFill: RAIZ.getElementById('progress-fill'),
    progressText: RAIZ.getElementById('progress-text'),
    cancelExportBtn: RAIZ.getElementById('cancel-export'),
    summaryOverlay: RAIZ.getElementById('summary-overlay'),
    summaryRows: RAIZ.getElementById('summary-rows'),
    summaryWarnings: RAIZ.getElementById('summary-warnings'),
    summaryCancel: RAIZ.getElementById('summary-cancel'),
    summaryDownload: RAIZ.getElementById('summary-download'),
    summaryOffer: RAIZ.getElementById('summary-offer'),
    summaryActions: RAIZ.querySelector('#summary-overlay .summary-actions'),
    trimOverlay: RAIZ.getElementById('trim-overlay'),
    trimPreview: RAIZ.getElementById('trim-preview'),
    trimText: RAIZ.getElementById('trim-text'),
    trimYes: RAIZ.getElementById('trim-yes'),
    trimNo: RAIZ.getElementById('trim-no'),
    restoreBanner: RAIZ.getElementById('restore-banner'),
    restoreText: RAIZ.getElementById('restore-text'),
    restoreYes: RAIZ.getElementById('restore-yes'),
    restoreNo: RAIZ.getElementById('restore-no'),
    toast: RAIZ.getElementById('toast')
  };

  // ---------------------------------------------------------- Konva setup
  var stage = new Konva.Stage({
    container: RAIZ.getElementById('stage-holder'),   // el elemento, no el id: en un Shadow DOM Konva no lo encontraria
    width: 300,
    height: 300
  });
  var gridLayer = new Konva.Layer({ listening: false });
  var itemLayer = new Konva.Layer();
  var uiLayer = new Konva.Layer();
  stage.add(gridLayer, itemLayer, uiLayer);

  var transformer = new Konva.Transformer({
    rotateEnabled: true,
    rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
    // corners resize proportionally (keepRatio); the side handles stretch the
    // art out of proportion ON PURPOSE — the card then shows a "Stretched"
    // badge with a one-click fix, and the export summary warns about it
    enabledAnchors: ['top-left', 'top-center', 'top-right', 'middle-left',
      'middle-right', 'bottom-left', 'bottom-center', 'bottom-right'],
    keepRatio: true,
    flipEnabled: false,
    anchorSize: 11,
    anchorCornerRadius: 3,
    anchorStroke: ACCENT,
    anchorFill: '#ffffff',
    borderStroke: ACCENT,
    // the rotation handle is easy to miss as a small hollow square; pull it
    // closer to the design and paint it as a solid blue dot so it reads as a
    // handle of its own
    rotateAnchorOffset: 30,
    rotateAnchorCursor: 'grab',
    anchorStyleFunc: function (anchor) {
      if (!anchor.hasName('rotater')) return;
      anchor.setAttrs({
        width: 17, height: 17, offsetX: 8.5, offsetY: 8.5,
        cornerRadius: 8.5, fill: ACCENT, stroke: '#ffffff', strokeWidth: 1.5
      });
    },
    // nodes carry a 2-unit warning stroke (world units = inches); without this the
    // transformer frame would inflate by 1 inch per side while the stroke is on
    ignoreStroke: true,
    // newBox is in absolute (screen) pixels. Reject only boxes that are both
    // below the minimum AND shrinking, so thin items can still rotate and grow.
    boundBoxFunc: function (oldBox, newBox) {
      var minPx = MIN_ITEM_IN * stage.scaleX();
      var tooSmall = Math.abs(newBox.width) < minPx || Math.abs(newBox.height) < minPx;
      var shrinking = Math.abs(newBox.width) < Math.abs(oldBox.width) - 1e-6 ||
        Math.abs(newBox.height) < Math.abs(oldBox.height) - 1e-6;
      if (tooSmall && shrinking) return oldBox;
      return newBox;
    }
  });
  uiLayer.add(transformer);

  // checkerboard pattern to signal that the sheet background is transparent
  // ---------------------------------------------- color de prenda (SOLO vista previa)
  // Ver el arte sobre negro o marino cambia la decision de compra en DTF, sobre todo
  // por la tinta blanca. Pero es una SIMULACION: el fondo no se imprime nunca.
  //
  // Que no se imprima no depende de acordarse de quitarlo: la exportacion
  // (renderSheetBlob) solo dibuja los nodos de arte sobre tiras transparentes y no
  // toca gridLayer, que es donde vive este color. Es imposible que se cuele en el PNG.
  // Nombres y tonos de catalogo de prenda (linea Gildan/Bella), no colores inventados:
  // el cliente reconoce "Heliconia" o "Carolina blue" porque es lo que pide su
  // proveedor de camisetas. Ordenados neutros primero y luego color, 5 por fila.
  var COLORES_PRENDA = [
    { n: 'White', c: '#ffffff' },
    { n: 'Natural', c: '#e9dfc9' },
    { n: 'Sport grey', c: '#9b9b9b' },
    { n: 'Charcoal', c: '#4a4a4a' },
    { n: 'Black', c: '#131313' },

    { n: 'Navy', c: '#1e2a44' },
    { n: 'Royal blue', c: '#1f4e9c' },
    { n: 'Carolina blue', c: '#7ba4db' },
    { n: 'Purple', c: '#4f2170' },
    { n: 'Forest green', c: '#1f4032' },

    { n: 'Safety green', c: '#c6d92e' },
    { n: 'Red', c: '#b1252b' },
    { n: 'Maroon', c: '#5c1a24' },
    { n: 'Orange', c: '#e26e28' },
    { n: 'Heliconia', c: '#db3e79' }
  ];
  // Tres niveles de cuadros, no uno. Los cuadros claros de siempre esconden el arte
  // blanco o muy claro, que en DTF es justo lo que mas hay que poder ver. Los tres
  // significan lo mismo (fondo transparente), solo cambia el contraste.
  var CUADROS = [
    { n: 'Transparent', a: '#ffffff', b: '#eef1f5' },
    { n: 'Transparent, grey', a: '#cfd6de', b: '#bcc4ce' },
    { n: 'Transparent, dark', a: '#7c848f', b: '#6b727c' },
    // Los dos ultimos no imitan ninguna prenda: son para REVISAR. Un recuadro blanco
    // que el cliente no quito, o un halo de mala recorte, sobre cuadros claros no se
    // ve y llega al taller impreso. Sobre magenta o cian salta a la vista.
    { n: 'Check edges, magenta', a: '#ff2ec0', b: '#e81fae' },
    { n: 'Check edges, cyan', a: '#00c8e0', b: '#00b2c7' }
  ];

  // { tipo: 'cuadros', i } o { tipo: 'color', c }
  var fondo = { tipo: 'cuadros', i: 0 };

  function hacerCuadros(a, b) {
    var c = document.createElement('canvas');
    c.width = 32; c.height = 32;
    var ctx = c.getContext('2d');
    ctx.fillStyle = a; ctx.fillRect(0, 0, 32, 32);
    ctx.fillStyle = b; ctx.fillRect(0, 0, 16, 16); ctx.fillRect(16, 16, 16, 16);
    return c;
  }

  var lienzosCuadros = CUADROS.map(function (g) { return hacerCuadros(g.a, g.b); });

  // CSS del patron para las muestras del popover y para el boton
  function cssCuadros(g, px) {
    var t = px || 8;
    return {
      backgroundColor: g.a,
      backgroundImage: 'linear-gradient(45deg, ' + g.b + ' 25%, transparent 25%, transparent 75%, ' + g.b + ' 75%),' +
                       'linear-gradient(45deg, ' + g.b + ' 25%, transparent 25%, transparent 75%, ' + g.b + ' 75%)',
      backgroundSize: t + 'px ' + t + 'px',
      backgroundPosition: '0 0, ' + (t / 2) + 'px ' + (t / 2) + 'px'
    };
  }

  function pintarFondoEn(elm, px) {
    if (!elm) return;
    if (fondoEsColor()) {
      elm.style.backgroundImage = 'none';
      elm.style.background = fondo.c;
      return;
    }
    var css = cssCuadros(CUADROS[fondo.i], px);
    Object.keys(css).forEach(function (k) { elm.style[k] = css[k]; });
  }

  function fondoEsColor() { return fondo.tipo === 'color'; }

  // ------------------------------------------------------------ utilities
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function round4(v) { return Math.round(v * 10000) / 10000; }

  function hexToRgba(hex, a) {
    var v = parseInt(hex.slice(1), 16);
    return 'rgba(' + (v >> 16 & 255) + ',' + (v >> 8 & 255) + ',' + (v & 255) + ',' + a + ')';
  }

  function toast(msg, ms) {
    el.toast.textContent = msg;
    el.toast.classList.add('show');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { el.toast.classList.remove('show'); }, ms || 3200);
  }

  function itemNodes() {
    return itemLayer.getChildren().slice();
  }

  function isLocked(n) { return !!n.getAttr('locked'); }

  function nodeSeqNum(n) {
    var m = /(\d+)$/.exec(String(n.getAttr('itemId')));
    return m ? parseInt(m[1], 10) : 0;
  }

  // Corners of a node's rectangle in world inches, honoring rotation.
  // Konva rotates around the node origin (top-left, offset 0,0).
  function nodeCorners(node) {
    var w = node.width() * node.scaleX();
    var h = node.height() * node.scaleY();
    var rad = Konva.getAngle(node.rotation());
    var cos = Math.cos(rad), sin = Math.sin(rad);
    var x = node.x(), y = node.y();
    function pt(px, py) { return { x: x + px * cos - py * sin, y: y + px * sin + py * cos }; }
    return [pt(0, 0), pt(w, 0), pt(w, h), pt(0, h)];
  }

  function nodeBBox(node) {
    var cs = nodeCorners(node);
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    cs.forEach(function (c) {
      minX = Math.min(minX, c.x); minY = Math.min(minY, c.y);
      maxX = Math.max(maxX, c.x); maxY = Math.max(maxY, c.y);
    });
    return { minX: minX, minY: minY, maxX: maxX, maxY: maxY, w: maxX - minX, h: maxY - minY };
  }

  function unionBBox(nodes) {
    var u = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    nodes.forEach(function (n) {
      var b = nodeBBox(n);
      u.minX = Math.min(u.minX, b.minX); u.minY = Math.min(u.minY, b.minY);
      u.maxX = Math.max(u.maxX, b.maxX); u.maxY = Math.max(u.maxY, b.maxY);
    });
    u.w = u.maxX - u.minX; u.h = u.maxY - u.minY;
    return u;
  }

  function isOutOfSheet(node) {
    var eps = 0.01;
    var b = nodeBBox(node);
    return b.minX < -eps || b.minY < -eps || b.maxX > SHEET_W_IN + eps || b.maxY > sheetHIn + eps;
  }

  function effectiveDpi(node) {
    var src = sources.get(node.getAttr('sourceId'));
    if (!src) return 0;
    if (src.isVector) return Infinity;
    var wIn = node.width() * node.scaleX();
    var hIn = node.height() * node.scaleY();
    if (wIn <= 0 || hIn <= 0) return 0;
    // sizes refer to the trimmed (visible) artwork; report the worst axis so
    // a stretched design shows its real print quality
    return Math.min(src.artW / wIn, src.artH / hIn);
  }

  // True when the design's width/height ratio no longer matches the artwork
  // (the customer stretched it with the side handles).
  function isStretched(node) {
    var src = sources.get(node.getAttr('sourceId'));
    if (!src) return false;
    var w = node.width() * node.scaleX();
    var h = node.height() * node.scaleY();
    if (w <= 0 || h <= 0) return false;
    var r = (w / h) / (src.artW / src.artH);
    return r > 1.02 || r < 0.98;
  }

  function dpiBadge(dpi) {
    if (dpi === Infinity) return { label: 'Vector', cls: 'ok', title: 'Vector artwork scales without quality loss.' };
    var d = Math.round(dpi);
    if (d >= 300) return { label: d + ' DPI', cls: 'ok', title: 'Print quality is excellent (300 DPI or more).' };
    if (d >= 200) return { label: d + ' DPI', cls: 'mid', title: 'Below 300 DPI. Quality is acceptable, fine details may soften.' };
    if (d >= 150) return { label: d + ' DPI', cls: 'warn', title: 'Low resolution for this print size. Consider a smaller size or a higher resolution file.' };
    return { label: d + ' DPI', cls: 'bad', title: 'Very low resolution. The print will look pixelated at this size.' };
  }

  // ------------------------------------------------------ guia de medidas
  // "De que tamano lo pongo" es la duda numero uno de quien monta una gang sheet,
  // y equivocarse ahi es una reimpresion. Son medidas de referencia del sector,
  // no promesas de la marca: solo cambian el ANCHO y la proporcion se respeta,
  // porque deformar el arte ya tiene su propio aviso en la tarjeta.
  var PRINT_SIZES = [
    { label: 'Sleeve', w: 3 },
    { label: 'Left chest or pocket', w: 3.5 },
    { label: 'Cap front', w: 4 },
    { label: 'Toddler front', w: 6 },
    { label: 'Youth front', w: 8 },
    { label: 'Tote bag', w: 10 },
    { label: 'Adult front or back', w: 11 },
    { label: 'Oversized front', w: 12 }
  ];

  function buildSizeGuide() {
    if (!el.sizeGuideList) return;
    el.sizeGuideList.innerHTML = '';
    PRINT_SIZES.forEach(function (sz) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      var name = document.createElement('span');
      name.textContent = sz.label;
      var val = document.createElement('span');
      val.className = 'sg-in';
      val.textContent = sz.w + '" wide';
      b.appendChild(name);
      b.appendChild(val);
      b.addEventListener('click', function () { applyPrintSize(sz); });
      li.appendChild(b);
      el.sizeGuideList.appendChild(li);
    });
  }

  // Se aplica escribiendo en el campo de ancho de la tarjeta y disparando su
  // evento: asi reutiliza la logica que ya existe (proporcion, limites,
  // recolocacion, historial) en vez de duplicarla.
  function applyPrintSize(sz) {
    var rows = el.itemList.querySelectorAll('.item-row');
    var row = el.itemList.querySelector('.item-row.selected');
    if (!row && rows.length === 1) row = rows[0];
    if (!row) {
      toast(rows.length ? 'Pick a design first, then choose a size.' : 'Upload a design first.');
      return;
    }
    var input = row.querySelector('.dim-w');
    if (!input) return;
    input.value = sz.w;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    if (el.sizeGuidePop) el.sizeGuidePop.hidden = true;
    toast(sz.label + ': set to ' + sz.w + '" wide.');
  }

  function toggleSizeGuide() {
    var pop = el.sizeGuidePop;
    if (!pop || !el.sizeGuideBtn) return;
    if (!pop.hidden) { pop.hidden = true; return; }
    if (el.sheetInfoPop) el.sheetInfoPop.hidden = true;
    if (el.arrangePop) el.arrangePop.hidden = true;
    if (el.arrangeSettingsBtn) el.arrangeSettingsBtn.setAttribute('aria-expanded', 'false');
    var r = el.sizeGuideBtn.getBoundingClientRect();
    pop.hidden = false;
    var alto = pop.getBoundingClientRect().height;
    // si no cabe debajo, se ancla hacia arriba
    var top = (r.bottom + 6 + alto < window.innerHeight) ? r.bottom + 6 : Math.max(8, r.top - 6 - alto);
    pop.style.top = top + 'px';
    pop.style.left = Math.max(8, r.left) + 'px';
  }

  // ------------------------------------------------------ overlap warning
  // Separating Axis Theorem between the (rotated) rectangles of two nodes.
  // Touching within OVERLAP_EPS does not count, so pieces placed exactly
  // `gap` apart or edge to edge never warn.
  function projInterval(corners, ax, ay) {
    var min = Infinity, max = -Infinity;
    for (var i = 0; i < corners.length; i++) {
      var p = corners[i].x * ax + corners[i].y * ay;
      if (p < min) min = p;
      if (p > max) max = p;
    }
    return { min: min, max: max };
  }

  function polysOverlap(c1, c2) {
    var polys = [c1, c2];
    for (var p = 0; p < 2; p++) {
      var poly = polys[p];
      for (var i = 0; i < poly.length; i++) {
        var j = (i + 1) % poly.length;
        var ex = poly[j].x - poly[i].x, ey = poly[j].y - poly[i].y;
        var len = Math.sqrt(ex * ex + ey * ey);
        if (len < 1e-12) continue;
        var ax = -ey / len, ay = ex / len;
        var i1 = projInterval(c1, ax, ay);
        var i2 = projInterval(c2, ax, ay);
        if (Math.min(i1.max, i2.max) - Math.max(i1.min, i2.min) <= OVERLAP_EPS) return false;
      }
    }
    return true;
  }

  // Point-in-mask lookup: world point -> node local space (inverse rotation)
  // -> alpha mask cell of the visible artwork.
  function maskHit(node, mask, px, py) {
    var rad = Konva.getAngle(node.rotation());
    var cos = Math.cos(rad), sin = Math.sin(rad);
    var lx = px - node.x(), ly = py - node.y();
    var u = (lx * cos + ly * sin) / (node.width() * node.scaleX());
    var v = (-lx * sin + ly * cos) / (node.height() * node.scaleY());
    if (u < 0 || u >= 1 || v < 0 || v >= 1) return false;
    var mx = Math.min(mask.w - 1, Math.floor(u * mask.w));
    var my = Math.min(mask.h - 1, Math.floor(v * mask.h));
    return mask.data[my * mask.w + mx] === 1;
  }

  // The rectangles already overlap; do any VISIBLE pixels? A grid of sample
  // points over the bbox intersection is checked against both alpha masks,
  // so logos whose transparent corners cross never warn. Tiny slivers below
  // the sampling resolution may slip through — fine for a warning.
  function masksCollide(nA, nB) {
    var sA = sources.get(nA.getAttr('sourceId'));
    var sB = sources.get(nB.getAttr('sourceId'));
    if (!sA || !sB || !sA.mask || !sB.mask) return true; // no mask: assume solid
    var a = nodeBBox(nA), b = nodeBBox(nB);
    var x0 = Math.max(a.minX, b.minX), x1 = Math.min(a.maxX, b.maxX);
    var y0 = Math.max(a.minY, b.minY), y1 = Math.min(a.maxY, b.maxY);
    if (x1 <= x0 || y1 <= y0) return false;
    var STEPS = 20;
    var dx = (x1 - x0) / STEPS, dy = (y1 - y0) / STEPS;
    for (var iy = 0; iy <= STEPS; iy++) {
      for (var ix = 0; ix <= STEPS; ix++) {
        var px = x0 + ix * dx, py = y0 + iy * dy;
        if (maskHit(nA, sA.mask, px, py) && maskHit(nB, sB.mask, px, py)) return true;
      }
    }
    return false;
  }

  function computeOverlaps() {
    overlapSet = new Set();
    var infos = itemNodes().map(function (n) {
      return { n: n, b: nodeBBox(n), c: nodeCorners(n), g: n.getAttr('groupId') || null };
    });
    for (var i = 0; i < infos.length; i++) {
      for (var j = i + 1; j < infos.length; j++) {
        var A = infos[i], B = infos[j];
        // overlap inside a group is treated as intentional (that is how a
        // customer marks "these pieces belong together like this")
        if (A.g && A.g === B.g) continue;
        if (A.b.maxX <= B.b.minX + OVERLAP_EPS || B.b.maxX <= A.b.minX + OVERLAP_EPS ||
            A.b.maxY <= B.b.minY + OVERLAP_EPS || B.b.maxY <= A.b.minY + OVERLAP_EPS) continue;
        if (!polysOverlap(A.c, B.c)) continue;
        if (!masksCollide(A.n, B.n)) continue; // only transparent areas touch
        overlapSet.add(A.n);
        overlapSet.add(B.n);
      }
    }
  }

  // ------------------------------------------------------------ zoom/grid

  // Padding band around the sheet, in world inches. At low zoom it grows so the
  // ruler labels (sized in screen px) always fit inside the canvas.
  function padForZoom(z) {
    return Math.max(PAD_IN, 50 / z);
  }

  // Cap the zoom so the stage's backing canvases stay inside every browser's
  // canvas limits. Konva allocates canvases at devicePixelRatio; Firefox caps
  // canvas area at ~125 MP, iOS Safari far lower, so budget per-layer pixels.
  // The padding depends on the zoom, so iterate the fixed point.
  function maxZoomAllowed() {
    var dpr = window.devicePixelRatio || 1;
    var budget = IS_IOS ? 12e6 : 32e6; // device pixels per layer canvas
    var z = ZOOM_MAX;
    for (var k = 0; k < 4; k++) {
      var p = padForZoom(z);
      z = Math.sqrt(budget / ((SHEET_W_IN + p * 2) * (sheetHIn + extraAuto() + p * 2))) / dpr;
    }
    return clamp(Math.floor(z), ZOOM_MIN, ZOOM_MAX);
  }

  function applyZoom(z) {
    zoom = clamp(z, ZOOM_MIN, maxZoomAllowed());
    padIn = padForZoom(zoom);
    stage.scale({ x: zoom, y: zoom });
    stage.size({
      width: Math.ceil((SHEET_W_IN + padIn * 2) * zoom),
      height: Math.ceil((sheetHIn + extraAuto() + padIn * 2) * zoom)
    });
    stage.position({ x: padIn * zoom, y: padIn * zoom });
    rebuildGrid();
    // 96 px per inch is the CSS reference pixel, so zoom 96 reads as 100%
    el.zoomLevelBtn.textContent = Math.round(zoom / 96 * 100) + '%';
  }

  function fitZoom() {
    // extra slack so the vertical scrollbar and ceil rounding never force a
    // horizontal scrollbar right after fitting
    var avail = Math.max(100, el.scroll.clientWidth - 48 - 18);
    // padForZoom depends on the zoom, so iterate the fixed point a few times
    var z = avail / (SHEET_W_IN + 2 * PAD_IN);
    for (var k = 0; k < 3; k++) z = avail / (SHEET_W_IN + 2 * padForZoom(z));
    applyZoom(z);
  }

  function rebuildGrid() {
    gridLayer.destroyChildren();

    // Fondo de la hoja. Con color de prenda elegido se pinta liso; sin el, los cuadros
    // que significan "transparente". En los dos casos es solo pantalla: al exportar,
    // renderSheetBlob no mira esta capa.
    if (fondoEsColor()) {
      gridLayer.add(new Konva.Rect({
        x: 0, y: 0, width: SHEET_W_IN, height: sheetHIn, fill: fondo.c
      }));
    } else {
      gridLayer.add(new Konva.Rect({
        x: 0, y: 0, width: SHEET_W_IN, height: sheetHIn,
        fillPatternImage: lienzosCuadros[fondo.i],
        fillPatternScale: { x: 0.25 / 16, y: 0.25 / 16 },
        fillPatternRepeat: 'repeat'
      }));
    }

    var i;
    for (i = 0; i <= SHEET_W_IN; i++) {
      gridLayer.add(new Konva.Line({
        points: [i, 0, i, sheetHIn],
        stroke: i % 6 === 0 ? '#c3cdd9' : '#e3e8ee',
        strokeWidth: 1 / zoom
      }));
    }
    for (i = 0; i <= sheetHIn; i++) {
      gridLayer.add(new Konva.Line({
        points: [0, i, SHEET_W_IN, i],
        stroke: i % 6 === 0 ? '#c3cdd9' : '#e3e8ee',
        strokeWidth: 1 / zoom
      }));
    }

    // Ruler labels every 6 inches. All boxes are sized in screen px (divided by
    // zoom) and wrap is off, so labels never wrap mid-digit, vanish, or spill
    // outside the padding band at low zoom.
    var fontIn = 11 / zoom;
    for (i = 0; i <= SHEET_W_IN; i += 6) {
      gridLayer.add(new Konva.Text({
        x: i - 30 / zoom, y: -(fontIn + 4 / zoom), width: 60 / zoom, align: 'center', wrap: 'none',
        text: i + '"', fontSize: fontIn, fill: '#64748b', fontFamily: 'system-ui, sans-serif'
      }));
    }
    for (i = 0; i <= sheetHIn; i += 6) {
      gridLayer.add(new Konva.Text({
        x: -46 / zoom, y: i - fontIn / 2, width: 40 / zoom, align: 'right', wrap: 'none',
        text: i + '"', fontSize: fontIn, fill: '#64748b', fontFamily: 'system-ui, sans-serif'
      }));
    }

    // sheet border
    gridLayer.add(new Konva.Rect({
      x: 0, y: 0, width: SHEET_W_IN, height: sheetHIn,
      stroke: '#8fa1b5', strokeWidth: 1.5 / zoom
    }));

    // Largo automatico: zona bajo la hoja donde soltar un diseno para alargarla.
    var extra = extraAuto();
    if (extra) {
      gridLayer.add(new Konva.Rect({
        x: 0, y: sheetHIn, width: SHEET_W_IN, height: extra,
        fill: 'rgba(2,159,218,0.05)', stroke: '#9fd6ee', strokeWidth: 1 / zoom, dash: [6 / zoom, 5 / zoom]
      }));
      gridLayer.add(new Konva.Text({
        x: 0, y: sheetHIn + extra / 2 - 7 / zoom, width: SHEET_W_IN, align: 'center', wrap: 'none',
        text: currentType() && currentType().porTamano ? 'Need more room? Drop a design here'
          : 'Drop a design here to make the sheet longer', fontSize: 13 / zoom,
        fill: '#3b8fb3', fontFamily: 'system-ui, sans-serif'
      }));
    }

    // dashed safety-margin guide; automatic placement keeps designs inside it
    gridLayer.add(new Konva.Rect({
      x: MARGIN_IN, y: MARGIN_IN,
      width: SHEET_W_IN - 2 * MARGIN_IN, height: sheetHIn - 2 * MARGIN_IN,
      stroke: '#77c4e4', strokeWidth: 1 / zoom, dash: [6 / zoom, 5 / zoom]
    }));
  }

  // --------------------------------------------------------------- upload
  function acceptFile(file) {
    if (/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type)) return true;
    // drag-and-drop can deliver an empty MIME type (no OS mapping for the
    // extension); fall back to the extension and let img.onerror catch fakes
    if (!file.type) return /\.(png|jpe?g|webp|svg)$/i.test(file.name);
    return false;
  }

  function addFiles(fileList) {
    if (exporting) {
      toast('Please wait for the current download to finish.');
      return;
    }
    var files = Array.prototype.slice.call(fileList);
    var rejected = files.filter(function (f) { return !acceptFile(f); });
    if (rejected.length) {
      toast('Skipped ' + rejected.length + ' unsupported file(s). Use PNG, JPG, WebP or SVG.');
    }
    files.filter(acceptFile).forEach(function (file) {
      loadSource(file).then(function (src) {
        sources.set(src.id, src);
        var node = addItemFromSource(src);
        var porTam = currentType() && currentType().porTamano;
        if (node.getParent() && !porTam) setSelection([node]);   // si paso a otra hoja ya no esta aqui
        renderList();
        refreshAll();
        pushHistory();
        if (porTam) rehacerTamano(tarjetasTamano(), true);   // by size: siempre en filas
        if (src.trimRect) queueTrimAsk(src);
      }).catch(function (err) {
        toast(err.message);
      });
    });
  }

  // Bounding box of the visible (non-transparent) pixels of a canvas, or
  // null when the image is fully transparent. Used to auto-trim the empty
  // border that most uploads carry around the real artwork.
  function computeTrim(pc) {
    try {
      var w = pc.width, h = pc.height;
      var d = pc.getContext('2d').getImageData(0, 0, w, h).data;
      var minX = w, minY = h, maxX = -1, maxY = -1;
      for (var y = 0; y < h; y++) {
        var row = y * w;
        for (var x = 0; x < w; x++) {
          if (d[(row + x) * 4 + 3] > 8) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return null;
      return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    } catch (err) {
      return null;
    }
  }

  // Suggested trim rectangle in NATIVE pixels (the bbox of the visible pixels
  // of the preview, scaled up, with a 2 px cushion), or null when the border
  // is not worth trimming (<2% and <8 px on both axes). Used for the upload
  // suggestion. (It also served the old "Remove white BG", removed 2026-07-21 and
  // parked in ../background-remover-lab/ until it works well enough to come back.)
  function suggestTrimRect(pc, nativeW, nativeH) {
    var tr = computeTrim(pc);
    if (!tr) return null;
    var back = nativeW / pc.width;
    var pad = 2;
    var tx = Math.max(0, Math.floor(tr.x * back) - pad);
    var ty = Math.max(0, Math.floor(tr.y * back) - pad);
    var tw = Math.min(nativeW - tx, Math.ceil(tr.w * back) + 2 * pad);
    var th = Math.min(nativeH - ty, Math.ceil(tr.h * back) + 2 * pad);
    if (nativeW - tw > Math.max(8, nativeW * 0.02) || nativeH - th > Math.max(8, nativeH * 0.02)) {
      return { x: tx, y: ty, w: tw, h: th };
    }
    return null;
  }

  // Coarse alpha mask (max 64 px per side) of the trimmed artwork. The
  // overlap warning uses it to tell VISIBLE pixels apart from transparent
  // ones, so two logos whose empty corners cross never warn.
  function buildAlphaMask(pc) {
    try {
      var mw = Math.min(64, pc.width);
      var mh = Math.max(1, Math.round(mw * pc.height / pc.width));
      if (mh > 64) {
        mh = 64;
        mw = Math.max(1, Math.round(mh * pc.width / pc.height));
      }
      var c = document.createElement('canvas');
      c.width = mw; c.height = mh;
      var x = c.getContext('2d');
      if (!x) return null;
      x.drawImage(pc, 0, 0, mw, mh);
      var d = x.getImageData(0, 0, mw, mh).data;
      var bits = new Uint8Array(mw * mh);
      for (var i = 0; i < bits.length; i++) bits[i] = d[i * 4 + 3] > 16 ? 1 : 0;
      return { w: mw, h: mh, data: bits };
    } catch (err) {
      return null;
    }
  }

  // forcedId is used when restoring an autosaved project, so the stored item
  // snapshots keep pointing at the right source.
  function loadSource(file, forcedId) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        // try/catch so a throw (e.g. getContext returning null when Safari's
        // canvas memory is exhausted) rejects instead of hanging the promise
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!w || !h) {
            URL.revokeObjectURL(url);
            reject(new Error('Could not read the size of "' + file.name + '". If it is an SVG, export it as PNG and try again.'));
            return;
          }
          var scale = Math.min(1, PREVIEW_MAX_PX / Math.max(w, h));
          var pc = document.createElement('canvas');
          pc.width = Math.max(1, Math.round(w * scale));
          pc.height = Math.max(1, Math.round(h * scale));
          var pctx = pc.getContext('2d');
          if (!pctx) throw new Error('no 2d context');
          pctx.imageSmoothingEnabled = true;
          pctx.imageSmoothingQuality = 'high';
          pctx.drawImage(img, 0, 0, pc.width, pc.height);

          // Scan for a transparent border but DON'T crop automatically: the
          // card offers a "Trim edges" button instead (the padding may be on
          // purpose). JPEG has no alpha channel, so the scan is skipped.
          var trimRect = file.type === 'image/jpeg' ? null : suggestTrimRect(pc, w, h);

          var ts = Math.min(1, THUMB_PX / Math.max(pc.width, pc.height));
          var tc = document.createElement('canvas');
          tc.width = Math.max(1, Math.round(pc.width * ts));
          tc.height = Math.max(1, Math.round(pc.height * ts));
          var tctx = tc.getContext('2d');
          if (!tctx) throw new Error('no 2d context');
          tctx.drawImage(pc, 0, 0, tc.width, tc.height);

          var id = forcedId || ('src-' + (++seq));
          var m = /(\d+)$/.exec(id);
          if (m) seq = Math.max(seq, parseInt(m[1], 10));

          // Un SVG es vectorial y no tiene fondo que quitar; el resto se mira.
          // Guarda el COLOR, no un si/no: puede ser blanco, azul, negro, el que sea.
          var bgColor = file.type === 'image/svg+xml' ? null : detectSolidBg(pc);

          resolve({
            id: id,
            name: file.name || 'artwork',
            file: file,          // original blob, persisted by the autosave
            imgEl: img,          // full resolution original, used by the export
            printSrc: img,       // lo que dibuja el export: pasa a ser un canvas ya
                                 // procesado cuando se quita el fondo blanco
            bgRemoved: false,
            url: url,            // kept alive while the source is referenced
            nativeW: w,
            nativeH: h,
            artX: 0,             // rectangle actually drawn/printed, in native
            artY: 0,             // px; narrows to trimRect when the customer
            artW: w,             // applies "Trim edges" on the card
            artH: h,
            trimRect: trimRect,  // suggested trim, null when not worth it
            trimmed: false,
            mask: buildAlphaMask(pc), // coarse alpha mask of the visible art
            previewCanvas: pc,   // downscaled preview (re-cropped after Trim)
            thumbUrl: tc.toDataURL(),
            isVector: file.type === 'image/svg+xml',
            bgColor: bgColor     // { r,g,b,uniform } del fondo detectado, o null si
                                 // el borde no es de un color solo (foto, degradado)
          });
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(new Error('Could not process "' + file.name + '". Close other tabs or try a smaller file.'));
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('Could not load "' + file.name + '". The file may be damaged.'));
      };
      img.src = url;
    });
  }

  // Rebuild the on-screen derivatives (preview raster, thumbnail, alpha mask)
  // of a source from the original image and current (trimmed) art rectangle.
  function rebuildDerived(src) {
    try {
      var aw = src.artW, ah = src.artH;
      var s2 = Math.min(1, PREVIEW_MAX_PX / Math.max(aw, ah));
      var pc = document.createElement('canvas');
      pc.width = Math.max(1, Math.round(aw * s2));
      pc.height = Math.max(1, Math.round(ah * s2));
      var ctx = pc.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      // De la imagen de IMPRESION, no del original: tras quitar el fondo, printSrc es
      // el lienzo ya limpio y es lo que el cliente tiene que ver en la hoja y en la
      // miniatura. Dibujar imgEl aqui dejaba el fondo a la vista aunque el export
      // saliera bien, que es peor que un fallo franco: parece que no hizo nada.
      ctx.drawImage(src.printSrc || src.imgEl, src.artX, src.artY, aw, ah, 0, 0, pc.width, pc.height);
      src.previewCanvas = pc;
      var ts = Math.min(1, THUMB_PX / Math.max(pc.width, pc.height));
      var tc = document.createElement('canvas');
      tc.width = Math.max(1, Math.round(pc.width * ts));
      tc.height = Math.max(1, Math.round(pc.height * ts));
      tc.getContext('2d').drawImage(pc, 0, 0, tc.width, tc.height);
      src.thumbUrl = tc.toDataURL();
      src.mask = buildAlphaMask(pc);
    } catch (err) { /* rebuild failed; export stays correct */ }
  }

  // Apply the suggested trim to a source: narrow the printed rectangle to the
  // visible artwork and rebuild its derivatives. Geometry of placed nodes is
  // up to the caller (trimSourceAndNodes).
  function applySourceTrim(src) {
    var t = src.trimRect;
    if (!t || src.trimmed) return;
    src.artX = t.x; src.artY = t.y;
    src.artW = t.w; src.artH = t.h;
    src.trimmed = true;
    rebuildDerived(src);
  }

  // Crop the source to src.trimRect and shrink every placed copy so the visible
  // artwork keeps its exact print size and position on the sheet. No toast or
  // history push (the caller decides); returns true when it applied.
  function applyTrimToNodes(src) {
    if (!src.trimRect || src.trimmed) return false;
    var fx = src.trimRect.x / src.nativeW;
    var fy = src.trimRect.y / src.nativeH;
    var kw = src.trimRect.w / src.nativeW;
    var kh = src.trimRect.h / src.nativeH;
    applySourceTrim(src);
    itemNodes().forEach(function (n) {
      if (n.getAttr('sourceId') !== src.id) return;
      var w = n.width() * n.scaleX(), h = n.height() * n.scaleY();
      var rad = Konva.getAngle(n.rotation());
      var cos = Math.cos(rad), sin = Math.sin(rad);
      var dx = fx * w, dy = fy * h;   // local offset, rotated into world space
      n.position({ x: n.x() + dx * cos - dy * sin, y: n.y() + dx * sin + dy * cos });
      n.width(w * kw);
      n.height(h * kh);
      n.scaleX(1); n.scaleY(1);
      n.image(src.previewCanvas);
    });
    return true;
  }

  // "Trim edges" card action: same crop, with its own toast + history entry.
  function trimSourceAndNodes(src) {
    if (!applyTrimToNodes(src)) return;
    renderList();
    refreshAll();
    pushHistory();
    toast('Trimmed "' + src.name + '". Sizes now refer to the visible artwork.');
  }

  // -------------------------------------------------- trim decision popup
  // A fresh upload with empty transparent edges asks the customer right away
  // (one dialog per file, queued). The "Trim edges" badge on the card stays
  // as a second chance if they dismiss it. Falls back to a toast when the
  // dialog markup is missing (an out-of-sync brand copy).
  var trimQueue = [];

  function queueTrimAsk(src) {
    if (!el.trimOverlay) {
      toast('"' + src.name + '" has empty transparent edges. Click "Trim edges" on its card to size just the visible artwork.', 5500);
      return;
    }
    trimQueue.push(src.id);
    showNextTrimAsk();
  }

  function showNextTrimAsk() {
    if (el.trimOverlay.classList.contains('show')) return;
    var src = null;
    while (trimQueue.length && !src) {
      var s = sources.get(trimQueue.shift());
      if (s && s.trimRect && !s.trimmed) src = s;
    }
    if (!src) return;
    // sizes as the customer sees them: the placed copy, before/after the trim
    var node = itemNodes().filter(function (n) { return n.getAttr('sourceId') === src.id; })[0];
    var w = node ? node.width() * node.scaleX() : src.nativeW / EXPORT_DPI;
    var h = node ? node.height() * node.scaleY() : src.nativeH / EXPORT_DPI;
    var tw = w * (src.trimRect.w / src.nativeW);
    var th = h * (src.trimRect.h / src.nativeH);
    el.trimText.textContent = '"' + src.name + '" has empty space around the artwork: the file measures ' +
      w.toFixed(2) + '" × ' + h.toFixed(2) + '" but the visible design only fills ' +
      tw.toFixed(2) + '" × ' + th.toFixed(2) + '". Trimming makes sizes and packing use just the design. ' +
      'You can also trim later from the design\'s card.';
    drawTrimPreview(src);
    el.trimOverlay.dataset.srcId = src.id;
    el.trimOverlay.classList.add('show');
  }

  function closeTrimAsk(applyTrim) {
    var src = sources.get(el.trimOverlay.dataset.srcId);
    el.trimOverlay.classList.remove('show');
    if (applyTrim && src) {
      trimSourceAndNodes(src);
      if (currentType() && currentType().porTamano) rehacerTamano(tarjetasTamano(), true);   // la medida cambio
    }
    showNextTrimAsk();
  }

  function drawTrimPreview(src) {
    var c = el.trimPreview;
    if (!c) return;
    var ctx = c.getContext('2d');
    var W = c.width, H = c.height;
    // checkerboard so transparency reads as transparency
    ctx.clearRect(0, 0, W, H);
    var sq = 8;
    for (var y = 0; y < H; y += sq) {
      for (var x = 0; x < W; x += sq) {
        ctx.fillStyle = ((x + y) / sq) % 2 ? '#eef1f5' : '#ffffff';
        ctx.fillRect(x, y, sq, sq);
      }
    }
    var scale = Math.min((W - 28) / src.nativeW, (H - 28) / src.nativeH);
    var dw = src.nativeW * scale, dh = src.nativeH * scale;
    var ox = (W - dw) / 2, oy = (H - dh) / 2;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src.previewCanvas, ox, oy, dw, dh);
    // dim what the trim would cut away
    var t = src.trimRect;
    var tx = ox + t.x * scale, ty = oy + t.y * scale;
    var tw2 = t.w * scale, th2 = t.h * scale;
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.fillRect(0, 0, W, ty);
    ctx.fillRect(0, ty, tx, th2);
    ctx.fillRect(tx + tw2, ty, W - tx - tw2, th2);
    ctx.fillRect(0, ty + th2, W, H - ty - th2);
    // dashed crop line + prepress corner marks
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(tx + 0.5, ty + 0.5, tw2 - 1, th2 - 1);
    ctx.setLineDash([]);
    var m = 7;
    [[tx, ty, -1, -1], [tx + tw2, ty, 1, -1], [tx + tw2, ty + th2, 1, 1], [tx, ty + th2, -1, 1]]
      .forEach(function (k) {
        ctx.beginPath();
        ctx.moveTo(k[0] + k[2] * 3, k[1] + k[3] * 3 - k[3] * 0);
        ctx.lineTo(k[0] + k[2] * 3, k[1] + k[3] * (3 + m));
        ctx.moveTo(k[0] + k[2] * 3, k[1] + k[3] * 3);
        ctx.lineTo(k[0] + k[2] * (3 + m), k[1] + k[3] * 3);
        ctx.stroke();
      });
  }

  // Drop sources that nothing references anymore: not a node on the canvas,
  // not an undo/redo snapshot (undoing a delete must revive the artwork),
  // not the clipboard.
  function gcSources() {
    var used = {};
    itemNodes().forEach(function (n) { used[n.getAttr('sourceId')] = true; });
    undoStack.concat(redoStack).forEach(function (st) {
      (st.hojas || [st]).forEach(function (h) { h.items.forEach(function (it) { used[it.s] = true; }); });
    });
    hojas.forEach(function (h) { if (h) h.items.forEach(function (it) { used[it.s] = true; }); });
    if (clipboard) clipboard.items.forEach(function (it) { used[it.s] = true; });
    sources.forEach(function (src, id) {
      if (!used[id]) {
        URL.revokeObjectURL(src.url);
        sources.delete(id);
      }
    });
  }

  // ---------------------------------------------------------------- items

  // Transform events do NOT bubble from nodes to their layer in Konva 9 (the
  // Transformer fires them on the node only), so these two handlers must be
  // attached per node. Konva binds `this` to the node, and node.clone() copies
  // listeners, so duplicated nodes keep them without closure problems.
  function onNodeTransform() {
    refreshAll();
    // live feedback while a handle is being dragged (overwrites the hint that
    // refreshAll just wrote)
    var deg = Math.round(((this.rotation() % 360) + 360) % 360);
    el.statusHint.textContent =
      (this.width() * this.scaleX()).toFixed(2) + '" × ' +
      (this.height() * this.scaleY()).toFixed(2) + '" · rotated ' + deg + '°' +
      (isStretched(this) ? ' · ⚠ stretched' : '');
  }

  function onNodeTransformEnd() {
    var w = Math.max(1e-6, Math.abs(this.width() * this.scaleX()));
    var h = Math.max(1e-6, Math.abs(this.height() * this.scaleY()));
    // enforce the minimum size scaling BOTH axes, so proportions never distort
    var k = Math.max(1, MIN_ITEM_IN / w, MIN_ITEM_IN / h);
    this.width(w * k);
    this.height(h * k);
    this.scaleX(1);
    this.scaleY(1);
    refreshAll();
    pushHistorySoon(); // transformend fires once per selected node; coalesce
    revisarSobranSoon();
  }

  // En largo automatico la hoja llega hasta su maximo (168"): un diseno que, al moverlo o
  // agrandarlo, queda mas alla de ese maximo se cortaria al imprimir. No se deja fuera: pasa a
  // la primera hoja con sitio (27-oct-2026, pedido del usuario). Bloqueados: no se tocan.
  var sobraTimer = null;
  function revisarSobranSoon() { clearTimeout(sobraTimer); sobraTimer = setTimeout(revisarSobran, 0); }
  function revisarSobran() {
    if (!largoAuto || exporting) return;
    var tope = maxAltura() - MARGIN_IN + 1e-6;
    var pasan = itemNodes().filter(function (n) { return !isLocked(n) && nodeBBox(n).maxY > tope; });
    if (!pasan.length) return;
    // un grupo viaja entero
    var gids = {};
    pasan.forEach(function (n) { if (n.getAttr('groupId')) gids[n.getAttr('groupId')] = true; });
    var mover = itemNodes().filter(function (n) { return pasan.indexOf(n) !== -1 || (n.getAttr('groupId') && gids[n.getAttr('groupId')]); });
    var unidades = unidadesDe(mover).length;
    var fue = desbordar(mover);
    if (!fue.n) return;
    renderList();
    refreshAll();
    pushHistory();
    toast((unidades === 1 ? 'That design' : unidades + ' designs') + ' went past the ' + maxAltura() + '" limit of this sheet, so ' + (unidades === 1 ? 'it' : 'they') + ' moved to ' + nombresHojas(fue.hojas) + '.');
  }

  function makeNode(src, attrs, forcedItemId) {
    var node = new Konva.Image(Object.assign({
      image: src.previewCanvas,
      draggable: true,
      stroke: DANGER,
      strokeWidth: 2,
      strokeScaleEnabled: false,
      strokeEnabled: false
    }, attrs, {
      sourceId: src.id,
      itemId: forcedItemId || ('item-' + (++seq))
    }));
    if (node.getAttr('locked')) node.draggable(false);
    node.on('transform', onNodeTransform);
    node.on('transformend', onNodeTransformEnd);
    itemLayer.add(node);
    return node;
  }

  function addItemFromSource(src) {
    // natural print size of the VISIBLE artwork at 300 DPI, clamped
    var wIn = clamp(src.artW / EXPORT_DPI, 0.5, SHEET_W_IN - 2 * MARGIN_IN);
    var hIn = wIn * (src.artH / src.artW);
    if (hIn > alturaTrabajo() - 2 * MARGIN_IN) {
      hIn = (alturaTrabajo() - 2 * MARGIN_IN) * 0.9;
      wIn = hIn * (src.artW / src.artH);
    }
    // land on the first free spot instead of piling up in the corner; a fresh
    // upload stays upright unless only the rotated orientation fits
    var spot = findFreeSpot(wIn, hIn, { rotate: 'prefer-upright' });
    var node = makeNode(src, { x: spot.x, y: spot.y, width: wIn, height: hIn });
    if (spot.rotated) placeUnitAt([node], spot.x, spot.y, true);
    if (!spot.fits) {
      if (largoAuto) {
        var fue = desbordar([node]);
        if (fue.n) toast('"' + src.name + '" did not fit on this sheet (' + maxAltura() + '" max), so it went to ' + nombresHojas(fue.hojas) + '.');
      } else {
        toast('The sheet is full, so "' + src.name + '" was placed below it. Try a taller sheet or Auto-arrange.');
      }
    }
    return node;
  }

  // clone one node in place; group/lock handling is up to the caller
  function duplicateNode(node) {
    var src = sources.get(node.getAttr('sourceId'));
    if (!src) return null;
    var copy = node.clone({ itemId: 'item-' + (++seq) });
    copy.setAttr('locked', false);
    copy.draggable(true);
    copy.strokeEnabled(false);
    itemLayer.add(copy);
    return copy;
  }

  // clone a whole unit (single node or all members of a group) in place,
  // remapping shared groupIds so the copy is its own group
  function duplicateUnit(nodes) {
    var gidMap = {};
    var clones = [];
    nodes.forEach(function (n) {
      var c = duplicateNode(n);
      if (!c) return;
      var g = n.getAttr('groupId');
      if (g) {
        if (!gidMap[g]) gidMap[g] = 'g-' + (++seq);
        c.setAttr('groupId', gidMap[g]);
      }
      clones.push(c);
    });
    return clones;
  }

  function deleteNodes(nodes) {
    if (!nodes.length) return;
    setSelection([]);
    nodes.slice().forEach(function (n) { n.destroy(); });
    renderList();
    refreshAll();
    pushHistory();
  }

  // ------------------------------------------------ placement and rotation

  function getSpacing() {
    var v = parseFloat(el.spacingInput.value);
    if (!isFinite(v) || v < 0) v = 0.2;   // 0.2" por defecto (pedido por el equipo, 20-oct-2026)
    return clamp(v, 0, 5);
  }

  // MaxRects engine over the usable area (inside the safety margin) with the
  // current designs as obstacles. Coordinates in/out are usable-area based;
  // callers add MARGIN_IN.
  function packerFor(excludeNodes, heightOverride) {
    var ex = excludeNodes || [];
    var eng = Packing.create({
      width: SHEET_W_IN - 2 * MARGIN_IN,
      height: heightOverride || (alturaTrabajo() - 2 * MARGIN_IN),
      gap: getSpacing()
    });
    itemNodes().forEach(function (n) {
      if (ex.indexOf(n) !== -1) return;
      var b = nodeBBox(n);
      eng.occupy(b.minX - MARGIN_IN, b.minY - MARGIN_IN, b.w, b.h);
    });
    return eng;
  }

  function contentBottom(excludeNodes) {
    var ex = excludeNodes || [];
    var bottom = 0;
    itemNodes().forEach(function (n) {
      if (ex.indexOf(n) !== -1) return;
      bottom = Math.max(bottom, nodeBBox(n).maxY);
    });
    return bottom;
  }

  // First free spot on the sheet for a w x h rectangle, keeping the layout
  // gap and the safety margin. opts.rotate: 'no' | 'prefer-upright' | 'best'.
  // 'best' lets the engine lay the piece sideways whenever that lands higher
  // on the sheet. When nothing fits the rectangle goes below everything and
  // the off-sheet warning takes over.
  function findFreeSpot(w, h, opts) {
    opts = opts || {};
    var eng = packerFor(opts.exclude);
    var s;
    if (opts.rotate === 'best') {
      s = eng.place(w, h, true);
    } else {
      s = eng.place(w, h, false);
      if (!s && opts.rotate === 'prefer-upright') s = eng.place(w, h, true);
    }
    if (s) return { x: MARGIN_IN + s.x, y: MARGIN_IN + s.y, rotated: s.rotated, fits: true };
    var bottom = contentBottom(opts.exclude);
    return {
      x: MARGIN_IN,
      y: bottom > 0 ? bottom + getSpacing() : MARGIN_IN,
      rotated: false, fits: false
    };
  }

  // Rotate a set of nodes 90 degrees as one rigid unit around the center of
  // their combined bounding box (a single node keeps its visual center).
  function rotateNodesAsUnit(nodes) {
    if (!nodes.length) return;
    var u = unionBBox(nodes);
    var cx = (u.minX + u.maxX) / 2, cy = (u.minY + u.maxY) / 2;
    nodes.forEach(function (n) {
      n.rotation((n.rotation() + 90) % 360);
      var x = n.x(), y = n.y();
      n.position({ x: cx - (y - cy), y: cy + (x - cx) });
    });
  }

  // Move a unit so its combined bounding box lands at (X, Y), optionally
  // rotating it 90 degrees first (that is what the packer's `rotated` means).
  function placeUnitAt(nodes, X, Y, rotated) {
    if (rotated) rotateNodesAsUnit(nodes);
    var u = unionBBox(nodes);
    var dx = X - u.minX, dy = Y - u.minY;
    nodes.forEach(function (n) { n.move({ x: dx, y: dy }); });
  }

  // Move a unit (e.g. a fresh duplicate) to the first free spot.
  function placeUnitFree(nodes, rotateMode) {
    var u = unionBBox(nodes);
    var spot = findFreeSpot(u.w, u.h, { exclude: nodes, rotate: rotateMode || 'best' });
    placeUnitAt(nodes, spot.x, spot.y, spot.rotated);
    return spot.fits;
  }

  // Add `count` copies of a unit, each landing in the best free spot (holes
  // included, rotation allowed). Copies that no longer fit stack below the
  // sheet. clearGid detaches copies from any canvas group (used by the card
  // buttons, where the customer thinks per-design, not per-group).
  function addCopies(unit, count, clearGid) {
    var u = unionBBox(unit);
    var eng = packerFor();
    var sp = getSpacing();
    var made = 0, overflow = 0, bottomCursor = null, fuera = [];
    for (var i = 0; i < count; i++) {
      var clones = duplicateUnit(unit);
      if (!clones.length) break;
      if (clearGid) clones.forEach(function (c) { c.setAttr('groupId', null); });
      var s = eng.place(u.w, u.h, true);
      if (s) {
        placeUnitAt(clones, MARGIN_IN + s.x, MARGIN_IN + s.y, s.rotated);
      } else {
        if (bottomCursor === null) bottomCursor = Math.max(MARGIN_IN, contentBottom(clones) + sp);
        placeUnitAt(clones, MARGIN_IN, bottomCursor, false);
        bottomCursor += u.h + sp;
        overflow++;
        fuera = fuera.concat(clones);
      }
      made++;
    }
    if (!made) return 0;
    if (overflow && largoAuto) {
      var fue = desbordar(fuera);
      renderList();
      refreshAll();
      pushHistory();
      if (fue.n) toast(overflow + ' cop' + (overflow === 1 ? 'y' : 'ies') + ' did not fit on this sheet and went to ' + nombresHojas(fue.hojas) + '.');
      return made;
    }
    renderList();
    refreshAll();
    pushHistory();
    if (overflow) {
      toast(overflow + ' cop' + (overflow === 1 ? 'y' : 'ies') + ' did not fit and went below the sheet. Try a taller sheet or Auto-arrange.');
    }
    return made;
  }

  // The unit that "Copies" and "Fill sheet" replicate: the selection's only
  // unit, or its first one when everything selected is the same card (that is
  // what clicking a sidebar card selects).
  function primaryUnitForCopies() {
    var units = unitsOfSelection();
    if (!units.length) return null;
    if (units.length === 1) return units[0];
    var k = groupKeyOf(selection[0]);
    var same = selection.every(function (n) { return groupKeyOf(n) === k; });
    return same ? units[0] : null;
  }

  // "Fill sheet": keep adding copies of the selected unit until nothing fits.
  function fillSheet() {
    var unit = primaryUnitForCopies();
    if (!unit) return;
    var u = unionBBox(unit);
    // la hoja que se ve, tambien en automatico: llenar hasta 300" no es lo que se pide
    var eng = packerFor(null, sheetHIn - 2 * MARGIN_IN);
    var added = 0;
    for (var i = 0; i < FILL_MAX; i++) {
      var s = eng.place(u.w, u.h, true);
      if (!s) break;
      var clones = duplicateUnit(unit);
      if (!clones.length) break;
      placeUnitAt(clones, MARGIN_IN + s.x, MARGIN_IN + s.y, s.rotated);
      added++;
    }
    if (!added) {
      toast('No more copies fit on this sheet with the current gap.');
      return;
    }
    renderList();
    refreshAll();
    pushHistory();
    toast('Added ' + added + ' cop' + (added === 1 ? 'y' : 'ies') + '. The sheet is as full as it gets.');
  }

  // ----------------------------------------------------------- selection

  // A "unit" is what moves together: a whole canvas group, or a single node.
  // Locked designs never enter the selection.
  function expandUnits(nodes) {
    var out = [];
    var seen = {};
    var gids = {};
    nodes.forEach(function (n) {
      var g = n.getAttr('groupId');
      if (g) gids[g] = true;
    });
    itemNodes().forEach(function (n) {
      var g = n.getAttr('groupId');
      var wanted = nodes.indexOf(n) !== -1 || (g && gids[g]);
      if (wanted && !isLocked(n) && !seen[n._id]) {
        seen[n._id] = true;
        out.push(n);
      }
    });
    return out;
  }

  function setSelection(nodes) {
    selection = expandUnits(nodes || []);
    transformer.nodes(selection);
    updateToolbarState();
    highlightRow();
    updateHint();
  }

  function toggleUnitSelection(node) {
    var unit = expandUnits([node]);
    if (!unit.length) return;
    var allIn = unit.every(function (n) { return selection.indexOf(n) !== -1; });
    if (allIn) {
      setSelection(selection.filter(function (n) { return unit.indexOf(n) === -1; }));
    } else {
      setSelection(selection.concat(unit));
    }
  }

  function unitsOfSelection() {
    var units = [], byGid = {};
    selection.forEach(function (n) {
      var g = n.getAttr('groupId');
      if (g) {
        if (!byGid[g]) { byGid[g] = []; units.push(byGid[g]); }
        byGid[g].push(n);
      } else {
        units.push([n]);
      }
    });
    return units;
  }

  function duplicateSelection() {
    if (!selection.length) return;
    var made = [];
    var missed = false;
    unitsOfSelection().forEach(function (unit) {
      var clones = duplicateUnit(unit);
      if (!clones.length) return;
      if (!placeUnitFree(clones, 'best')) missed = true;
      made = made.concat(clones);
    });
    if (!made.length) return;
    setSelection(made);
    renderList();
    refreshAll();
    pushHistory();
    if (missed) toast('No space left on the sheet. The copy was placed below it.');
  }

  function rotateSelection90() {
    if (!selection.length) return;
    rotateNodesAsUnit(selection);
    refreshAll();
    pushHistory();
  }

  // Align the selected units flush to the selection's leftmost/topmost edge.
  // Units (groups) travel as one piece, like everywhere else.
  function alignSelection(edge) {
    var units = unitsOfSelection();
    if (units.length < 2) return;
    var boxes = units.map(function (u) { return { u: u, b: unionBBox(u) }; });
    var target = Infinity;
    boxes.forEach(function (x) {
      target = Math.min(target, edge === 'left' ? x.b.minX : x.b.minY);
    });
    boxes.forEach(function (x) {
      var d = (edge === 'left' ? target - x.b.minX : target - x.b.minY);
      x.u.forEach(function (n) {
        n.move(edge === 'left' ? { x: d, y: 0 } : { x: 0, y: d });
      });
    });
    refreshAll();
    pushHistory();
  }

  // Centrar horizontalmente. Hace una cosa u otra segun cuantos haya seleccionados,
  // que es lo que espera cualquiera que venga de un editor de diseno:
  //   - un solo diseno: no hay nada con que alinearlo, asi que se centra EN LA HOJA.
  //   - dos o mas: se alinean sus centros ENTRE ELLOS, como hacen alinear-izquierda y
  //     alinear-arriba. Antes movia todo el bloque a la mitad de la hoja, que no es
  //     alinear: dejaba los disenos igual de descolocados unos respecto a otros.
  function centerSelection() {
    var units = unitsOfSelection();
    if (!units.length) return;

    if (units.length === 1) {
      var u = unionBBox(selection);
      var dxHoja = SHEET_W_IN / 2 - (u.minX + u.maxX) / 2;
      selection.forEach(function (n) { n.move({ x: dxHoja, y: 0 }); });
    } else {
      var boxes = units.map(function (g) { return { u: g, b: unionBBox(g) }; });
      var izq = Infinity, der = -Infinity;
      boxes.forEach(function (x) {
        izq = Math.min(izq, x.b.minX);
        der = Math.max(der, x.b.maxX);
      });
      var eje = (izq + der) / 2;
      boxes.forEach(function (x) {
        var d = eje - (x.b.minX + x.b.maxX) / 2;
        x.u.forEach(function (n) { n.move({ x: d, y: 0 }); });
      });
    }
    refreshAll();
    pushHistory();
  }

  // Distribute the selected units so the GAPS between them are equal along the
  // axis (Illustrator's "distribute spacing"). The two outermost units stay
  // put; the ones in between are re-spaced. Needs 3+ units to do anything.
  function distributeSelection(axis) {
    var units = unitsOfSelection();
    if (units.length < 3) return;
    var lo = axis === 'h' ? 'minX' : 'minY';
    var hi = axis === 'h' ? 'maxX' : 'maxY';
    var boxes = units.map(function (u) { return { u: u, b: unionBBox(u) }; });
    boxes.sort(function (a, b) { return a.b[lo] - b.b[lo]; });
    var first = boxes[0], last = boxes[boxes.length - 1];
    var span = last.b[hi] - first.b[lo];
    var sumSize = 0;
    boxes.forEach(function (x) { sumSize += x.b[hi] - x.b[lo]; });
    var gap = (span - sumSize) / (boxes.length - 1);
    var cursor = first.b[lo];
    boxes.forEach(function (x) {
      var target = cursor;
      var d = target - x.b[lo];
      x.u.forEach(function (n) {
        n.move(axis === 'h' ? { x: d, y: 0 } : { x: 0, y: d });
      });
      cursor += (x.b[hi] - x.b[lo]) + gap;
    });
    refreshAll();
    pushHistory();
  }

  // ---------------------------------------------------------------- groups
  function groupSelection() {
    if (unitsOfSelection().length < 2) {
      toast('Select two or more designs first (Shift+click or drag a box around them).');
      return;
    }
    var gid = 'g-' + (++seq);
    selection.forEach(function (n) { n.setAttr('groupId', gid); });
    setSelection(selection);
    refreshAll();
    pushHistory();
    toast('Grouped ' + selection.length + ' designs. They now move, rotate and duplicate together.');
  }

  function ungroupSelection() {
    var any = false;
    selection.forEach(function (n) {
      if (n.getAttr('groupId')) { n.setAttr('groupId', null); any = true; }
    });
    if (!any) return;
    refreshAll();
    updateToolbarState();
    updateHint();
    pushHistory();
    toast('Ungrouped.');
  }

  // --------------------------------------------------------------- history
  function snapOfNode(n) {
    return {
      s: n.getAttr('sourceId'),
      q: nodeSeqNum(n),
      x: round4(n.x()), y: round4(n.y()),
      w: round4(n.width() * n.scaleX()), h: round4(n.height() * n.scaleY()),
      r: round4(n.rotation()),
      v: n.getAttr('cardVariant') || 0,
      g: n.getAttr('groupId') || null,
      l: isLocked(n) ? 1 : 0
    };
  }

  // Se guarda el TIPO, no el ancho: el ancho es derivado y podria cambiar en la tienda.
  // Se conserva sheetW por compatibilidad con proyectos guardados por la version por
  // ancho, para que a nadie se le pierda el trabajo al actualizar el builder.
  function snapshotState() {
    return { typeId: typeId, sheetW: SHEET_W_IN, sheetH: sheetHIn, auto: largoAuto, items: itemNodes().map(snapOfNode),
             hoja: hojaActual, hojas: todasLasHojas() };
  }

  function nodeFromSnap(src, it) {
    seq = Math.max(seq, it.q || 0);
    return makeNode(src, {
      x: it.x, y: it.y, width: it.w, height: it.h,
      rotation: it.r || 0,
      cardVariant: it.v || 0,
      groupId: it.g || null,
      locked: !!it.l
    }, it.q ? 'item-' + it.q : null);
  }

  function pushHistory() {
    var s = snapshotState();
    var top = undoStack[undoStack.length - 1];
    if (top && JSON.stringify(top) === JSON.stringify(s)) {
      scheduleSave();
      return;
    }
    undoStack.push(s);
    if (undoStack.length > HISTORY_MAX) undoStack.shift();
    redoStack.length = 0;
    gcSources();
    scheduleSave();
    updateToolbarState();
  }

  // transformend/dragend can fire once per node of a multi-selection; fold
  // them into a single history entry
  var pushTimer = null;
  function pushHistorySoon() {
    clearTimeout(pushTimer);
    pushTimer = setTimeout(pushHistory, 0);
  }

  function restoreState(s) {
    if (s.hojas && s.hojas.length) {
      hojas = s.hojas.map(function (h) { return { sheetH: h.sheetH, auto: h.auto !== false, items: h.items.slice(), copias: Math.max(1, h.copias || 1) }; });
      hojaActual = Math.min(s.hoja || 0, hojas.length - 1);
      var actual = hojas[hojaActual];
      copiasActual = actual.copias;
      s = { typeId: s.typeId, sheetW: s.sheetW, sheetH: actual.sheetH, auto: actual.auto, items: actual.items };
    } else {
      hojas = [null];
      hojaActual = 0;
      copiasActual = 1;
    }
    setSelection([]);
    itemNodes().forEach(function (n) { n.destroy(); });
    s.items.forEach(function (it) {
      var src = sources.get(it.s);
      if (src) nodeFromSnap(src, it);
    });
    var geomChanged = false;
    // Proyecto guardado con la version por TIPO.
    var guardado = s.typeId ? typeById(s.typeId) : null;
    // Proyecto guardado con la version vieja por ANCHO: no sabemos que producto era,
    // asi que se recupera el primer tipo que se venda a ese ancho. Es una suposicion,
    // pero conserva la geometria del cliente en vez de tirarla.
    if (!guardado && s.sheetW) {
      for (var iT = 0; iT < TYPES.length; iT++) {
        if (TYPES[iT].widthIn === s.sheetW) { guardado = TYPES[iT]; break; }
      }
    }
    if (guardado && guardado.id !== typeId) {
      typeId = guardado.id;
      SHEET_W_IN = guardado.widthIn || SHEET_W_IN;
      if (el.typeSelect) el.typeSelect.value = typeId;
      updateWidthLabel();
      rebuildHeightOptions();   // el tipo nuevo ofrece otra escalera de largos
      geomChanged = true;
    }
    // By size no tiene largo que elegir (no se cobra por hoja): siempre automatico.
    var autoGuardado = currentType() && currentType().porTamano ? true : s.auto;
    if (typeof autoGuardado === 'boolean' && autoGuardado !== largoAuto) {
      largoAuto = autoGuardado;
      geomChanged = true;
    }
    if (s.sheetH !== sheetHIn) {
      sheetHIn = nearestHeight(s.sheetH);
      geomChanged = true;
    }
    el.sheetSelect.value = largoAuto ? 'auto' : String(sheetHIn);
    if (geomChanged) applyZoom(zoom);
    renderList();
    refreshAll();
    pintarHojas();
    gcSources();
    scheduleSave();
  }

  function undo() {
    if (undoStack.length < 2) return;
    redoStack.push(undoStack.pop());
    restoreState(undoStack[undoStack.length - 1]);
    updateToolbarState();
  }

  function redo() {
    if (!redoStack.length) return;
    var s = redoStack.pop();
    undoStack.push(s);
    restoreState(s);
    updateToolbarState();
  }


  // ----------------------------------------------------------------- hojas
  // VARIAS HOJAS (20-oct-2026). Como el builder de Kixxl: cada hoja llega a MAX_LARGO_IN
  // (168") y, en largo automatico, lo que no cabe pasa a la hoja siguiente. En el lienzo se
  // ve UNA hoja; las demas viven aqui como instantaneas con la misma forma que guarda el
  // historial ({ sheetH, auto, items }). hojas[hojaActual] solo esta al dia despues de
  // guardarHojaActual(): el lienzo manda mientras se edita.
  var hojas = [null];
  var hojaActual = 0;
  // Copias de cada hoja (21-oct-2026): viven CON la hoja, para que la columna de hojas y el
  // resumen muestren lo mismo, se guarden y se puedan deshacer. La de la hoja del lienzo va aparte
  // (como su largo), las demas en hojas[i].copias.
  var copiasActual = 1;

  function copiasDe(i) {
    var c = i === hojaActual ? copiasActual : (hojas[i] && hojas[i].copias);
    return Math.max(1, c || 1);
  }

  function ponerCopias(i, n) {
    n = Math.min(99, Math.max(1, Math.round(n) || 1));
    if (i === hojaActual) copiasActual = n;
    else if (hojas[i]) hojas[i].copias = n;
  }

  function hojaVacia() { return { sheetH: SHEET_HEIGHTS[0], auto: true, items: [], copias: 1 }; }

  function estadoHojaActual() {
    return { sheetH: sheetHIn, auto: largoAuto, items: itemNodes().map(snapOfNode), copias: copiasActual };
  }

  function guardarHojaActual() { hojas[hojaActual] = estadoHojaActual(); }

  // Todas las hojas, con la del lienzo al dia. Copias: no comparten arrays con 'hojas'.
  function todasLasHojas() {
    return hojas.map(function (h, i) {
      var x = i === hojaActual ? estadoHojaActual() : h;
      return { sheetH: x.sheetH, auto: x.auto !== false, items: x.items.slice(), copias: Math.max(1, x.copias || 1) };
    });
  }

  function disenosEnOtrasHojas() {
    var n = 0;
    hojas.forEach(function (h, i) { if (i !== hojaActual && h) n += h.items.length; });
    return n;
  }

  // Pone en el lienzo los disenos de una hoja. Sin historial: eso lo decide quien llama.
  function cargarHoja(h) {
    setSelection([]);
    itemNodes().forEach(function (n) { n.destroy(); });
    (h.items || []).forEach(function (it) {
      var src = sources.get(it.s);
      if (src) nodeFromSnap(src, it);
    });
    largoAuto = h.auto !== false;
    copiasActual = Math.max(1, h.copias || 1);
    sheetHIn = largoAuto ? largoQueToca() : nearestHeight(h.sheetH);
    var opt = el.sheetSelect.querySelector('option[value="auto"]');
    if (opt) opt.textContent = textoOpcionAuto();
    el.sheetSelect.value = largoAuto ? 'auto' : String(sheetHIn);
    applyZoom(zoom);
    renderList();
    refreshAll();
  }

  function irAHoja(i) {
    if (i === hojaActual || !hojas[i]) return;
    guardarHojaActual();
    hojaActual = i;
    cargarHoja(hojas[i]);
    if (el.scroll) el.scroll.scrollTop = 0;
    pintarHojas();
    pushHistory();
  }

  function nuevaHoja() {
    guardarHojaActual();
    hojas.push(hojaVacia());
    hojaActual = hojas.length - 1;
    cargarHoja(hojas[hojaActual]);
    pintarHojas();
    pushHistory();
    toast('Sheet ' + hojas.length + ' added. New designs go on this sheet.');
  }

  function quitarHoja(i) {
    if (hojas.length < 2) return;
    guardarHojaActual();
    var n = hojas[i].items.length;
    if (n && !window.confirm('Remove sheet ' + (i + 1) + ' and its ' + n + ' design' + (n === 1 ? '' : 's') + '?')) return;
    hojas.splice(i, 1);
    if (hojaActual >= i && hojaActual > 0) hojaActual--;
    cargarHoja(hojas[hojaActual]);
    pintarHojas();
    pushHistory();
  }

  // Agrupa nodos en unidades (un grupo se mueve entero).
  function unidadesDe(nodos) {
    var units = [], porG = {};
    nodos.forEach(function (n) {
      var g = n.getAttr('groupId');
      if (g) {
        if (!porG[g]) { porG[g] = []; units.push(porG[g]); }
        porG[g].push(n);
      } else {
        units.push([n]);
      }
    });
    return units;
  }

  // ---- colocar en hojas que NO estan en el lienzo (27-oct-2026) -------------------------
  // Antes, pasar disenos a otra hoja la CARGABA en el lienzo (y la siguiente, y la otra): cada
  // carga redimensiona los lienzos de Konva al largo de la hoja (hasta 32 MP por capa) y eso
  // costaba ~150 ms por hoja; con 5 hojas, casi un segundo congelado por cada copia. Ahora se
  // coloca sobre los datos de cada hoja (sus instantaneas) con el mismo motor MaxRects, y el
  // lienzo solo vuelve a pintar la hoja que se esta viendo.
  function esquinasSnap(it) {
    var rad = (it.r || 0) * Math.PI / 180, cos = Math.cos(rad), sin = Math.sin(rad);
    function pt(px, py) { return { x: it.x + px * cos - py * sin, y: it.y + px * sin + py * cos }; }
    return [pt(0, 0), pt(it.w, 0), pt(it.w, it.h), pt(0, it.h)];
  }
  function cajaSnap(it) {
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    esquinasSnap(it).forEach(function (c) {
      minX = Math.min(minX, c.x); minY = Math.min(minY, c.y); maxX = Math.max(maxX, c.x); maxY = Math.max(maxY, c.y);
    });
    return { minX: minX, minY: minY, maxX: maxX, maxY: maxY, w: maxX - minX, h: maxY - minY };
  }
  function cajaSnaps(u) {
    var b = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    u.forEach(function (it) {
      var c = cajaSnap(it);
      b.minX = Math.min(b.minX, c.minX); b.minY = Math.min(b.minY, c.minY); b.maxX = Math.max(b.maxX, c.maxX); b.maxY = Math.max(b.maxY, c.maxY);
    });
    b.w = b.maxX - b.minX; b.h = b.maxY - b.minY;
    return b;
  }
  // girar 90 grados una unidad de instantaneas como pieza rigida (lo mismo que rotateNodesAsUnit)
  function girarSnaps(u) {
    var b = cajaSnaps(u), cx = (b.minX + b.maxX) / 2, cy = (b.minY + b.maxY) / 2;
    u.forEach(function (it) {
      it.r = ((it.r || 0) + 90) % 360;
      var x = it.x, y = it.y;
      it.x = cx - (y - cy); it.y = cy + (x - cx);
    });
  }
  function moverSnaps(u, X, Y) {
    var b = cajaSnaps(u), dx = X - b.minX, dy = Y - b.minY;
    u.forEach(function (it) { it.x = round4(it.x + dx); it.y = round4(it.y + dy); });
  }
  function unidadesSnaps(items) {
    var units = [], porG = {};
    items.forEach(function (it) {
      if (it.g) { if (!porG[it.g]) { porG[it.g] = []; units.push(porG[it.g]); } porG[it.g].push(it); }
      else units.push([it]);
    });
    return units;
  }
  function claveDeSnap(it) {   // la misma clave que groupKeyOf: una tarjeta = un arte a un tamano
    return it.s + '|' + (it.v || 0) + '|' + Number(it.w).toFixed(2) + '|' + Number(it.h).toFixed(2);
  }
  // el largo maximo de una hoja en automatico, mire o no el lienzo esa hoja
  function tipoExacto() { var t = currentType(); return !!(t && (t.exacto || t.mayoreo || t.porTamano)); }
  function maxAlturaAuto() {
    if (!tipoExacto()) return SHEET_HEIGHTS[SHEET_HEIGHTS.length - 1];
    var t = currentType();
    return (t.exacto && t.exacto.max) ? Math.min(MAX_LARGO_IN, t.exacto.max) : MAX_LARGO_IN;
  }
  function capacidadHoja(h) { return h.auto !== false ? maxAlturaAuto() : h.sheetH; }
  // el largo que toca a una hoja en automatico por lo que lleva (lo mismo que largoQueToca)
  function largoDeItems(items) {
    var fondo = 0;
    items.forEach(function (it) { fondo = Math.max(fondo, cajaSnap(it).maxY); });
    if (tipoExacto()) {
      var t = currentType(), e = t.exacto || { paso: 2, min: SHEET_HEIGHTS[0] || 12 };
      var L = Math.ceil(((items.length ? fondo + MARGIN_IN : 0) - 1e-9) / e.paso) * e.paso;
      return Math.min(maxAlturaAuto(), Math.max(e.min, MIN_LARGO_IN, L));
    }
    if (!items.length) return SHEET_HEIGHTS[0];
    return nearestHeight(fondo + MARGIN_IN);
  }
  function motorDeHoja(h) {
    var eng = Packing.create({ width: SHEET_W_IN - 2 * MARGIN_IN, height: capacidadHoja(h) - 2 * MARGIN_IN, gap: getSpacing() });
    h.items.forEach(function (it) { var b = cajaSnap(it); eng.occupy(b.minX - MARGIN_IN, b.minY - MARGIN_IN, b.w, b.h); });
    return eng;
  }
  // Coloca unidades (listas de instantaneas ya con su tamano) en las hojas de 'orden' (indices de
  // 'hojas', con la del lienzo ya guardada), la primera donde quepan, y en hojas nuevas al final
  // si no caben en ninguna. Las piezas iguales van todas con la misma orientacion (la que gasta
  // menos largo, como Auto-arrange). Devuelve { porHoja: { indice: piezas }, nuevas }.
  function colocarEnHojas(units, orden) {
    var motores = {}, porHoja = {}, nuevas = 0, sp = getSpacing(), anchoUtil = SHEET_W_IN - 2 * MARGIN_IN;
    orden = orden.slice();
    function motor(i) { return motores[i] || (motores[i] = motorDeHoja(hojas[i])); }
    // orientacion por tamano: de pie o tumbadas, segun cuantas caben por fila
    var porTam = {};
    units.forEach(function (u) {
      if (u.length !== 1 || ((Math.round(u[0].r || 0) % 90) !== 0)) return;
      var it = u[0], k = [Math.min(it.w, it.h).toFixed(2), Math.max(it.w, it.h).toFixed(2)].join('x');
      (porTam[k] = porTam[k] || []).push(u);
    });
    Object.keys(porTam).forEach(function (k) {
      var g = porTam[k], it0 = g[0][0], nw = it0.w, nh = it0.h;
      function largoCon(w, h) { var cols = Math.floor((anchoUtil + sp) / (w + sp) + 1e-9); return cols < 1 ? Infinity : Math.ceil(g.length / cols) * (h + sp); }
      var tumbar = largoCon(nh, nw) < largoCon(nw, nh) - 1e-6;
      g.forEach(function (u) {
        var tumbada = Math.round(u[0].r || 0) % 180 !== 0;
        if (tumbar !== tumbada) girarSnaps(u);
      });
    });
    units.forEach(function (u) {
      var colocada = false, k;
      function probar(i, girar) {
        var b = cajaSnaps(u), s = motor(i).place(b.w, b.h, girar);
        if (!s) return false;
        if (s.rotated) girarSnaps(u);
        moverSnaps(u, MARGIN_IN + s.x, MARGIN_IN + s.y);
        hojas[i].items = hojas[i].items.concat(u);
        porHoja[i] = (porHoja[i] || 0) + u.length;
        return true;
      }
      for (k = 0; k < orden.length && !colocada; k++) colocada = probar(orden[k], false);
      for (k = 0; k < orden.length && !colocada; k++) colocada = probar(orden[k], true);   // girada, si de otra forma no entra
      while (!colocada) {
        hojas.push(hojaVacia()); nuevas++;
        var j = hojas.length - 1;
        orden.push(j);
        colocada = probar(j, true);
        if (!colocada) { hojas[j].items = hojas[j].items.concat(u); porHoja[j] = (porHoja[j] || 0) + u.length; break; }   // mas grande que una hoja vacia: no deberia llegar aqui
      }
    });
    Object.keys(porHoja).forEach(function (i) { var h = hojas[i]; if (h.auto !== false) h.sheetH = largoDeItems(h.items); });
    return { porHoja: porHoja, nuevas: nuevas };
  }
  // Las hojas que se quedaron sin disenos se van (nunca la del lienzo ni la ultima que quede).
  function quitarHojasVacias() {
    for (var i = hojas.length - 1; i >= 0; i--) {
      if (hojas.length < 2) break;
      if (i !== hojaActual && hojas[i] && !hojas[i].items.length) { hojas.splice(i, 1); if (hojaActual > i) hojaActual--; }
    }
  }

  // Disenos que no caben en esta hoja (en automatico: mas alla de los 168"). Van a la primera
  // OTRA hoja donde quepan (tambien las anteriores: asi un diseno pequeno aprovecha el hueco que
  // quedo al final de la hoja 1) y, si no caben en ninguna, a una hoja nueva. Con un largo
  // elegido a mano no se mueven: se quedan debajo con el aviso de "fuera de la hoja", como
  // siempre. El lienzo no se mueve de hoja. Devuelve { n, hojas: [indices a los que fueron] }.
  function desbordar(nodos) {
    if (!largoAuto || !nodos.length) return { n: 0, hojas: [] };
    // Lo que no cabe ni en una hoja VACIA (mas ancho y mas largo que la hoja, aun girado) no se mueve:
    // se queda donde esta, marcado como fuera de la hoja. Si no, cada hoja nueva lo volvia a pasar a
    // la siguiente y la pagina se colgaba (auditoria 23-oct).
    var enormes = [], movibles = [];
    unidadesDe(nodos).forEach(function (u) {
      var b = unionBBox(u);
      var vacia = Packing.create({ width: SHEET_W_IN - 2 * MARGIN_IN, height: alturaTrabajo() - 2 * MARGIN_IN, gap: 0 });
      (vacia.place(b.w, b.h, u.length === 1) ? movibles : enormes).push(u);
    });
    if (enormes.length) {
      toast(enormes.length === 1 ? 'One design is bigger than a whole sheet, so it stays where it is. Make it smaller to fit.'
        : enormes.length + ' designs are bigger than a whole sheet, so they stay where they are. Make them smaller to fit.');
      nodos = [];
      movibles.forEach(function (u) { nodos = nodos.concat(u); });
      if (!nodos.length) return { n: 0, hojas: [] };
    }
    var snaps = nodos.map(snapOfNode);
    setSelection([]);
    nodos.forEach(function (n) { n.destroy(); });
    ajustarLargo();        // sin lo que se va, la hoja puede ser mas corta: guardarla ya ajustada
    guardarHojaActual();
    var orden = [];
    for (var i = 0; i < hojas.length; i++) if (i !== hojaActual) orden.push(i);
    var r = colocarEnHojas(unidadesSnaps(snaps), orden);
    pintarHojas();
    return { n: snaps.length, hojas: Object.keys(r.porHoja).map(Number).sort(function (a, b) { return a - b; }) };
  }
  function nombresHojas(lista) {
    var t = lista.map(function (i) { return i + 1; });
    return t.length === 1 ? 'sheet ' + t[0] : 'sheets ' + t.slice(0, -1).join(', ') + ' and ' + t[t.length - 1];
  }

  // Pestanas encima del lienzo: una por hoja, con su largo y su precio, y "+ Add sheet".
  function precioDeHoja(h) {
    if (currentType() && (currentType().mayoreo || currentType().porTamano)) return null;
    var o = ofertaHoja(currentType(), h.sheetH);
    return o ? o.price : null;
  }

  // Miniatura de una hoja: su arte en pequeno, para reconocerla en la columna de hojas.
  function pintarMiniatura(cv, h) {
    var maxW = 54, maxH = 84;
    var esc = Math.min(maxW / SHEET_W_IN, maxH / Math.max(h.sheetH, 1));
    var w = Math.max(8, Math.round(SHEET_W_IN * esc)), hh = Math.max(8, Math.round(h.sheetH * esc));
    var dpr = window.devicePixelRatio || 1;
    cv.width = w * dpr; cv.height = hh * dpr;
    cv.style.width = w + 'px'; cv.style.height = hh + 'px';
    var ctx = cv.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(0, 0, w, hh);
    h.items.forEach(function (it) {
      var src = sources.get(it.s);
      if (!src || !src.previewCanvas) return;
      ctx.save();
      ctx.translate(it.x * esc, it.y * esc);
      ctx.rotate((it.r || 0) * Math.PI / 180);
      try { ctx.drawImage(src.previewCanvas, 0, 0, it.w * esc, it.h * esc); } catch (e) { /* arte aun cargando */ }
      ctx.restore();
    });
  }

  // BY SIZE no se vende por hoja (21-oct-2026, la primera prueba del usuario: «esta mal»): fuera
  // el ancho, el largo y las copias de hoja, la izquierda explica el precio por tamano y la
  // columna de la derecha da el precio de cada diseno. Las hojas siguen existiendo, solo para el
  // taller. Lo de cada modo va en la plantilla con las clases solo-hoja / solo-tamano.
  function aplicarModo() {
    var t = currentType();
    var tam = !!(t && t.porTamano);
    [el.appHeader, el.appMain].forEach(function (x) { if (x) x.classList.toggle('modo-tamano', tam); });
    if (el.sheetTabs && el.sheetTabs.parentNode) {
      el.sheetTabs.parentNode.setAttribute('aria-label', tam ? 'Price of each design' : 'Sheets in this order');
    }
    if (!tam) return false;
    var tr = t.tramosTam, ult = tr[tr.length - 1];
    var datos = {
      tarifa: precioIn2(tr[0].price / t.unidadIn2),
      min1: tr[1] ? String(tr[1].min) : '',
      desc1: tr[1] ? '\u2212' + descTramo(t, tr[1]) + '%' : '',
      minN: String(ult.min),
      descN: '\u2212' + descTramo(t, ult) + '%',
      corte: t.corte ? money(t.corte.price) : ''
    };
    Array.prototype.forEach.call(RAIZ.querySelectorAll('[data-tam]'), function (x) {
      x.textContent = datos[x.getAttribute('data-tam')] || '';
    });
    Array.prototype.forEach.call(RAIZ.querySelectorAll('[data-tam-si="corte"]'), function (x) {
      x.hidden = !t.corte;
    });
    if (el.modeNote) el.modeNote.textContent = 'Priced per design \u00b7 ' + datos.tarifa + ' / sq in';
    return true;
  }

  // $0.05 y no $0.050; $0.045 si hace falta el tercer decimal.
  function precioIn2(v) {
    return '$' + (Math.round(v * 1000) / 1000).toFixed(3).replace(/0$/, '');
  }

  function descTramo(t, x) {
    return Math.round((1 - x.price / t.tramosTam[0].price) * 100);
  }

  // Columna en by size: el pedido (disenos, piezas, corte, total, plazo y el boton para seguir) y la
  // escalera de precio. El precio de cada diseno va en su tarjeta, en el centro.
  var corteTamano = false;   // corte elegido aqui o en el resumen: el mismo en los dos sitios
  function pintarPreciosTamano(t, lista) {
    var grupos = gruposDeHojas(lista);
    var calc = precioPorTamano(t, grupos, corteTamano);
    if (!grupos.length) {
      var vacio = document.createElement('p');
      vacio.className = 'tam-empty';
      vacio.textContent = 'Upload a design and its price shows up here.';
      el.sheetTabs.appendChild(vacio);
    } else {
      var res = document.createElement('div');
      res.className = 'tam-sum';
      [['Designs', String(grupos.length)], ['Pieces', String(calc.piezas)]].forEach(function (f) {
        var r = document.createElement('div');
        var a = document.createElement('span');
        a.textContent = f[0];
        var b = document.createElement('b');
        b.textContent = f[1];
        r.appendChild(a); r.appendChild(b);
        res.appendChild(r);
      });
      el.sheetTabs.appendChild(res);
      if (t.corte) {
        var sw = document.createElement('label');
        sw.className = 'tam-switch';
        var caja = document.createElement('input');
        caja.type = 'checkbox';
        caja.checked = corteTamano;
        var txt = document.createElement('span');
        var tb = document.createElement('b');
        tb.textContent = 'Precut each piece';
        var ts = document.createElement('small');
        ts.textContent = '+' + money(t.corte.price) + ' per piece, ready to press';
        txt.appendChild(tb); txt.appendChild(ts);
        sw.appendChild(caja); sw.appendChild(txt);
        caja.addEventListener('change', function () { corteTamano = caja.checked; pintarHojas(); });
        el.sheetTabs.appendChild(sw);
      }
      var tt = document.createElement('div');
      tt.className = 'sheet-total tam-total';
      var lt = document.createElement('span');
      lt.textContent = 'Total';
      var vt = document.createElement('b');
      vt.textContent = money(calc.total);
      tt.appendChild(lt); tt.appendChild(vt);
      el.sheetTabs.appendChild(tt);
      var pl = document.createElement('p');
      pl.className = 'tam-note';
      pl.textContent = (corteTamano ? 'Ships within 48 hours, Monday to Friday' : notaCortaEstandar(t)) + '. Shipping and taxes at checkout.';
      el.sheetTabs.appendChild(pl);
      var seguir = document.createElement('button');
      seguir.type = 'button';
      seguir.className = 'btn btn-primary tam-go';
      seguir.textContent = 'Review order · ' + money(calc.total);
      seguir.addEventListener('click', function () { if (el.exportBtn && !el.exportBtn.disabled) el.exportBtn.click(); });
      el.sheetTabs.appendChild(seguir);
    }
    // Escalera de precio (la misma de la ficha): piezas DEL MISMO diseno
    var esc = document.createElement('div');
    esc.className = 'tam-ladder';
    var et = document.createElement('p');
    et.className = 'tam-ladder-title';
    et.textContent = 'Pieces of the same design';
    esc.appendChild(et);
    t.tramosTam.forEach(function (x, k) {
      var r = document.createElement('div');
      var a = document.createElement('span');
      var sig = t.tramosTam[k + 1];
      a.textContent = sig ? x.min + '–' + (sig.min - 1) : x.min + '+';
      var b = document.createElement('b');
      b.textContent = k ? '−' + descTramo(t, x) + '%' : precioIn2(x.price / t.unidadIn2) + ' / sq in';
      r.appendChild(a); r.appendChild(b);
      esc.appendChild(r);
    });
    el.sheetTabs.appendChild(esc);
  }

  // ------------------------------------------------- by size: tarjetas en vez de hoja
  // En by size el cliente NO ve la hoja (21-oct-2026, idea del usuario a partir del editor by size
  // de Kixxl, «solo le interesa subir sus disenos y elegir la cantidad»): una tarjeta por diseno con
  // su medida, piezas, precio y descuento. Las hojas siguen existiendo porque el taller las imprime:
  // el builder las rehace solas con cada cambio (rehacerTamano) y van con el pedido como siempre.
  // Una tarjeta = un arte a una medida (fuente + cardVariant + medida) con TODAS sus piezas, de
  // todas las hojas.
  function tarjetasTamano() {
    var porK = {}, orden = [];
    todasLasHojas().forEach(function (h) {
      h.items.forEach(function (it) {
        var k = it.s + '|' + (it.v || 0) + '|' + it.w.toFixed(2) + 'x' + it.h.toFixed(2);
        var c = porK[k];
        if (!c) { c = porK[k] = { k: k, s: it.s, v: it.v || 0, w: it.w, h: it.h, n: 0, q: it.q || 0 }; orden.push(c); }
        c.n++;
        if ((it.q || 0) < c.q) c.q = it.q || 0;
      });
    });
    return orden.filter(function (c) { return sources.get(c.s); }).sort(function (a, b) { return a.q - b.q; });
  }

  function dos(x) { return Math.round(x * 100) / 100; }
  function txtIn(x) { return x.toFixed(2).replace(/\.?0+$/, ''); }

  // La medida mas grande que cabe: el lado corto a lo ancho de la hoja y el largo hasta el tope.
  function medidaValida(w, h) {
    var ancho = SHEET_W_IN - 2 * MARGIN_IN, largo = MAX_LARGO_IN - 2 * MARGIN_IN;
    var corto = Math.min(w, h), lar = Math.max(w, h), f = 1;
    if (corto > ancho) f = Math.min(f, ancho / corto);
    if (lar > largo) f = Math.min(f, largo / lar);
    if (corto * f < 0.5) f = 0.5 / corto;
    return { w: dos(w * f), h: dos(h * f), ajustada: Math.abs(f - 1) > 1e-6 };
  }

  // Alto de una hoja hecha a mano (la regla de largoQueToca en exacto: de 2 en 2, minimo 12).
  function largoDeHoja(items) {
    var maxY = 0;
    items.forEach(function (it) { maxY = Math.max(maxY, bboxOfSnap(it).maxY); });
    var L = Math.ceil((maxY + MARGIN_IN - 1e-9) / 2) * 2;
    return Math.min(MAX_LARGO_IN, Math.max(SHEET_HEIGHTS[0] || 12, MIN_LARGO_IN, L));
  }

  // ACOMODO EN GUILLOTINA (21-oct-2026). Primero by size iba con MaxRects (piezas giradas y salteadas:
  // «con precut estaría muy complicado de trabajar») y luego en filas simples (rectas, pero un diseno de
  // 13.85" dejaba una franja vacia de 7.5" al lado: «ahora desperdician mucho espacio... sería como
  // buscar patrones»). Este motor es puro (no toca el lienzo) y lo usan by size y el Auto-arrange:
  //   - los disenos van del mas grande al mas pequeno, y cada uno se coloca en BLOQUES: una cuadricula
  //     de piezas iguales, todas con la misma orientacion dentro del bloque;
  //   - cada bloque va al primer hueco libre (hoja, de arriba abajo, de izquierda a derecha) donde cabe
  //     alguna pieza, con la orientacion que mete mas piezas en ese hueco (de pie si da igual);
  //   - el hueco se parte con cortes rectos: lo que queda a la derecha del bloque (de su alto), lo que
  //     queda debajo (de todo el ancho) y lo que sobra de su ultima fila pasan a ser huecos, y ahi
  //     entran los disenos mas pequenos. Asi la franja al lado de un diseno grande se llena, y TODA la
  //     hoja se puede separar con cortes rectos de lado a lado, que es como se corta a mano.
  // piezas: [{ w, h, n, girable }] en pulgadas (w x h = como esta el arte, sin girar). Devuelve, por
  // cada pieza, sus sitios [{ hoja, x, y, gir }]: x, y = esquina de su caja (ya girada) dentro del area
  // util; gir = tumbada 90 respecto a w x h. Lo que no cabe ni en una hoja vacia se queda sin sitio.
  function planGuillotina(piezas, ancho, alto, sp) {
    var BW = ancho + sp, BH = alto + sp, EPS = 1e-6;
    // Espacio de "celdas": cada pieza ocupa su medida + la separacion, y el area se alarga lo mismo
    // (igual que packing.js), asi dos piezas quedan siempre a 'sp' y pueden tocar el margen.
    var hojasL = [];
    function otra() { var h = { libres: [{ x: 0, y: 0, w: BW, h: BH }] }; hojasL.push(h); return h; }
    function hueco(h, x, y, w, hh) {
      if (w <= EPS || hh <= EPS) return;
      h.libres.push({ x: x, y: y, w: w, h: hh });
      h.libres.sort(function (a, b) { return a.y - b.y || a.x - b.x; });
    }
    function bloque(p, F, quedan) {
      var mejor = null;
      (p.girable ? [false, true] : [false]).forEach(function (gir) {
        var cw = (gir ? p.h : p.w) + sp, ch = (gir ? p.w : p.h) + sp;
        var cols = Math.floor((F.w + EPS) / cw), filas = Math.floor((F.h + EPS) / ch);
        if (cols < 1 || filas < 1) return;
        var k = Math.min(cols * filas, quedan);
        var usadas = Math.min(k, cols);
        var op = { gir: gir, cw: cw, ch: ch, cols: usadas, k: k, bw: usadas * cw, bh: Math.ceil(k / cols) * ch };
        // mas piezas gana; con las mismas, de pie salvo que tumbado ocupe un 5 % menos
        if (!mejor || op.k > mejor.k || (op.k === mejor.k && op.bw * op.bh < mejor.bw * mejor.bh * 0.95)) mejor = op;
      });
      return mejor;
    }
    otra();
    var sitios = piezas.map(function () { return []; });
    var orden = piezas.map(function (p, i) { return i; }).sort(function (a, b) {
      var A = piezas[a], B = piezas[b];
      return Math.max(B.w, B.h) - Math.max(A.w, A.h) || B.w * B.h - A.w * A.h || a - b;
    });
    orden.forEach(function (pi) {
      var p = piezas[pi], quedan = p.n, vueltas = 0;
      while (quedan > 0 && vueltas++ < 5000) {
        var hi = -1, F = null, op = null;
        for (var a = 0; a < hojasL.length && !op; a++) {
          for (var li = 0; li < hojasL[a].libres.length; li++) {
            var o = bloque(p, hojasL[a].libres[li], quedan);
            if (o) { hi = a; F = hojasL[a].libres[li]; op = o; break; }
          }
        }
        if (!op) {
          var vacia = otra();
          op = bloque(p, vacia.libres[0], quedan);
          if (!op) { hojasL.pop(); break; }   // ni en un area vacia: se queda sin sitio
          hi = hojasL.length - 1; F = vacia.libres[0];
        }
        for (var i = 0; i < op.k; i++) {
          sitios[pi].push({ hoja: hi, x: F.x + (i % op.cols) * op.cw, y: F.y + Math.floor(i / op.cols) * op.ch, gir: op.gir });
        }
        var h = hojasL[hi];
        h.libres.splice(h.libres.indexOf(F), 1);
        hueco(h, F.x + op.bw, F.y, F.w - op.bw, op.bh);
        hueco(h, F.x, F.y + op.bh, F.w, F.h - op.bh);
        var ult = op.k - (Math.ceil(op.k / op.cols) - 1) * op.cols;
        if (ult < op.cols) hueco(h, F.x + ult * op.cw, F.y + op.bh - op.ch, (op.cols - ult) * op.cw, op.ch);
        quedan -= op.k;
      }
    });
    return { sitios: sitios, hojas: hojasL.length };
  }

  // By size: rehace TODAS las hojas a partir de las tarjetas con el motor de guillotina, con el margen
  // y la separacion de siempre (la guia de corte solo dibuja, no pide espacio: decision del usuario).
  // Hoja nueva solo cuando ya no cabe en ninguna. Los numeros de orden siguen el orden de las
  // tarjetas, asi que ninguna cambia de sitio en pantalla.
  function rehacerTamano(cards, soloSiCambia) {
    var ancho = SHEET_W_IN - 2 * MARGIN_IN, largo = MAX_LARGO_IN - 2 * MARGIN_IN;
    var sp = getSpacing();
    // el mismo arte a la misma medida es una sola tarjeta (y una sola linea en el carrito)
    var unidas = [], porM = {};
    cards.forEach(function (c) {
      if (!(c.n > 0)) return;
      var m = c.s + '|' + c.w.toFixed(2) + 'x' + c.h.toFixed(2);
      if (porM[m]) { porM[m].n += c.n; return; }
      porM[m] = { s: c.s, v: c.v, w: c.w, h: c.h, n: c.n, girable: true };
      unidas.push(porM[m]);
    });
    var plan = planGuillotina(unidas, ancho, largo, sp);
    var nuevas = [];
    for (var k = 0; k < Math.max(1, plan.hojas); k++) nuevas.push({ sheetH: 0, auto: true, items: [], copias: 1 });
    unidas.forEach(function (c, i) {
      var q = seq;
      seq += c.n;
      plan.sitios[i].forEach(function (s) {
        var X = MARGIN_IN + s.x, Y = MARGIN_IN + s.y;
        // girado 90 sobre su esquina, el diseno ocupa de x - h a x: se corre su alto a la derecha
        nuevas[s.hoja].items.push({ s: c.s, q: ++q, x: round4(s.gir ? X + c.h : X), y: round4(Y), w: c.w, h: c.h,
                                    r: s.gir ? 90 : 0, v: c.v, g: null, l: 0 });
      });
    });
    nuevas.forEach(function (h) { h.sheetH = largoDeHoja(h.items); });
    // ya estaba asi (p. ej. al pulsar Finish sin tocar nada): no se toca, ni se ensucia el historial
    if (soloSiCambia && firmaHojas(nuevas) === firmaHojas(todasLasHojas())) return;
    hojas = nuevas;
    hojaActual = 0;
    largoAuto = true;
    cargarHoja(hojas[0]);
    pushHistory();
  }

  // La forma de unas hojas sin los numeros de orden: lo que se imprimiria.
  function firmaHojas(lista) {
    return lista.map(function (h) {
      return h.items.map(function (it) {
        return [it.s, round4(it.x), round4(it.y), round4(it.w), round4(it.h), it.r || 0].join(',');
      }).sort().join(';');
    }).join('|');
  }

  function cambiarTarjeta(k, fn) {
    var cards = tarjetasTamano();
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].k === k) { fn(cards[i], cards, i); break; }
    }
    rehacerTamano(cards);
  }

  function ponerMedida(k, w, h) {
    var m = medidaValida(w, h);
    if (m.ajustada) {
      toast('Resized to ' + txtIn(m.w) + ' × ' + txtIn(m.h) + ' in: a design can be up to ' +
            txtIn(SHEET_W_IN - 2 * MARGIN_IN) + ' in on its short side and at least 0.5 in.');
    }
    cambiarTarjeta(k, function (c) { c.w = m.w; c.h = m.h; });
  }

  var firmaTablero = null;
  function pintarTablero(t, lista) {
    var board = RAIZ.getElementById('tam-board');
    if (!board) return;
    var cards = tarjetasTamano();
    el.emptyState.classList.toggle('con-disenos', cards.length > 0);
    var calc = precioPorTamano(t, gruposDeHojas(lista), false);
    var porM = {};
    calc.lineas.forEach(function (l) {
      var g = l.disenos[0];
      porM[g.s + '|' + g.w.toFixed(2) + 'x' + g.h.toFixed(2)] = g;
    });
    var firma = typeId + '#' + cards.map(function (c) {
      var g = porM[c.s + '|' + c.w.toFixed(2) + 'x' + c.h.toFixed(2)];
      return c.k + ':' + c.n + ':' + (g ? g.precio : '') + ':' + (sources.get(c.s).thumbUrl || '').length;
    }).join(';');
    if (firma === firmaTablero && board.childNodes.length) return;
    firmaTablero = firma;
    // volver a poner el foco donde estaba (la tarjeta se rehace al cambiar algo)
    var a = activo();
    var foco = a && board.contains(a) && a.getAttribute('data-foco');
    board.innerHTML = '';

    if (!cards.length) {
      var drop = document.createElement('div');
      drop.className = 'tam-drop';
      drop.innerHTML = '<svg width="40" height="40" viewBox="0 0 30 30" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 19V5.5M9.6 10.4 15 5l5.4 5.4"/><path d="M5 20.5v3.2A1.8 1.8 0 0 0 6.8 25.5h16.4a1.8 1.8 0 0 0 1.8-1.8v-3.2"/></svg>';
      var h2 = document.createElement('h2');
      h2.textContent = 'Upload your designs';
      var pp = document.createElement('p');
      pp.textContent = 'Then choose the size and how many of each. No sheet to arrange: the builder lays them out for printing.';
      var bt = document.createElement('button');
      bt.type = 'button';
      bt.className = 'btn btn-primary';
      bt.textContent = 'Upload artwork';
      bt.addEventListener('click', function (e) { e.stopPropagation(); el.fileInput.click(); });
      var fm = document.createElement('small');
      fm.textContent = 'or drag & drop here · PNG, JPG, WebP or SVG · transparent PNG prints best';
      drop.appendChild(h2); drop.appendChild(pp); drop.appendChild(bt); drop.appendChild(fm);
      drop.addEventListener('click', function () { el.fileInput.click(); });
      board.appendChild(drop);
      return;
    }

    var cab = document.createElement('div');
    cab.className = 'tam-head';
    var ct = document.createElement('div');
    var h = document.createElement('h2');
    h.textContent = 'Your designs';
    var sub = document.createElement('p');
    sub.textContent = cards.length + ' design' + (cards.length === 1 ? '' : 's') + ' · ' + calc.piezas + ' piece' +
      (calc.piezas === 1 ? '' : 's') + ' · ' + money(calc.total);
    ct.appendChild(h); ct.appendChild(sub);
    var mas = document.createElement('button');
    mas.type = 'button';
    mas.className = 'btn';
    mas.textContent = '+ Upload more';
    mas.addEventListener('click', function () { el.fileInput.click(); });
    cab.appendChild(ct); cab.appendChild(mas);
    board.appendChild(cab);

    var grid = document.createElement('div');
    grid.className = 'tam-grid';
    var base = t.tramosTam[0].price / t.unidadIn2;
    cards.forEach(function (c) {
      var src = sources.get(c.s);
      var g = porM[c.s + '|' + c.w.toFixed(2) + 'x' + c.h.toFixed(2)] || { precio: 0, desc: 0 };
      var idF = c.s + '|' + c.v;
      var card = document.createElement('article');
      card.className = 'tam-card';

      var arte = document.createElement('div');
      arte.className = 'tam-art';
      var img = document.createElement('img');
      img.alt = '';
      if (src.thumbUrl) img.src = src.thumbUrl;
      arte.appendChild(img);
      card.appendChild(arte);

      var cuerpo = document.createElement('div');
      cuerpo.className = 'tam-body';
      var top = document.createElement('div');
      top.className = 'tam-top';
      var nom = document.createElement('b');
      nom.textContent = src.name;
      nom.title = src.name;
      var del = document.createElement('button');
      del.type = 'button';
      del.className = 'tam-del';
      del.textContent = '×';
      del.title = 'Remove this design';
      del.setAttribute('aria-label', 'Remove ' + src.name);
      del.addEventListener('click', function () {
        cambiarTarjeta(c.k, function (x) { x.n = 0; });
        toast('Removed "' + src.name + '". Press Ctrl+Z to undo.');
      });
      top.appendChild(nom); top.appendChild(del);
      cuerpo.appendChild(top);

      // medida: siempre proporcional (estirar el arte casi nunca es lo que se quiere)
      var med = document.createElement('div');
      med.className = 'tam-size';
      function campo(lado, valor) {
        var lab = document.createElement('label');
        var l = document.createElement('span');
        l.textContent = lado === 'w' ? 'Width' : 'Height';
        var inp = document.createElement('input');
        inp.type = 'number';
        inp.step = '0.1';
        inp.min = '0.5';
        inp.inputMode = 'decimal';
        inp.value = txtIn(valor);
        inp.setAttribute('data-foco', idF + '|' + lado);
        inp.setAttribute('aria-label', (lado === 'w' ? 'Width' : 'Height') + ' of ' + src.name + ' in inches');
        inp.addEventListener('change', function () {
          var v = parseFloat(String(inp.value).replace(',', '.'));
          if (!(v > 0)) { inp.value = txtIn(valor); return; }
          if (lado === 'w') ponerMedida(c.k, v, v * c.h / c.w);
          else ponerMedida(c.k, v * c.w / c.h, v);
        });
        lab.appendChild(l); lab.appendChild(inp);
        return lab;
      }
      med.appendChild(campo('w', c.w));
      var x = document.createElement('span');
      x.className = 'tam-x';
      x.textContent = '×';
      med.appendChild(x);
      med.appendChild(campo('h', c.h));
      var u = document.createElement('span');
      u.className = 'tam-u';
      u.textContent = 'in';
      med.appendChild(u);
      cuerpo.appendChild(med);

      var fila = document.createElement('div');
      fila.className = 'tam-meta';
      var pre = document.createElement('select');
      pre.className = 'tam-preset';
      pre.setAttribute('data-foco', idF + '|p');
      pre.setAttribute('aria-label', 'Common print sizes for ' + src.name);
      var o0 = document.createElement('option');
      o0.value = '';
      o0.textContent = 'Common sizes';
      pre.appendChild(o0);
      PRINT_SIZES.forEach(function (sz, i) {
        var o = document.createElement('option');
        o.value = String(i);
        o.textContent = sz.label + ' · ' + sz.w + '" wide';
        if (Math.abs(sz.w - c.w) < 0.005) o0.textContent = sz.label;
        pre.appendChild(o);
      });
      pre.addEventListener('change', function () {
        var sz = PRINT_SIZES[+pre.value];
        if (sz) ponerMedida(c.k, sz.w, sz.w * c.h / c.w);
      });
      fila.appendChild(pre);
      var dpi = src.isVector ? Infinity : Math.min(src.artW / c.w, src.artH / c.h);
      var q = document.createElement('span');
      q.className = 'tam-dpi ' + (dpi >= 300 ? 'ok' : (dpi >= 150 ? 'mid' : 'low'));
      q.textContent = dpi === Infinity ? 'Vector' : Math.round(dpi) + ' DPI' + (dpi >= 300 ? ' · sharp' : (dpi >= 150 ? ' · OK' : ' · may blur'));
      q.title = dpi === Infinity ? 'Vector artwork prints sharp at any size.'
        : dpi >= 300 ? 'Plenty of resolution for this size.'
        : dpi >= 150 ? 'Prints fine for most designs. A bigger file would be sharper.'
        : 'Low resolution for this size: it may print blurry. Upload a bigger file or make it smaller.';
      fila.appendChild(q);
      cuerpo.appendChild(fila);
      card.appendChild(cuerpo);

      var pie = document.createElement('div');
      pie.className = 'tam-foot';
      var qty = document.createElement('div');
      qty.className = 'tam-qty';
      qty.setAttribute('role', 'group');
      qty.setAttribute('aria-label', 'Pieces of ' + src.name);
      var bm = document.createElement('button');
      bm.type = 'button';
      bm.textContent = '−';
      bm.setAttribute('aria-label', 'One piece less');
      bm.disabled = c.n <= 1;
      bm.setAttribute('data-foco', idF + '|m');
      var ni = document.createElement('input');
      ni.type = 'number';
      ni.min = '1';
      ni.step = '1';
      ni.inputMode = 'numeric';
      ni.className = 'tam-n';
      ni.value = String(c.n);
      ni.setAttribute('data-foco', idF + '|n');
      ni.setAttribute('aria-label', 'Pieces of ' + src.name);
      var bp = document.createElement('button');
      bp.type = 'button';
      bp.textContent = '+';
      bp.setAttribute('aria-label', 'One piece more');
      bp.setAttribute('data-foco', idF + '|p1');
      function ponerN(n) {
        n = Math.max(1, Math.min(9999, Math.round(n) || 1));
        cambiarTarjeta(c.k, function (x2) { x2.n = n; });
      }
      bm.addEventListener('click', function () { ponerN(c.n - 1); });
      bp.addEventListener('click', function () { ponerN(c.n + 1); });
      ni.addEventListener('change', function () { ponerN(parseInt(ni.value, 10)); });
      qty.appendChild(bm); qty.appendChild(ni); qty.appendChild(bp);
      var ql = document.createElement('span');
      ql.className = 'tam-qty-l';
      ql.textContent = 'Pieces';
      var izq = document.createElement('div');
      izq.className = 'tam-qty-wrap';
      izq.appendChild(ql); izq.appendChild(qty);
      pie.appendChild(izq);

      var din = document.createElement('div');
      din.className = 'tam-money';
      var cada = document.createElement('span');
      cada.className = 'tam-each';
      var unaBase = c.w * c.h * base;
      if (g.desc > 0) {
        var tach = document.createElement('s');
        tach.textContent = money(dos(unaBase));
        cada.appendChild(tach);
        cada.appendChild(document.createTextNode(' '));
      }
      cada.appendChild(document.createTextNode(money(dos(g.precio / c.n)) + ' each'));
      var sub2 = document.createElement('strong');
      sub2.textContent = money(g.precio);
      din.appendChild(cada); din.appendChild(sub2);
      if (g.desc > 0) {
        var dsc = document.createElement('em');
        dsc.className = 'tam-off';
        dsc.textContent = '−' + g.desc + '%';
        din.appendChild(dsc);
      }
      pie.appendChild(din);
      card.appendChild(pie);

      var acciones = document.createElement('div');
      acciones.className = 'tam-actions';
      var sig = null;
      t.tramosTam.forEach(function (tr) { if (!sig && tr.min > c.n) sig = tr; });
      if (sig) {
        var emp = document.createElement('button');
        emp.type = 'button';
        emp.className = 'tam-nudge';
        emp.textContent = 'Make it ' + sig.min + ' and pay ' + descTramo(t, sig) + '% less per piece';
        emp.title = 'From ' + sig.min + ' pieces of this design at this size, each piece costs ' + descTramo(t, sig) + '% less';
        emp.addEventListener('click', function () { ponerN(sig.min); });
        acciones.appendChild(emp);
      } else {
        var top2 = document.createElement('span');
        top2.className = 'tam-best';
        top2.textContent = 'Best price reached';
        acciones.appendChild(top2);
      }
      var otro = document.createElement('button');
      otro.type = 'button';
      otro.className = 'tam-other';
      otro.textContent = '+ Another size';
      otro.title = 'Add this same artwork at a different size';
      otro.addEventListener('click', function () {
        var w2 = c.w >= 6 ? 3.5 : 11;
        var m = medidaValida(w2, w2 * c.h / c.w);
        cambiarTarjeta(c.k, function (x3, todas, i) {
          var vMax = 0;
          todas.forEach(function (y) { if (y.s === c.s) vMax = Math.max(vMax, y.v); });
          todas.splice(i + 1, 0, { s: c.s, v: vMax + 1, w: m.w, h: m.h, n: 1 });
        });
        toast('Added "' + src.name + '" at ' + txtIn(m.w) + ' × ' + txtIn(m.h) + ' in. Change the size if you need another.');
      });
      acciones.appendChild(otro);
      card.appendChild(acciones);
      grid.appendChild(card);
    });
    board.appendChild(grid);
    if (foco) {
      var f = board.querySelector('[data-foco="' + foco.replace(/"/g, '\\"') + '"]');
      if (f) f.focus();
    }
  }

  // Columna de hojas (a la derecha): una tarjeta por hoja con su miniatura, largo, disenos
  // y precio; "+ Add sheet" y el total del pedido. En by size, el precio de cada diseno.
  function pintarHojas() {
    if (!el.sheetTabs) return;
    var tam = aplicarModo();
    var maxTxt = RAIZ.getElementById('sheet-panel-max');
    if (maxTxt) maxTxt.textContent = tam ? 'by size' : 'up to ' + maxAltura() + '" each';
    var lista = todasLasHojas();
    el.sheetTabs.innerHTML = '';
    if (tam) {   // by size: el pedido a la derecha y las tarjetas de disenos en el centro; sin hojas
      pintarPreciosTamano(currentType(), lista);
      pintarTablero(currentType(), lista);
      return;
    }
    var total = 0, todasConPrecio = true, conDisenos = 0;
    lista.forEach(function (h, i) {
      var card = document.createElement('div');
      card.className = 'sheet-card' + (i === hojaActual ? ' on' : '');
      card.setAttribute('role', 'tab');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-selected', i === hojaActual ? 'true' : 'false');
      var cop = Math.max(1, h.copias || 1);
      var pr = h.items.length ? precioDeHoja(h) : null;
      if (h.items.length) { conDisenos++; if (pr == null) todasConPrecio = false; else total += pr * cop; }
      var cv = document.createElement('canvas');
      cv.className = 'sheet-thumb';
      pintarMiniatura(cv, h);
      card.appendChild(cv);
      var info = document.createElement('span');
      info.className = 'sheet-info';
      var nom = document.createElement('b');
      nom.textContent = 'Sheet ' + (i + 1);
      var med = document.createElement('span');
      med.textContent = SHEET_W_IN + '" × ' + h.sheetH + '"';
      var det = document.createElement('span');
      det.className = 'sheet-price';
      det.textContent = h.items.length
        ? h.items.length + ' design' + (h.items.length === 1 ? '' : 's') + (pr != null ? ' · ' + money(Math.round(pr * cop * 100) / 100) : '')
        : 'Empty';
      info.appendChild(nom); info.appendChild(med); info.appendChild(det);
      // Copias de esta hoja, tambien desde aqui (las mismas que en el resumen).
      if (h.items.length) {
        var qty = document.createElement('div');
        qty.className = 'sheet-qty';
        qty.setAttribute('role', 'group');
        qty.setAttribute('aria-label', 'Copies of sheet ' + (i + 1));
        var bMenos = document.createElement('button');
        bMenos.type = 'button';
        bMenos.textContent = '−';
        bMenos.setAttribute('aria-label', 'One copy less');
        bMenos.disabled = cop <= 1;
        var vQty = document.createElement('span');
        vQty.textContent = '× ' + cop;
        var bMas = document.createElement('button');
        bMas.type = 'button';
        bMas.textContent = '+';
        bMas.setAttribute('aria-label', 'One copy more');
        [[bMenos, -1], [bMas, 1]].forEach(function (d) {
          d[0].addEventListener('click', function (e) {
            e.stopPropagation();   // tocar las copias no cambia de hoja
            ponerCopias(i, cop + d[1]);
            pintarHojas();
            pushHistory();
          });
          d[0].addEventListener('keydown', function (e) { e.stopPropagation(); });
        });
        qty.appendChild(bMenos); qty.appendChild(vQty); qty.appendChild(bMas);
        qty.title = 'Copies of this sheet';
        info.appendChild(qty);
      }
      card.appendChild(info);
      card.addEventListener('click', function () { irAHoja(i); });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); irAHoja(i); }
      });
      if (lista.length > 1) {
        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'sheet-x';
        x.title = 'Remove sheet ' + (i + 1);
        x.setAttribute('aria-label', 'Remove sheet ' + (i + 1));
        x.textContent = '×';
        x.addEventListener('click', function (e) { e.stopPropagation(); quitarHoja(i); });
        card.appendChild(x);
      }
      el.sheetTabs.appendChild(card);
    });
    var mas = document.createElement('button');
    mas.type = 'button';
    mas.className = 'sheet-add';
    mas.textContent = '+ Add sheet';
    mas.title = 'Start another sheet in this same order (up to ' + maxAltura() + '" each)';
    mas.addEventListener('click', nuevaHoja);
    el.sheetTabs.appendChild(mas);
    var copiasTotal = 0;
    lista.forEach(function (h) { if (h.items.length) copiasTotal += Math.max(1, h.copias || 1); });
    var tM = currentType();
    if (tM && tM.mayoreo && conDisenos) {
      var totalIn = 0;
      lista.forEach(function (h) { if (h.items.length) totalIn += h.sheetH * Math.max(1, h.copias || 1); });
      var oM = ofertaMayoreo(tM, Math.max(totalIn, tM.minTotal));
      var tm = document.createElement('div');
      tm.className = 'sheet-total';
      var l1 = document.createElement('span');
      l1.textContent = (totalIn / 12).toFixed(1).replace(/\.0$/, '') + ' ft' +
        (totalIn < tM.minTotal ? ' of ' + (tM.minTotal / 12) + ' ft min.' : '');
      var v1 = document.createElement('b');
      v1.textContent = totalIn >= tM.minTotal && oM ? money(oM.price) : '';
      tm.appendChild(l1); tm.appendChild(v1);
      el.sheetTabs.appendChild(tm);
    } else if ((conDisenos > 1 || copiasTotal > 1) && todasConPrecio) {
      var tot = document.createElement('div');
      tot.className = 'sheet-total';
      tot.innerHTML = '';
      var l = document.createElement('span');
      l.textContent = copiasTotal + ' sheets';
      var v = document.createElement('b');
      v.textContent = money(total);
      tot.appendChild(l); tot.appendChild(v);
      el.sheetTabs.appendChild(tot);
    }
  }


  // ------------------------------------------------------------- clipboard
  // The system clipboard decides what Ctrl+V means: a pasted image file wins
  // over the internal clipboard, so a stale screenshot would shadow copied
  // designs forever. Writing this marker on copy/cut replaces that stale image
  // in the system clipboard, making "most recent copy wins" hold across both
  // clipboards. Best-effort: if the browser refuses the write, behavior falls
  // back to preferring system images (the old behavior).
  var CLIP_MARKER = 'application/x-gang-sheet-builder-clip';
  function markSystemClipboard() {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(CLIP_MARKER).catch(function () {});
      }
    } catch (err) {}
  }

  function copySelection() {
    if (!selection.length) return 0;
    clipboard = { items: selection.map(snapOfNode) };
    markSystemClipboard();
    return clipboard.items.length;
  }

  function cutSelection() {
    var n = copySelection();
    if (!n) return;
    deleteNodes(selection.slice());
    toast('Cut ' + n + ' design' + (n === 1 ? '' : 's') + '. Press Ctrl+V to paste.');
  }

  function bboxOfSnap(it) {
    var rad = (it.r || 0) * Math.PI / 180;
    var cos = Math.cos(rad), sin = Math.sin(rad);
    var pts = [[0, 0], [it.w, 0], [it.w, it.h], [0, it.h]];
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    pts.forEach(function (p) {
      var x = it.x + p[0] * cos - p[1] * sin;
      var y = it.y + p[0] * sin + p[1] * cos;
      minX = Math.min(minX, x); minY = Math.min(minY, y);
      maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
    });
    return { minX: minX, minY: minY, maxX: maxX, maxY: maxY };
  }

  // Paste keeps the copied layout (relative positions, rotations, grouping)
  // and drops the whole block into the first free spot.
  function pasteClipboard() {
    if (!clipboard || !clipboard.items.length) return;
    var u = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    clipboard.items.forEach(function (it) {
      var b = bboxOfSnap(it);
      u.minX = Math.min(u.minX, b.minX); u.minY = Math.min(u.minY, b.minY);
      u.maxX = Math.max(u.maxX, b.maxX); u.maxY = Math.max(u.maxY, b.maxY);
    });
    var spot = findFreeSpot(u.maxX - u.minX, u.maxY - u.minY, { rotate: 'no' });
    var dx = spot.x - u.minX, dy = spot.y - u.minY;
    var gidMap = {};
    var made = [];
    clipboard.items.forEach(function (it) {
      var src = sources.get(it.s);
      if (!src) return;
      var g = null;
      if (it.g) {
        if (!gidMap[it.g]) gidMap[it.g] = 'g-' + (++seq);
        g = gidMap[it.g];
      }
      made.push(makeNode(src, {
        x: it.x + dx, y: it.y + dy,
        width: it.w, height: it.h,
        rotation: it.r || 0,
        cardVariant: it.v || 0,
        groupId: g
      }));
    });
    if (!made.length) {
      toast('Nothing to paste. The copied designs are no longer available.');
      return;
    }
    setSelection(made);
    renderList();
    refreshAll();
    pushHistory();
    if (!spot.fits) toast('No space left. Pasted below the sheet.');
  }

  function updateToolbarState() {
    var selN = selection.length;
    var units = selN ? unitsOfSelection().length : 0;
    el.rotateBtn.disabled = !selN;
    el.dupBtn.disabled = !selN;
    el.deleteBtn.disabled = !selN;
    el.fillBtn.disabled = !(selN && primaryUnitForCopies());
    // the Group button carries two inline SVGs; the .ungroup class decides
    // which one shows (never touch textContent, it would wipe them)
    var grouped = selN > 0 && selection.some(function (n) { return n.getAttr('groupId'); });
    var asUngroup = units < 2 && grouped;
    el.groupBtn.disabled = !(units >= 2 || grouped);
    el.groupBtn.classList.toggle('ungroup', asUngroup);
    el.groupBtn.title = asUngroup
      ? 'Split the selected group back into single designs (Ctrl+Shift+G)'
      : 'Group the selected designs so they move together (Ctrl+G)';
    el.alignLeftBtn.disabled = units < 2;
    el.alignTopBtn.disabled = units < 2;
    el.centerBtn.disabled = !selN;
    if (el.distHBtn) el.distHBtn.disabled = units < 3;
    if (el.distVBtn) el.distVBtn.disabled = units < 3;
    // La barra de la seleccion no se apaga: aparece y desaparece. Sus botones solo
    // sirven con algo seleccionado, asi que fuera de eso no tiene por que ocupar sitio.
    if (el.selectionBar) el.selectionBar.hidden = !selN;

    var any = itemLayer.getChildren().length > 0;
    el.arrangeBtn.disabled = !any;
    el.clearBtn.disabled = !any;
    el.exportBtn.disabled = !(any || disenosEnOtrasHojas()) || exporting;
    el.undoBtn.disabled = undoStack.length < 2;
    el.redoBtn.disabled = !redoStack.length;
  }

  // -------------------------------------------------- canvas interactions

  var dragCtx = null;        // multi-selection drag baseline
  var clickCandidate = null; // clicked an already-selected node: maybe collapse on mouseup

  itemLayer.on('mousedown touchstart', function (e) {
    var n = e.target;
    if (!n || n.getParent() !== itemLayer) return;
    clickCandidate = null;
    if (isLocked(n)) {
      setSelection([]);
      el.statusHint.textContent = 'This design is locked. Unlock it from its card in the list.';
      return;
    }
    if (e.evt && e.evt.shiftKey) {
      toggleUnitSelection(n);
    } else if (selection.indexOf(n) === -1) {
      setSelection([n]);
    } else if (selection.length > 1) {
      // clicking inside a multi-selection: keep it (so the drag moves all),
      // but collapse to just this unit if the mouse never moves
      clickCandidate = n;
    }
  });

  itemLayer.on('mouseup touchend', function (e) {
    if (clickCandidate && e.target === clickCandidate) {
      setSelection([clickCandidate]);
    }
    clickCandidate = null;
  });

  itemLayer.on('dragstart', function (e) {
    clickCandidate = null;
    var n = e.target;
    // Alt+drag: leave a copy behind at the original spot
    if (e.evt && e.evt.altKey && !isLocked(n)) {
      unitsOfSelection().forEach(function (unit) { duplicateUnit(unit); });
      renderList();
    }
    if (selection.indexOf(n) !== -1 && selection.length > 1) {
      dragCtx = {
        node: n,
        sx: n.x(), sy: n.y(),
        starts: selection.map(function (s) { return { n: s, x: s.x(), y: s.y() }; })
      };
    } else {
      dragCtx = null;
    }
  });

  itemLayer.on('dragmove', function (e) {
    var n = e.target;
    if (el.snapCheck.checked) {
      n.position({
        x: Math.round(n.x() / SNAP_STEP_IN) * SNAP_STEP_IN,
        y: Math.round(n.y() / SNAP_STEP_IN) * SNAP_STEP_IN
      });
    }
    if (dragCtx && dragCtx.node === n) {
      var dx = n.x() - dragCtx.sx, dy = n.y() - dragCtx.sy;
      dragCtx.starts.forEach(function (s) {
        if (s.n !== n) s.n.position({ x: s.x + dx, y: s.y + dy });
      });
      clearGuides();
    } else if (selection.length === 1 && selection[0] === n) {
      updateGuides(n);
    }
  });

  // drag events do bubble to the layer (unlike transform events, see above)
  itemLayer.on('dragend', function () {
    clearGuides();
    dragCtx = null;
    refreshAll();
    pushHistorySoon();
    revisarSobranSoon();
  });

  // ------------------------------------------------------- smart guides
  var guideShapes = [];

  function clearGuides() {
    if (!guideShapes.length) return;
    guideShapes.forEach(function (s) { s.destroy(); });
    guideShapes = [];
  }

  // While dragging a single design, snap its edges/center to the edges and
  // centers of other designs and of the sheet, and show a magenta guide line
  // at the matched coordinate. Runs after the grid snap, so the guide wins.
  function updateGuides(node) {
    clearGuides();
    var thr = 6 / zoom; // 6 screen px
    var b = nodeBBox(node);
    var vT = [0, SHEET_W_IN, SHEET_W_IN / 2, MARGIN_IN, SHEET_W_IN - MARGIN_IN];
    var hT = [0, sheetHIn, sheetHIn / 2, MARGIN_IN, sheetHIn - MARGIN_IN];
    itemNodes().forEach(function (o) {
      if (o === node) return;
      var ob = nodeBBox(o);
      vT.push(ob.minX, ob.maxX, (ob.minX + ob.maxX) / 2);
      hT.push(ob.minY, ob.maxY, (ob.minY + ob.maxY) / 2);
    });
    var vE = [b.minX, (b.minX + b.maxX) / 2, b.maxX];
    var hE = [b.minY, (b.minY + b.maxY) / 2, b.maxY];
    var bestV = null, bestH = null;
    vT.forEach(function (t) {
      vE.forEach(function (edge) {
        var d = t - edge;
        if (Math.abs(d) < thr && (!bestV || Math.abs(d) < Math.abs(bestV.d))) bestV = { d: d, t: t };
      });
    });
    hT.forEach(function (t) {
      hE.forEach(function (edge) {
        var d = t - edge;
        if (Math.abs(d) < thr && (!bestH || Math.abs(d) < Math.abs(bestH.d))) bestH = { d: d, t: t };
      });
    });
    if (bestV) node.x(node.x() + bestV.d);
    if (bestH) node.y(node.y() + bestH.d);
    if (bestV) guideShapes.push(addGuideLine([bestV.t, 0, bestV.t, sheetHIn]));
    if (bestH) guideShapes.push(addGuideLine([0, bestH.t, SHEET_W_IN, bestH.t]));
  }

  function addGuideLine(points) {
    var l = new Konva.Line({
      points: points, stroke: GUIDE,
      strokeWidth: 1 / zoom, dash: [4 / zoom, 4 / zoom],
      listening: false
    });
    uiLayer.add(l);
    return l;
  }

  // -------------------------------------------------- marquee (box select)
  var marquee = null;

  function marqueeWorldPoint(ev) {
    var hrect = el.holder.getBoundingClientRect();
    return {
      x: (ev.clientX - hrect.left) / zoom - padIn,
      y: (ev.clientY - hrect.top) / zoom - padIn
    };
  }

  function cancelMarquee() {
    window.removeEventListener('mousemove', marqueeMove);
    window.removeEventListener('mouseup', marqueeUp);
    if (marquee && marquee.rect) marquee.rect.destroy();
    marquee = null;
  }

  function marqueeMove(ev) {
    if (!marquee) return;
    var p = marqueeWorldPoint(ev);
    var x = Math.min(marquee.x0, p.x), y = Math.min(marquee.y0, p.y);
    var w = Math.abs(p.x - marquee.x0), h = Math.abs(p.y - marquee.y0);
    if (w > 3 / zoom || h > 3 / zoom) marquee.moved = true;
    if (!marquee.rect) {
      marquee.rect = new Konva.Rect({
        stroke: ACCENT, strokeWidth: 1 / zoom, dash: [4 / zoom, 4 / zoom],
        fill: hexToRgba(ACCENT, 0.08), listening: false
      });
      uiLayer.add(marquee.rect);
    }
    marquee.rect.setAttrs({ x: x, y: y, width: w, height: h });
  }

  function marqueeUp() {
    var m = marquee;
    cancelMarquee();
    if (!m || !m.rect || !m.moved) return;
    var r = {
      minX: m.rect.x(), minY: m.rect.y(),
      maxX: m.rect.x() + m.rect.width(), maxY: m.rect.y() + m.rect.height()
    };
    var hit = itemNodes().filter(function (n) {
      if (isLocked(n)) return false;
      var b = nodeBBox(n);
      return b.minX < r.maxX && b.maxX > r.minX && b.minY < r.maxY && b.maxY > r.minY;
    });
    setSelection(m.add ? selection.concat(hit) : hit);
  }

  stage.on('mousedown', function (e) {
    if (e.target !== stage) return;
    if (e.evt.button !== 0) return;
    var p = stage.getRelativePointerPosition();
    marquee = { x0: p.x, y0: p.y, add: !!e.evt.shiftKey, rect: null, moved: false };
    if (!e.evt.shiftKey) setSelection([]);
    window.addEventListener('mousemove', marqueeMove);
    window.addEventListener('mouseup', marqueeUp);
  });

  stage.on('touchstart', function (e) {
    if (e.target === stage) setSelection([]);
  });

  // ---------------------------------------------------------- refresh/UI
  function refreshAll() {
    ajustarLargo();   // en automatico la hoja sigue al contenido
    computeOverlaps();
    itemNodes().forEach(function (n) {
      var out = isOutOfSheet(n);
      var over = overlapSet.has(n);
      n.strokeEnabled(out || over);
      if (out) n.stroke(DANGER);
      else if (over) n.stroke(WARN);
    });
    updateListValues();
    updateStatusbar();
    updateToolbarState();
    pintarHojas();
  }

  function smallestFittingHeight() {
    var nodes = itemNodes();
    if (!nodes.length) return null;
    var maxY = 0, maxX = 0;
    nodes.forEach(function (n) {
      var b = nodeBBox(n);
      maxY = Math.max(maxY, b.maxY);
      maxX = Math.max(maxX, b.maxX);
    });
    if (maxX > SHEET_W_IN + 0.01) return null; // sticking out sideways: no height helps
    var needed = maxY + MARGIN_IN;
    for (var i = 0; i < SHEET_HEIGHTS.length; i++) {
      if (SHEET_HEIGHTS[i] >= needed - 1e-9) return SHEET_HEIGHTS[i];
    }
    return null;
  }

  function updateStatusbar() {
    var nodes = itemNodes();
    var area = 0;
    var anyOut = false;
    nodes.forEach(function (n) {
      area += (n.width() * n.scaleX()) * (n.height() * n.scaleY());
      if (isOutOfSheet(n)) anyOut = true;
    });
    var pct = Math.min(999, Math.round(100 * area / (SHEET_W_IN * sheetHIn)));
    var porTam = !!(currentType() && currentType().porTamano);
    var txt = porTam
      ? 'Priced per design, by size  •  ' + nodes.length + ' print' + (nodes.length === 1 ? '' : 's') +
        (hojas.length > 1 ? ' on this layout sheet' : '')
      : 'Sheet ' + SHEET_W_IN + '" × ' + sheetHIn + '"' + (largoAuto ? ' (auto length)' : '') + '  •  ' +
        nodes.length + ' design' + (nodes.length === 1 ? '' : 's') + '  •  ' +
        pct + '% of the sheet used';
    if (fondoEsColor()) {
      txt += '  •  PREVIEW: the background color is not printed';
    }
    if (anyOut) txt += '  •  ⚠ some designs are off the sheet';
    if (overlapSet.size) txt += '  •  ⚠ some designs overlap';
    if (!anyOut && nodes.length && !porTam) {
      var fit = smallestFittingHeight();
      if (fit && fit < sheetHIn) txt += '  •  everything fits on ' + SHEET_W_IN + '" × ' + fit + '"';
    }
    el.statusText.textContent = txt;
    el.statusbar.classList.toggle('has-warning', anyOut || overlapSet.size > 0);
    updateHint();
  }

  // Short "what can I do now" reminder on the right side of the status bar.
  function updateHint() {
    var txt = '';
    if (selection.length > 1) {
      var grouped = selection.every(function (n) { return n.getAttr('groupId'); });
      txt = selection.length + ' designs selected · drag moves them together · ' +
        (grouped ? 'Ctrl+Shift+G ungroups' : 'Ctrl+G groups them');
    } else if (selection.length === 1) {
      txt = 'Drag to move · corner handles resize · blue dot above rotates (R) · Alt+drag duplicates';
    } else if (itemLayer.getChildren().length) {
      txt = 'Click a design to select it · drag on empty space to select several';
    }
    el.statusHint.textContent = txt;
  }

  // ------------------------------------------------------------- sidebar

  // The list shows one card per design at a given print size, with a copy
  // counter, instead of one card per placed image. Resizing a single copy on
  // the canvas gives it its own card automatically (its group key changes),
  // and the card's ⧉ button forks a new card on purpose via cardVariant, so
  // the same upload can live on several cards with independent sizes.
  // Cards keep a FIXED order (creation order of their oldest copy), so adding
  // or selecting copies never shuffles the list.
  function groupKeyOf(node) {
    return node.getAttr('sourceId') + '|' +
      (node.getAttr('cardVariant') || 0) + '|' +
      (node.width() * node.scaleX()).toFixed(2) + '|' +
      (node.height() * node.scaleY()).toFixed(2);
  }

  // Next unused variant for a source, so a forked card never merges back into
  // an existing one even if both end up at the same size.
  function nextCardVariant(sourceId) {
    var v = 0;
    itemNodes().forEach(function (n) {
      if (n.getAttr('sourceId') === sourceId) {
        v = Math.max(v, (n.getAttr('cardVariant') || 0) + 1);
      }
    });
    return v;
  }

  function computeGroups() {
    var order = [];
    var byKey = {};
    itemNodes().forEach(function (n) {
      var key = groupKeyOf(n);
      var g = byKey[key];
      if (!g) {
        g = { key: key, src: sources.get(n.getAttr('sourceId')), nodes: [] };
        byKey[key] = g;
        order.push(g);
      }
      g.nodes.push(n);
    });
    order.forEach(function (g) {
      g.nodes.sort(function (a, b) { return nodeSeqNum(a) - nodeSeqNum(b); });
      g.minSeq = g.nodes.length ? nodeSeqNum(g.nodes[0]) : 0;
    });
    order.sort(function (a, b) { return a.minSeq - b.minSeq; });
    return order.filter(function (g) { return !!g.src; });
  }

  function groupsSignature(groups) {
    return groups.map(function (g) { return g.key + ':' + g.nodes.length; }).join(';');
  }

  var renderedSignature = null;

  function renderList() {
    var groups = computeGroups();
    renderedSignature = groupsSignature(groups);
    el.itemList.innerHTML = '';
    el.emptyState.style.display = groups.length ? 'none' : '';
    groups.forEach(function (g) {
      var src = g.src;
      var row = document.createElement('div');
      row.className = 'item-row';
      row.dataset.groupKey = g.key;

      var thumb = document.createElement('img');
      thumb.className = 'item-thumb';
      thumb.src = src.thumbUrl;
      thumb.alt = '';
      thumb.draggable = false; // keep native image drags from triggering the drop overlay

      var meta = document.createElement('div');
      meta.className = 'item-meta';

      var name = document.createElement('div');
      name.className = 'item-name';
      name.textContent = src.name;
      name.title = src.name;

      var dims = document.createElement('div');
      dims.className = 'item-dims';
      var wInput = document.createElement('input');
      wInput.type = 'number'; wInput.step = '0.05'; wInput.min = '0.25';
      wInput.className = 'dim-w';
      var hInput = document.createElement('input');
      hInput.type = 'number'; hInput.step = '0.05'; hInput.min = '0.25';
      hInput.className = 'dim-h';
      var sep = document.createElement('span');
      sep.textContent = '×';
      var unit = document.createElement('span');
      unit.className = 'dim-unit';
      unit.textContent = 'in';
      dims.appendChild(wInput); dims.appendChild(sep); dims.appendChild(hInput); dims.appendChild(unit);

      var copiesRow = document.createElement('div');
      copiesRow.className = 'copies-row';
      var copiesLabel = document.createElement('span');
      copiesLabel.className = 'copies-label';
      copiesLabel.textContent = 'Copies';
      var minusB = document.createElement('button');
      minusB.className = 'icon-btn minus-btn'; minusB.title = 'Remove one copy'; minusB.textContent = '−';
      var countInput = document.createElement('input');
      countInput.type = 'number'; countInput.min = '1'; countInput.max = '999'; countInput.step = '1';
      countInput.className = 'count-input';
      countInput.title = 'How many copies of this design you want in total, on all your sheets. Type a number and the sheets update.';
      var plusB = document.createElement('button');
      plusB.className = 'icon-btn plus-btn'; plusB.title = 'Add one copy'; plusB.textContent = '+';
      copiesRow.appendChild(copiesLabel);
      copiesRow.appendChild(minusB);
      copiesRow.appendChild(countInput);
      copiesRow.appendChild(plusB);

      var badges = document.createElement('div');
      badges.className = 'item-badges';

      var where = document.createElement('div');
      where.className = 'copies-where';
      meta.appendChild(name); meta.appendChild(dims); meta.appendChild(copiesRow); meta.appendChild(where); meta.appendChild(badges);

      var actions = document.createElement('div');
      actions.className = 'item-actions';
      var forkB = document.createElement('button');
      forkB.className = 'icon-btn'; forkB.title = 'Duplicate to a new card, so you can give it a different size without uploading again'; forkB.textContent = '⧉';
      var lockB = document.createElement('button');
      lockB.className = 'icon-btn lock-btn'; lockB.textContent = '🔒';
      var delB = document.createElement('button');
      delB.className = 'icon-btn danger'; delB.title = 'Remove this design'; delB.textContent = '✕';
      actions.appendChild(forkB); actions.appendChild(lockB);
      actions.appendChild(delB);

      row.appendChild(thumb); row.appendChild(meta); row.appendChild(actions);
      el.itemList.appendChild(row);

      row.addEventListener('mousedown', function (ev) {
        if (ev.target.tagName === 'INPUT' || ev.target.tagName === 'BUTTON') return;
        if (isLocked(g.nodes[0])) return;
        setSelection(g.nodes);
      });
      plusB.addEventListener('click', function (ev) {
        ev.stopPropagation();
        addCopies([g.nodes[0]], 1, true);
      });
      minusB.addEventListener('click', function (ev) {
        ev.stopPropagation();
        if (g.nodes.length + copiasEnOtrasHojas(g.key) > 1) quitarCopias(g, 1);
      });
      countInput.addEventListener('change', function () {
        var v = parseInt(countInput.value, 10);
        if (!isFinite(v)) { updateListValues(); return; }
        setCopyCount(g, v);
      });
      forkB.addEventListener('click', function (ev) {
        ev.stopPropagation();
        var copy = duplicateNode(g.nodes[0]);
        if (!copy) return;
        copy.setAttr('groupId', null);
        copy.setAttr('cardVariant', nextCardVariant(src.id));
        placeUnitFree([copy], 'best');
        setSelection([copy]);
        renderList();
        refreshAll();
        pushHistory();
      });
      lockB.addEventListener('click', function (ev) {
        ev.stopPropagation();
        var lock = !isLocked(g.nodes[0]);
        g.nodes.forEach(function (n) {
          n.setAttr('locked', lock);
          n.draggable(!lock);
        });
        if (lock) {
          setSelection(selection.filter(function (n) { return g.nodes.indexOf(n) === -1; }));
        }
        updateListValues();
        pushHistory();
      });
      delB.addEventListener('click', function (ev) {
        ev.stopPropagation();
        var otras = copiasEnOtrasHojas(g.key), total = g.nodes.length + otras;
        if (total > 1 &&
            !window.confirm('Remove all ' + total + ' copies of "' + src.name + '"' + (otras ? ' from every sheet' : '') + '?')) return;
        if (otras) quitarDeOtrasHojas(g.key, otras);
        deleteNodes(g.nodes.slice());
      });
      // enforce the minimum size scaling both axes together, so a wide banner
      // never gets its short side stretched out of proportion; the new size
      // applies to every copy on the card
      function setSizeIn(w, h) {
        var k = Math.max(1, MIN_ITEM_IN / w, MIN_ITEM_IN / h);
        cambiarTamanoTarjeta(g, w * k, h * k);
      }
      // the card inputs scale with the node's CURRENT ratio, so a design the
      // customer stretched on purpose stays stretched when resized here
      function currentRatio() {
        var n0 = g.nodes[0];
        return (n0.height() * n0.scaleY()) / (n0.width() * n0.scaleX());
      }
      wInput.addEventListener('change', function () {
        var v = parseFloat(wInput.value);
        if (!isFinite(v) || v <= 0) { updateListValues(); return; }
        v = clamp(v, 0.01, 200);
        setSizeIn(v, v * currentRatio());
      });
      hInput.addEventListener('change', function () {
        var v = parseFloat(hInput.value);
        if (!isFinite(v) || v <= 0) { updateListValues(); return; }
        v = clamp(v, 0.01, 200);
        setSizeIn(v / currentRatio(), v);
      });
    });
    fillListValues(groups);
    highlightRow();
  }

  // COPIAS EN TOTAL (27-oct-2026, pedido del usuario): la tarjeta cuenta las copias de TODAS las
  // hojas, no solo las de la que se ve. Escribir 60 deja 60 en total; las que no caben aqui van a
  // las otras hojas (primero a los huecos de las que ya hay) y las que sobran se quitan de las
  // ultimas hojas primero.
  function copiasEnOtrasHojas(key) {
    var n = 0;
    hojas.forEach(function (h, i) {
      if (i === hojaActual || !h) return;
      h.items.forEach(function (it) { if (claveDeSnap(it) === key) n++; });
    });
    return n;
  }
  // quita n copias de esta tarjeta de las otras hojas, de la ultima hacia atras y las mas nuevas
  // primero. Devuelve cuantas quito.
  function quitarDeOtrasHojas(key, n) {
    var quitadas = 0;
    for (var i = hojas.length - 1; i >= 0 && quitadas < n; i--) {
      var h = hojas[i];
      if (i === hojaActual || !h) continue;
      var suyas = [];
      h.items.forEach(function (it, k) { if (claveDeSnap(it) === key) suyas.push(k); });
      suyas.sort(function (a, b) { return (h.items[b].q || 0) - (h.items[a].q || 0); });
      var fuera = suyas.slice(0, n - quitadas);
      if (!fuera.length) continue;
      quitadas += fuera.length;
      h.items = h.items.filter(function (it, k) { return fuera.indexOf(k) === -1; });
      if (h.auto !== false) h.sheetH = largoDeItems(h.items);
    }
    quitarHojasVacias();
    return quitadas;
  }
  function quitarCopias(g, n) {
    var deOtras = quitarDeOtrasHojas(g.key, n), aqui = n - deOtras;
    if (aqui > 0) {
      // las mas nuevas primero
      var byNewest = g.nodes.slice().sort(function (a, b) { return nodeSeqNum(b) - nodeSeqNum(a); });
      deleteNodes(byNewest.slice(0, Math.min(aqui, g.nodes.length)));
      return;
    }
    renderList();
    refreshAll();
    pushHistory();
  }
  function setCopyCount(g, target) {
    target = clamp(Math.round(target) || 1, 1, 999);
    var total = g.nodes.length + copiasEnOtrasHojas(g.key);
    if (target > total) addCopies([g.nodes[0]], target - total, true);
    else if (target < total) quitarCopias(g, total - target);
  }

  // TAMANO DESDE LA TARJETA (27-oct-2026, pedido del usuario): el tamano nuevo vale para todas las
  // copias de la tarjeta en todas las hojas y, en largo automatico, esas copias se vuelven a colocar
  // donde quepan (primero esta hoja, luego los huecos de las otras, luego una hoja nueva), asi que
  // nada se encima ni se sale de la hoja. Los demas disenos no se mueven. Las copias bloqueadas o
  // agrupadas cambian de tamano en su sitio, como antes.
  function cambiarTamanoTarjeta(g, W, Hh) {
    var key = g.key;
    var mias = {};
    g.nodes.forEach(function (n) { mias[nodeSeqNum(n)] = true; });
    if (!largoAuto) {
      g.nodes.forEach(function (n) { n.width(W); n.height(Hh); n.scaleX(1); n.scaleY(1); });
      hojas.forEach(function (h, i) {   // las mismas copias en las otras hojas
        if (i === hojaActual || !h) return;
        h.items.forEach(function (it) { if (claveDeSnap(it) === key) { it.w = round4(W); it.h = round4(Hh); } });
      });
      refreshAll();
      pushHistory();
      return;
    }
    guardarHojaActual();
    var units = [], movidas = 0;
    hojas.forEach(function (h, i) {
      if (!h) return;
      var quedan = [];
      h.items.forEach(function (it) {
        var suya = i === hojaActual ? !!mias[it.q] : claveDeSnap(it) === key;
        if (!suya) { quedan.push(it); return; }
        var c = Object.assign({}, it, { w: round4(W), h: round4(Hh) });
        if (it.l || it.g) quedan.push(c);   // bloqueada o en un grupo: crece o encoge en su sitio
        else { units.push([c]); movidas++; }
      });
      h.items = quedan;
      if (h.auto !== false && i !== hojaActual) h.sheetH = largoDeItems(h.items);
    });
    var orden = [hojaActual];
    for (var i = 0; i < hojas.length; i++) if (i !== hojaActual) orden.push(i);
    var r = colocarEnHojas(units, orden);
    quitarHojasVacias();
    cargarHoja(hojas[hojaActual]);
    pintarHojas();
    pushHistory();
    var fuera = Object.keys(r.porHoja).map(Number).filter(function (i) { return i !== hojaActual; });
    if (movidas > 1 || fuera.length) toast('Resized ' + movidas + ' cop' + (movidas === 1 ? 'y' : 'ies') + ' and placed ' + (movidas === 1 ? 'it' : 'them') + ' where ' + (movidas === 1 ? 'it fits' : 'they fit') +
      (fuera.length ? ': some went to ' + nombresHojas(fuera) + '.' : ', so nothing overlaps.'));
  }

  function fillListValues(groups) {
    var byKey = {};
    groups.forEach(function (g) { byKey[g.key] = g; });
    Array.prototype.forEach.call(el.itemList.children, function (row) {
      var g = byKey[row.dataset.groupKey];
      if (!g) return;
      var n = g.nodes[0];
      var locked = isLocked(n);
      var wInput = row.querySelector('.dim-w');
      var hInput = row.querySelector('.dim-h');
      if (activo() !== wInput) wInput.value = (n.width() * n.scaleX()).toFixed(2);
      if (activo() !== hInput) hInput.value = (n.height() * n.scaleY()).toFixed(2);
      wInput.disabled = locked;
      hInput.disabled = locked;

      var countInput = row.querySelector('.count-input');
      var otras = copiasEnOtrasHojas(g.key), total = g.nodes.length + otras;
      if (activo() !== countInput) countInput.value = total;
      countInput.disabled = locked;
      var where = row.querySelector('.copies-where');
      if (where) {
        where.textContent = otras ? g.nodes.length + ' on this sheet · ' + otras + ' on the other' + (hojas.length > 2 ? 's' : '') : '';
        where.hidden = !otras;
      }

      var badges = row.querySelector('.item-badges');
      badges.innerHTML = '';
      var d = dpiBadge(effectiveDpi(n));
      var b1 = document.createElement('span');
      b1.className = 'badge ' + d.cls;
      b1.textContent = d.label;
      b1.title = d.title;
      badges.appendChild(b1);
      if (g.src.trimRect && !g.src.trimmed) {
        var bt = document.createElement('button');
        bt.className = 'badge info badge-btn trim-badge';
        bt.textContent = 'Trim edges';
        bt.title = 'This file has empty transparent edges around the artwork. Click to trim them so the size in inches refers to the visible artwork only (also packs tighter).';
        bt.addEventListener('click', function (ev) {
          ev.stopPropagation();
          trimSourceAndNodes(g.src);
        });
        badges.appendChild(bt);
      }
      if (isStretched(n)) {
        var bs = document.createElement('button');
        bs.className = 'badge mid badge-btn';
        bs.textContent = 'Stretched · fix';
        bs.title = 'This design is stretched out of proportion and will print distorted. Click to restore the original shape (keeps the current width).';
        bs.addEventListener('click', function (ev) {
          ev.stopPropagation();
          var w0 = n.width() * n.scaleX();
          var ratio = g.src.artH / g.src.artW;
          g.nodes.forEach(function (nn) {
            nn.width(w0);
            nn.height(w0 * ratio);
            nn.scaleX(1); nn.scaleY(1);
          });
          refreshAll();
          pushHistory();
        });
        badges.appendChild(bs);
      }
      if (g.nodes.some(isOutOfSheet)) {
        var b2 = document.createElement('span');
        b2.className = 'badge bad';
        b2.textContent = 'Off sheet';
        b2.title = 'Part of this design is outside the sheet and would be cropped.';
        badges.appendChild(b2);
      }
      if (g.nodes.some(function (nn) { return overlapSet.has(nn); })) {
        var b3 = document.createElement('span');
        b3.className = 'badge warn';
        b3.textContent = 'Overlap';
        b3.title = 'This design overlaps another one. Overlapping artwork prints merged. Group designs (Ctrl+G) if the overlap is on purpose.';
        badges.appendChild(b3);
      }
      if (QUITAR_FONDO && g.src.bgRemoved) {
        // stays on the card so the background can be re-opened and tweaked
        var be = document.createElement('button');
        be.className = 'badge info badge-btn';
        be.textContent = 'Edit background';
        be.title = 'Re-open the background remover to tweak it (sensitivity, brush) — continues from your last settings.';
        be.addEventListener('click', function (ev) {
          ev.stopPropagation();
          openBgDialog(g.src);
        });
        badges.appendChild(be);
      } else if (QUITAR_FONDO && !g.src.isVector) {
        // SIEMPRE se ofrece, se haya detectado fondo liso o no. Antes solo salia con
        // fondo de un color detectado, y eso dejaba fuera justo los casos que ahora
        // SI se pueden resolver: una foto o un degradado, donde el recorte automatico
        // es la unica salida. Sin la etiqueta no habia ni forma de abrir el dialogo.
        // Un SVG sigue fuera: es vectorial y no tiene fondo que quitar.
        var b4 = document.createElement('button');
        var detectado = !!g.src.bgColor;
        // En ambar cuando se ha detectado fondo: ahi es un AVISO, porque ese color se
        // imprimiria como un recuadro. Sin deteccion es solo una herramienta a mano,
        // y en ambar seria una alarma falsa en cada arte que suba el cliente.
        b4.className = detectado ? 'badge mid badge-btn' : 'badge tool badge-btn';
        b4.textContent = 'Remove background';
        b4.title = detectado
          ? 'This file appears to sit on a solid background color, which would print as a colored box around your design. Click to make it transparent (keeps that color where it is INSIDE the design).'
          : 'Open the background remover. No solid background was detected, so try the automatic cut-out for photos and busy backgrounds.';
        b4.addEventListener('click', function (ev) {
          ev.stopPropagation();
          openBgDialog(g.src);
        });
        badges.appendChild(b4);
      }
      var minusB = row.querySelector('.minus-btn');
      if (minusB) minusB.disabled = locked || total <= 1;
      var plusB = row.querySelector('.plus-btn');
      if (plusB) plusB.disabled = locked;
      var lockB = row.querySelector('.lock-btn');
      if (lockB) {
        lockB.textContent = locked ? '🔒' : '🔓';
        lockB.classList.toggle('locked', locked);
        lockB.title = locked
          ? 'Unlock this design so it can be moved again'
          : 'Lock this design in place so it cannot be moved or resized by accident';
      }
      row.classList.toggle('locked', locked);
    });
  }

  // Refresh the card values in place; when the grouping itself changed (copy
  // added or removed, size edited) rebuild the list. During an active handle
  // drag the rebuild waits for transformend so the DOM stays stable.
  function updateListValues() {
    var groups = computeGroups();
    if (groupsSignature(groups) !== renderedSignature &&
        !(transformer.isTransforming && transformer.isTransforming())) {
      renderList();
      return;
    }
    fillListValues(groups);
  }

  function highlightRow() {
    var selKeys = {};
    selection.forEach(function (n) { selKeys[groupKeyOf(n)] = true; });
    Array.prototype.forEach.call(el.itemList.children, function (row) {
      row.classList.toggle('selected', !!selKeys[row.dataset.groupKey]);
    });
  }

  // --------------------------------------------------------- auto-arrange

  // Repack everything with the MaxRects engine: bottom-left placement with
  // 90-degree rotation, so leftover holes get used. Groups travel as one
  // piece; locked designs stay where they are and become obstacles.
  // Auto-arrange con una hoja larga y muchos disenos deja la pagina congelada varios
  // segundos: el calculo es rapido (el empaquetado tarda 1 ms con 400 piezas), pero
  // recolocar y REDIBUJAR cientos de imagenes en el lienzo es un trabajo de golpe que
  // el navegador no puede repartir. No se puede hacer instantaneo, asi que al menos que
  // no parezca rota: se ensena el overlay, se cede el hilo para que el navegador lo
  // pinte, y despues se hace el trabajo.
  async function autoArrange() {
    if (exporting) return;                       // ya hay una tarea larga en curso
    var nodesPrev = itemNodes();
    if (!nodesPrev.length) return;

    // Solo se ensena el overlay cuando el trabajo se va a notar. Por debajo de esto
    // es imperceptible y un overlay que parpadea es peor que no ponerlo. Se mira el
    // numero de disenos Y el largo de la hoja, porque las dos cosas suman.
    if (nodesPrev.length < 40 && sheetHIn < 96) { autoArrangeAhora(); return; }

    await conOverlay('Arranging your designs',
      nodesPrev.length + ' designs on a ' + SHEET_W_IN + '" \u00d7 ' + sheetHIn + '" sheet. This can take a moment.',
      async function () {
        setProgress(0.15);
        await nextTick();                        // que el navegador pinte el overlay
        autoArrangeAhora();
        setProgress(1);
        await nextTick();
      }, false);
  }

  function autoArrangeAhora() {
    var nodes = itemNodes();
    if (!nodes.length) return;
    var sp = getSpacing();

    // bake any leftover scale so width/height are the real inches
    nodes.forEach(function (n) {
      if (isLocked(n)) return;
      n.width(Math.abs(n.width() * n.scaleX()));
      n.height(Math.abs(n.height() * n.scaleY()));
      n.scaleX(1); n.scaleY(1);
    });

    var locked = nodes.filter(isLocked);
    var movable = nodes.filter(function (n) { return !isLocked(n); });
    if (!movable.length) {
      toast('All designs are locked. Unlock some to rearrange them.');
      return;
    }

    var units = [], byGid = {};
    movable.forEach(function (n) {
      var g = n.getAttr('groupId');
      if (g) {
        if (!byGid[g]) { byGid[g] = []; units.push(byGid[g]); }
        byGid[g].push(n);
      } else {
        units.push([n]);
      }
    });

    // Pack into an unbounded-height bin, then compare with the sheet: the
    // layout stays identical, only the "does it fit" message changes.
    var eng = Packing.create({ width: SHEET_W_IN - 2 * MARGIN_IN, height: 1e6, gap: sp });
    locked.forEach(function (n) {
      var b = nodeBBox(n);
      eng.occupy(b.minX - MARGIN_IN, b.minY - MARGIN_IN, b.w, b.h);
    });

    // ORIENTACION POR GRUPO (20-oct-2026). Antes el motor decidia pieza a pieza si girarla
    // (la giraba si asi caia un poco mas arriba): con muchas copias iguales salia una mezcla
    // de piezas de lado y de pie, con huecos, y una pieza ya girada podia acabar boca abajo.
    // Ahora las piezas del MISMO tamano van todas con la misma orientacion: la que gasta menos
    // largo de hoja para ese grupo (mas piezas por fila). Los grupos y las piezas con angulos
    // raros no se giran.
    var anchoUtil = SHEET_W_IN - 2 * MARGIN_IN;
    function infoUnidad(u) {
      var b = unionBBox(u);
      var r = u.length === 1 ? ((Math.round(u[0].rotation()) % 360) + 360) % 360 : null;
      var girable = r !== null && r % 90 === 0;
      var nw = girable ? Math.abs(u[0].width() * u[0].scaleX()) : b.w;
      var nh = girable ? Math.abs(u[0].height() * u[0].scaleY()) : b.h;
      return { u: u, b: b, r: r, girable: girable, nw: nw, nh: nh,
               area: b.w * b.h, seq: nodeSeqNum(u[0]),
               clave: girable ? [Math.min(nw, nh).toFixed(2), Math.max(nw, nh).toFixed(2)].join('x') : 'fijo-' + nodeSeqNum(u[0]) };
    }
    var metas = units.map(infoUnidad);
    var grupos = {};
    metas.forEach(function (m) { (grupos[m.clave] = grupos[m.clave] || []).push(m); });
    Object.keys(grupos).forEach(function (k) {
      var g = grupos[k];
      var m0 = g[0];
      if (!m0.girable) { g.forEach(function (m) { m.ow = m.b.w; m.oh = m.b.h; m.giro = null; }); return; }
      // de pie = el lado corto de ancho si asi caben mas por fila; se compara el largo total
      function largoCon(w, h) {
        var cols = Math.floor((anchoUtil + sp) / (w + sp) + 1e-9);
        if (cols < 1) return Infinity;
        return Math.ceil(g.length / cols) * (h + sp);
      }
      var dePie = largoCon(m0.nw, m0.nh);     // orientacion natural del arte
      var deLado = largoCon(m0.nh, m0.nw);
      var tumbar = deLado < dePie - 1e-6;     // empate: de pie
      g.forEach(function (m) {
        var actualTumbada = m.r % 180 === 90;
        m.giro = tumbar === actualTumbada ? null : (actualTumbada ? m.r - 90 : m.r + 90);
        m.ow = tumbar ? m.nh : m.nw;
        m.oh = tumbar ? m.nw : m.nh;
      });
    });

    // los grupos mas grandes (por pieza) primero; dentro del grupo, en el orden en que se subieron
    metas.sort(function (a, b) {
      return Math.max(b.ow, b.oh) - Math.max(a.ow, a.oh) || b.area - a.area || a.clave.localeCompare(b.clave) || a.seq - b.seq;
    });

    // DOS PLANES, sin tocar el lienzo todavia (21-oct-2026): el de siempre (MaxRects por grupos) y el de
    // guillotina (planGuillotina, el de by size: bloques ordenados, los huecos se llenan con los disenos
    // pequenos y la hoja se separa con cortes rectos). Gana el que cobra menos largo; si empatan, la
    // guillotina, porque se corta mejor. Con disenos bloqueados solo el de siempre: lo bloqueado es un
    // obstaculo y con el la guillotina ya no se puede garantizar. Mover y encimar a mano sigue libre.
    var fondoFijo = 0;   // lo bloqueado tambien ocupa
    locked.forEach(function (n) { fondoFijo = Math.max(fondoFijo, nodeBBox(n).maxY - MARGIN_IN); });
    var planA = [], fondoA = fondoFijo, anchosA = [];
    metas.forEach(function (m) {
      var s = eng.place(m.ow, m.oh, false);
      if (s) { planA.push({ m: m, giro: m.giro, x: s.x, y: s.y }); fondoA = Math.max(fondoA, s.y + m.oh); }
      else anchosA.push(m);
    });
    // mas ancho que la hoja en las dos orientaciones: se aparca debajo de todo y salta el aviso
    anchosA.forEach(function (m) { planA.push({ m: m, giro: m.giro, x: 0, y: fondoA + sp }); fondoA += sp + m.oh; });

    var planB = null, fondoB = 0;
    if (!locked.length) {
      var piezasG = [], porClave = {};
      metas.forEach(function (m) {
        if (!m.girable) { piezasG.push({ w: m.b.w, h: m.b.h, n: 1, girable: false, metas: [m] }); return; }
        var pz = porClave[m.clave];
        if (!pz) { pz = porClave[m.clave] = { w: m.nw, h: m.nh, n: 0, girable: true, metas: [] }; piezasG.push(pz); }
        pz.n++;
        pz.metas.push(m);
      });
      piezasG.forEach(function (pz) { pz.metas.sort(function (x, y) { return x.seq - y.seq; }); });
      var pg = planGuillotina(piezasG, anchoUtil, 1e6, sp);
      planB = [];
      var sinSitio = [];
      piezasG.forEach(function (pz, i) {
        pz.metas.forEach(function (m, j) {
          var s = pg.sitios[i][j];
          if (!s) { sinSitio.push(m); return; }
          var W = s.gir ? pz.h : pz.w, H = s.gir ? pz.w : pz.h;
          var giro = null;
          if (m.girable && Math.abs(W - H) > 1e-3) {
            // de pie si su ancho natural es el de la celda; se gira lo minimo (nunca boca abajo)
            var quiereDePie = Math.abs(m.nw - W) < 1e-3;
            var tumbada = m.r % 180 === 90;
            giro = (!tumbada) === quiereDePie ? null : (tumbada ? m.r - 90 : m.r + 90);
          }
          planB.push({ m: m, giro: giro, x: s.x, y: s.y });
          fondoB = Math.max(fondoB, s.y + H);
        });
      });
      sinSitio.forEach(function (m) { planB.push({ m: m, giro: m.giro, x: 0, y: fondoB + sp }); fondoB += sp + m.oh; });
    }

    // el largo que se cobraria con cada plan (la escalera del producto, o de 2 en 2 si es exacto)
    function cobra(fondo) {
      var necesita = fondo + 2 * MARGIN_IN;
      if (largoExacto()) {
        var e = currentType().exacto || { paso: 2, min: SHEET_HEIGHTS[0] || 12 };
        return Math.max(e.min, MIN_LARGO_IN, Math.ceil((necesita - 1e-9) / e.paso) * e.paso);
      }
      for (var i = 0; i < SHEET_HEIGHTS.length; i++) if (SHEET_HEIGHTS[i] >= necesita - 1e-9) return SHEET_HEIGHTS[i];
      return necesita;   // mas que la hoja mayor: se compara el largo de verdad
    }
    var plan = (planB && cobra(fondoB) <= cobra(fondoA) + 1e-6) ? planB : planA;
    plan.forEach(function (e) {
      if (e.giro !== null && e.giro !== undefined) e.m.u[0].rotation(e.giro);
      placeUnitAt(e.m.u, MARGIN_IN + e.x, MARGIN_IN + e.y, false);
    });

    if (largoAuto) {
      var tope = maxAltura() - MARGIN_IN + 1e-6;
      var pasan = movable.filter(function (n) { return nodeBBox(n).maxY > tope; });
      if (pasan.length) {
        var unidadesFuera = unidadesDe(pasan).length;
        var fue = desbordar(pasan);
        renderList();
        refreshAll();
        pushHistory();
        if (fue.n) toast(unidadesFuera + ' design' + (unidadesFuera === 1 ? '' : 's') + ' did not fit in ' + maxAltura() + '" and went to ' + nombresHojas(fue.hojas) + '.');
        return;
      }
    }
    var needed = contentBottom() + MARGIN_IN;
    refreshAll();
    pushHistory();
    if (needed > sheetHIn + 1e-9) {
      var fit = null;
      for (var i = 0; i < SHEET_HEIGHTS.length; i++) {
        if (SHEET_HEIGHTS[i] >= needed) { fit = SHEET_HEIGHTS[i]; break; }
      }
      toast(fit
        ? 'Your designs need about ' + Math.ceil(needed) + '". Try the ' + SHEET_W_IN + '" × ' + fit + '" sheet.'
        : 'Your designs need about ' + Math.ceil(needed) + '", more than the largest sheet. Remove some designs.');
    }
  }

  // ------------------------------------------------- quitar fondo blanco
  // Portado el 10-sep-2026 desde DTF Transfers Now/background-remover-lab/, donde
  // quedo congelado el 21-jul cuando se retiro de los builders por no estar fino.
  // Su LEEME-LAB.md deja anotados los cuatro problemas conocidos; el que de verdad
  // importa en DTF es el primero, porque el halo SE IMPRIME:
  //   1. bordes con antialias dejan un hilo de halo
  //   2. fondos con degradado o sombra suave no los cubre el flood near-white
  //   3. el flood a resolucion completa es lento en imagenes muy grandes
  //   4. se guarda la tolerancia y las mascaras, no el resultado ya procesado
  // Es un algoritmo LOCAL: flood-fill desde los bordes mas mascaras de pincel.
  // Cero servicios externos, cero coste, y el arte del cliente no sale de su equipo.

  // Mira la BANDA DEL BORDE y decide si el arte viene sobre un fondo de un solo
  // color. Antes solo reconocia blanco y grises muy claros, asi que un logo sobre
  // azul liso o sobre negro no se ofrecia siquiera. Ahora saca el color dominante
  // del borde, sea el que sea, y mide cuanto del borde se le parece.
  //
  // Devuelve { r, g, b, uniform } o null. 'uniform' es la fraccion del borde que
  // cae dentro de la tolerancia del color dominante: por debajo de 0.72 el fondo no
  // es liso (degradado, foto, textura) y no se ofrece quitarlo, porque este
  // algoritmo no puede con eso y prometerlo seria mentir.
  function detectSolidBg(pc) {
    try {
      var w = pc.width, h = pc.height;
      var d = pc.getContext('2d').getImageData(0, 0, w, h).data;
      var band = Math.max(1, Math.round(Math.min(w, h) * 0.04));
      var step = Math.max(1, Math.floor(Math.max(w, h) / 200));
      var muestras = 0, opacos = 0;
      var cubos = {};   // color troceado en 32 niveles por canal -> cuantos y su suma

      function px(x, y) {
        var i = (y * w + x) * 4;
        muestras++;
        if (d[i + 3] < 200) return;
        opacos++;
        var r = d[i], g = d[i + 1], b = d[i + 2];
        var k = (r >> 3) * 1024 + (g >> 3) * 32 + (b >> 3);
        var c = cubos[k] || (cubos[k] = { n: 0, r: 0, g: 0, b: 0 });
        c.n++; c.r += r; c.g += g; c.b += b;
      }

      var x, y, b2;
      for (x = 0; x < w; x += step) {
        for (b2 = 0; b2 < band; b2++) { px(x, b2); px(x, h - 1 - b2); }
      }
      for (y = 0; y < h; y += step) {
        for (b2 = 0; b2 < band; b2++) { px(b2, y); px(w - 1 - b2, y); }
      }

      if (!muestras || opacos / muestras < 0.85) return null;  // borde ya transparente

      var mejor = null;
      Object.keys(cubos).forEach(function (k) {
        if (!mejor || cubos[k].n > mejor.n) mejor = cubos[k];
      });
      if (!mejor) return null;

      // color exacto: la media de los pixeles de ese cubo, no el centro del cubo
      var R = Math.round(mejor.r / mejor.n);
      var G = Math.round(mejor.g / mejor.n);
      var B = Math.round(mejor.b / mejor.n);

      // cuanto del borde se parece de verdad a ese color
      var cerca = 0;
      Object.keys(cubos).forEach(function (k) {
        var c = cubos[k];
        var cr = c.r / c.n, cg = c.g / c.n, cb = c.b / c.n;
        if (Math.abs(cr - R) <= BG_TOL_DEFAULT &&
            Math.abs(cg - G) <= BG_TOL_DEFAULT &&
            Math.abs(cb - B) <= BG_TOL_DEFAULT) cerca += c.n;
      });

      var uniform = cerca / opacos;
      if (uniform <= 0.72) return null;   // borde no uniforme: este motor no puede
      return { r: R, g: G, b: B, uniform: uniform };
    } catch (err) {
      return null;
    }
  }

  // Bounding box of the visible (non-transparent) pixels of a canvas, or
  // null when the image is fully transparent. Used to auto-trim the empty
  // border that most uploads carry around the real artwork.

  // color: { r, g, b } del fondo a quitar. Antes esto estaba clavado en el blanco
  // (cada canal a menos de 'tol' de 255), asi que solo servia para JPG con fondo
  // blanco. Ahora se compara contra el color que se le pase, con la misma medida
  // (distancia maxima por canal), asi que el comportamiento sobre blanco es
  // exactamente el de antes y ademas funciona sobre cualquier fondo liso.
  function floodClearBg(imageData, tol, color, keepMask, removeMask, eraseMask) {
    var w = imageData.width, h = imageData.height, d = imageData.data;
    var n = w * h;
    var visited = new Uint8Array(n);
    var stack = new Int32Array(n);   // worst case: every pixel is background
    var top = 0;
    var R = color ? color.r : 255, G = color ? color.g : 255, B = color ? color.b : 255;
    function bg(idx) {
      if (keepMask && keepMask[idx]) return false;         // protected
      var i = idx * 4;
      if (d[i + 3] < 8) return true;                       // already transparent
      return Math.abs(d[i] - R) <= tol &&
             Math.abs(d[i + 1] - G) <= tol &&
             Math.abs(d[i + 2] - B) <= tol;
    }
    function seed(idx) {
      if (visited[idx]) return;
      visited[idx] = 1;
      if (bg(idx)) stack[top++] = idx;
    }
    var x, y;
    for (x = 0; x < w; x++) { seed(x); seed((h - 1) * w + x); }
    for (y = 0; y < h; y++) { seed(y * w); seed(y * w + (w - 1)); }
    // painted "remove" pixels join the flood as seeds, but bg() still gates
    // them on the near-white test, so colored details under the brush stay
    if (removeMask) { for (var k = 0; k < n; k++) if (removeMask[k]) seed(k); }
    while (top > 0) {
      var idx = stack[--top];
      d[idx * 4 + 3] = 0;                                  // clear alpha
      var cx = idx % w, cy = (idx - cx) / w;
      if (cx > 0) seed(idx - 1);
      if (cx < w - 1) seed(idx + 1);
      if (cy > 0) seed(idx - w);
      if (cy < h - 1) seed(idx + w);
    }
    // hard eraser last: delete exactly what was painted, any color
    if (eraseMask) { for (var e = 0; e < n; e++) if (eraseMask[e]) d[e * 4 + 3] = 0; }

    // Y al final, siempre, limpiar el hilo de fondo pegado al borde. Va aqui dentro
    // y no en quien llama para que no se pueda olvidar en un camino nuevo.
    defringeEdges(imageData, tol, color, keepMask);
  }

  // ------------------------------------------------- halo de los bordes (defringe)
  //
  // EL PROBLEMA, y es el que hizo retirar esta funcion en julio: un JPG tiene los
  // bordes suavizados (antialias). Los pixeles del borde no son ni arte ni fondo,
  // son una MEZCLA de los dos. El flood los deja opacos porque no llegan a parecerse
  // bastante al fondo, asi que queda un hilo del color del fondo pegado al arte.
  // En pantalla casi no se ve. En DTF se imprime, y sobre una camiseta oscura ese
  // hilo blanco alrededor de las letras se ve desde el otro lado de la sala.
  //
  // LA SOLUCION: en el anillo de pixeles que tocan el vacio, deshacer la mezcla.
  // De P = a*C + (1-a)*B, con P el pixel que hay, B el color del fondo y C el color
  // real del arte, se estima 'a' por lo lejos que esta P de B y se despeja C.
  // Resultado: el borde queda semitransparente donde de verdad lo era, con su color
  // limpio, en vez de opaco y contaminado.
  //
  // Solo toca el anillo del borde: el interior del arte no se altera.
  function defringeEdges(imageData, tol, color, keepMask) {
    var w = imageData.width, h = imageData.height, d = imageData.data;
    var R = color ? color.r : 255, G = color ? color.g : 255, B = color ? color.b : 255;
    var n = w * h;

    // 1) el anillo: opaco y con al menos un vecino ya transparente
    var borde = new Uint8Array(n);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = y * w + x;
        if (d[i * 4 + 3] < 8) continue;
        if (keepMask && keepMask[i]) continue;   // lo que el cliente protegio, intacto
        if ((x > 0 && d[(i - 1) * 4 + 3] < 8) ||
            (x < w - 1 && d[(i + 1) * 4 + 3] < 8) ||
            (y > 0 && d[(i - w) * 4 + 3] < 8) ||
            (y < h - 1 && d[(i + w) * 4 + 3] < 8)) borde[i] = 1;
      }
    }

    // 2) deshacer la mezcla, pixel a pixel del anillo
    var orig = new Uint8ClampedArray(d);   // leer siempre del original, no de lo ya tocado
    for (var y2 = 0; y2 < h; y2++) {
      for (var x2 = 0; x2 < w; x2++) {
        var k = y2 * w + x2;
        if (!borde[k]) continue;
        var j = k * 4;

        // Color del arte de al lado: la media de los vecinos opacos que NO son borde.
        // Ese es el C de la mezcla P = a*C + (1-a)*B. Sin un vecino asi no hay con
        // que comparar (trazo de un pixel de grosor) y se deja como esta.
        var cr = 0, cg = 0, cb = 0, cn = 0;
        for (var dy = -1; dy <= 1; dy++) {
          for (var dx = -1; dx <= 1; dx++) {
            var nx = x2 + dx, ny = y2 + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            var m = ny * w + nx;
            if (borde[m] || orig[m * 4 + 3] < 250) continue;
            cr += orig[m * 4]; cg += orig[m * 4 + 1]; cb += orig[m * 4 + 2]; cn++;
          }
        }
        if (!cn) continue;
        cr /= cn; cg /= cn; cb /= cn;

        // Se mide en el canal donde arte y fondo mas se diferencian: es donde la
        // estimacion es mas fiable. Si apenas se diferencian, no hay halo que quitar.
        var dr = Math.abs(cr - R), dg = Math.abs(cg - G), db = Math.abs(cb - B);
        var span = Math.max(dr, dg, db);
        if (span < Math.max(8, tol / 2)) continue;

        var canal = span === dr ? 0 : (span === dg ? 1 : 2);
        var base = canal === 0 ? R : (canal === 1 ? G : B);
        var a = (orig[j + canal] - base) / ((canal === 0 ? cr : canal === 1 ? cg : cb) - base);
        if (!isFinite(a)) continue;
        a = Math.max(0, Math.min(1, a));

        if (a >= 0.98) continue;                       // arte puro: no se toca
        if (a <= 0.03) { d[j + 3] = 0; continue; }     // era fondo: fuera

        // C = (P - (1-a)*B) / a
        d[j]     = Math.max(0, Math.min(255, Math.round((orig[j]     - (1 - a) * R) / a)));
        d[j + 1] = Math.max(0, Math.min(255, Math.round((orig[j + 1] - (1 - a) * G) / a)));
        d[j + 2] = Math.max(0, Math.min(255, Math.round((orig[j + 2] - (1 - a) * B) / a)));
        d[j + 3] = Math.round(orig[j + 3] * a);
      }
    }
  }

  // Escala una mascara de ALFA (0..255) con interpolacion suave. Las del pincel usan
  // scaleMask, que va a vecino mas cercano porque son de 0 o 1 y ahi el suavizado
  // solo ensuciaria; esta es un degradado y necesita lo contrario.
  function scaleAlphaMask(mask, mw, mh, nw, nh) {
    if (!mask) return null;
    var c = document.createElement('canvas');
    c.width = mw; c.height = mh;
    var ctx = c.getContext('2d');
    var im = ctx.createImageData(mw, mh);
    for (var i = 0; i < mw * mh; i++) {
      im.data[i * 4] = im.data[i * 4 + 1] = im.data[i * 4 + 2] = mask[i];
      im.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(im, 0, 0);
    var g = document.createElement('canvas');
    g.width = nw; g.height = nh;
    var gx = g.getContext('2d');
    gx.imageSmoothingEnabled = true;
    gx.imageSmoothingQuality = 'high';
    gx.drawImage(c, 0, 0, mw, mh, 0, 0, nw, nh);
    var d = gx.getImageData(0, 0, nw, nh).data;
    var out = new Uint8Array(nw * nh);
    for (var k = 0; k < nw * nh; k++) out[k] = d[k * 4];
    return out;
  }

  // Aplica el alfa del modelo, con los pinceles por encima. Verde fuerza opaco,
  // azul y rojo fuerzan transparente: con IA "quitar" y "borrar" quieren lo mismo.
  function applyAiAlpha(imageData, alpha, keepMask, removeMask, eraseMask) {
    var d = imageData.data, n = imageData.width * imageData.height;
    for (var i = 0; i < n; i++) {
      var a = alpha[i];
      if (keepMask && keepMask[i]) a = 255;
      if (removeMask && removeMask[i]) a = 0;
      if (eraseMask && eraseMask[i]) a = 0;
      var j = i * 4;
      if (a < d[j + 3]) d[j + 3] = a;   // nunca vuelve opaco lo que ya era transparente
    }
  }

  // Nearest-neighbor scale a base-resolution brush mask up to native size, so
  // marks painted on the (smaller) dialog preview apply to the full export.
  function scaleMask(mask, mw, mh, nw, nh) {
    if (!mask) return null;
    if (mw === nw && mh === nh) return mask;
    var out = new Uint8Array(nw * nh);
    for (var y = 0; y < nh; y++) {
      var sy = Math.min(mh - 1, (y * mh / nh) | 0);
      for (var x = 0; x < nw; x++) {
        var sx = Math.min(mw - 1, (x * mw / nw) | 0);
        if (mask[sy * mw + sx]) out[y * nw + x] = 1;
      }
    }
    return out;
  }

  var BG_TOL_DEFAULT = 32;

  // Low-level "remove background": replace the print image with a
  // native-resolution canvas whose border-connected white is transparent, and
  // rebuild the derivatives. `tol` is the sensitivity (distance from 255);
  // `masks` are the optional base-resolution keep/remove brush marks. No UI
  // side effects (used on restore too).
  function applyBgRemoval(src, tol, masks, color, aiMask) {
    if (!src || src.isVector || src.bgRemoved) return false;
    try {
      var c = document.createElement('canvas');
      c.width = src.nativeW; c.height = src.nativeH;
      var ctx = c.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(src.imgEl, 0, 0);
      var img = ctx.getImageData(0, 0, c.width, c.height);
      var keep = masks ? scaleMask(masks.keep, masks.w, masks.h, c.width, c.height) : null;
      var rem = masks ? scaleMask(masks.remove, masks.w, masks.h, c.width, c.height) : null;
      var era = masks ? scaleMask(masks.erase, masks.w, masks.h, c.width, c.height) : null;
      if (aiMask) {
        applyAiAlpha(img, scaleAlphaMask(aiMask.a, aiMask.w, aiMask.h, c.width, c.height), keep, rem, era);
      } else {
        floodClearBg(img, tol == null ? BG_TOL_DEFAULT : tol, color || src.bgColor, keep, rem, era);
      }
      ctx.putImageData(img, 0, 0);
      src.printSrc = c;
      src.bgRemoved = true;
      src.bgTol = tol == null ? BG_TOL_DEFAULT : tol;
      src.bgMasks = masks || null;   // base-res, persisted so restore reproduces it
      src.bgUsedColor = color || src.bgColor;   // con que color se quito, para reeditar
      src.bgAi = aiMask || null;                // si se uso el modelo, para reeditar y guardar
      src.bgColor = null;                       // ya no hay fondo que ofrecer quitar
      rebuildDerived(src);
      return true;
    } catch (err) {
      return false;
    }
  }

  // Crop the source to src.trimRect and shrink every placed copy so the visible
  // artwork keeps its exact print size and position on the sheet. No toast or
  // history push (the caller decides); returns true when it applied.
  function applyTrimToNodes(src) {
    if (!src.trimRect || src.trimmed) return false;
    var fx = src.trimRect.x / src.nativeW;
    var fy = src.trimRect.y / src.nativeH;
    var kw = src.trimRect.w / src.nativeW;
    var kh = src.trimRect.h / src.nativeH;
    applySourceTrim(src);
    itemNodes().forEach(function (n) {
      if (n.getAttr('sourceId') !== src.id) return;
      var w = n.width() * n.scaleX(), h = n.height() * n.scaleY();
      var rad = Konva.getAngle(n.rotation());
      var cos = Math.cos(rad), sin = Math.sin(rad);
      var dx = fx * w, dy = fy * h;   // local offset, rotated into world space
      n.position({ x: n.x() + dx * cos - dy * sin, y: n.y() + dx * sin + dy * cos });
      n.width(w * kw);
      n.height(h * kh);
      n.scaleX(1); n.scaleY(1);
      n.image(src.previewCanvas);
    });
    return true;
  }

  // Undo a source's background edit + trim, returning it to the pristine
  // original so it can be re-processed from scratch (used when re-editing).
  // Every placed copy keeps its print scale: we un-scale/un-shift the trim, so
  // the design lands back at full-native proportions before the new trim.
  function resetSourceEdits(src) {
    if (src.trimmed && src.trimRect) {
      var t = src.trimRect;
      var kw = t.w / src.nativeW, kh = t.h / src.nativeH;
      var fx = t.x / src.nativeW, fy = t.y / src.nativeH;
      itemNodes().forEach(function (n) {
        if (n.getAttr('sourceId') !== src.id) return;
        var w = (n.width() * n.scaleX()) / kw, h = (n.height() * n.scaleY()) / kh;
        var rad = Konva.getAngle(n.rotation()), cos = Math.cos(rad), sin = Math.sin(rad);
        var dx = fx * w, dy = fy * h;              // local trim offset, un-shifted
        n.position({ x: n.x() - (dx * cos - dy * sin), y: n.y() - (dx * sin + dy * cos) });
        n.width(w); n.height(h); n.scaleX(1); n.scaleY(1);
      });
    }
    src.artX = 0; src.artY = 0; src.artW = src.nativeW; src.artH = src.nativeH;
    src.trimmed = false; src.trimRect = null;
    src.printSrc = src.imgEl; src.bgRemoved = false; src.bgTol = undefined; src.bgMasks = null;
    // al volver al original, vuelve a haber fondo que ofrecer quitar
    if (!src.bgColor) src.bgColor = src.bgUsedColor || null;
    src.bgAi = null;
    rebuildDerived(src);
  }

  // "Remove background": clear the background at sensitivity `tol`, then auto-trim
  // the transparent margin it just revealed (here the margin is definitely junk,
  // unlike a fresh upload, so trimming is automatic) so sizes refer to the
  // visible design. When re-editing an already-removed source it resets to the
  // original first, so edits never compound.
  function removeBackground(src, tol, masks, color, aiMask) {
    if (src.bgRemoved) resetSourceEdits(src);   // re-edit: start clean from the original
    if (!applyBgRemoval(src, tol, masks, color, aiMask)) {
      toast('Could not remove the background from "' + src.name + '". Try a PNG saved with transparency.');
      return;
    }
    var trimmed = false;
    if (!src.trimmed) {
      src.trimRect = suggestTrimRect(src.previewCanvas, src.nativeW, src.nativeH);
      trimmed = applyTrimToNodes(src);
    }
    if (!trimmed) {
      itemNodes().forEach(function (n) {
        if (n.getAttr('sourceId') === src.id) n.image(src.previewCanvas);
      });
    }
    renderList();
    refreshAll();
    pushHistory();
    toast(trimmed
      ? 'Removed the background and trimmed "' + src.name + '" to the design.'
      : 'Removed the background from "' + src.name + '".');
  }

  // "Trim edges" card action: same crop, with its own toast + history entry.
  function trimSourceAndNodes(src) {
    if (!applyTrimToNodes(src)) return;
    renderList();
    refreshAll();
    pushHistory();
    toast('Trimmed "' + src.name + '" — sizes now refer to the visible artwork.');
  }

  // ----------------------------------------- background removal dialog
  // "Remove background" opens this dialog: a big live preview updates as the
  // customer drags the sensitivity slider (so they can dial it down when a
  // pale shirt is being eaten), and they can zoom/pan the preview to inspect
  // the edges before committing. Only on Apply is the full-resolution source
  // processed.
  var bgDialogSrc = null;
  var bgBase = null;         // { data: ImageData, w, h } working copy of the original
  var bgResult = null;       // canvas holding the flood-cleared base at current tol
  var bgKeep = null;         // Uint8Array base mask: protect (green brush)
  var bgRemove = null;       // Uint8Array base mask: remove white here (blue brush)
  var bgErase = null;        // Uint8Array base mask: hard delete (red eraser)
  var bgOverlayCanvas = null;// base-size canvas tinting the painted marks
  var bgView = { scale: 1, cx: 0, cy: 0 };  // zoom + center (in base pixels)
  var bgMode = 'pan';        // 'pan' | 'keep' | 'remove' | 'erase'
  // Donde esta el puntero dentro del canvas, para dibujar el circulo del pincel.
  // Sin esto el cliente ajusta el tamano a ciegas y no lo descubre hasta que pinta.
  var bgCursor = null;       // { x, y } en pixeles de canvas, o null si esta fuera
  var bgCursorTimer = null;  // muestra el circulo en el centro al mover el deslizador
  var bgBrush = 26;          // brush radius in base pixels
  // Color que se esta quitando en el dialogo. Sale de la deteccion automatica y el
  // cliente puede cambiarlo con el cuentagotas si acerto mal (p. ej. un logo con
  // marco blanco sobre fondo azul: quiza quiere quitar el azul, quiza el blanco).
  var bgPickColor = { r: 255, g: 255, b: 255 };
  // Alfa que propone el modelo, a resolucion base. Cuando existe manda sobre el
  // flood: son dos motores distintos y mezclarlos daria un resultado impredecible.
  // Los pinceles siguen funcionando encima de los dos.
  var bgAiMask = null;

  function hex2(v) { return ('0' + Math.max(0, Math.min(255, v)).toString(16)).slice(-2); }

  // El boton dice lo que va a hacer al pulsarlo, no lo que hizo. Y con el recorte
  // del modelo puesto, el deslizador de color no pinta nada: se desactiva para que
  // nadie lo mueva esperando un cambio.
  function pintarEstadoIa() {
    if (el.bgAi) {
      el.bgAi.classList.toggle('activo', !!bgAiMask);
      var txt = el.bgAi.lastChild;
      if (txt && txt.nodeType === 3) txt.nodeValue = bgAiMask ? 'Undo auto cut-out' : 'Auto cut-out';
      el.bgAi.title = bgAiMask
        ? 'Go back to removing a single background color'
        : 'Photo or busy background? Let the computer find the subject. Runs on your device; nothing is uploaded';
    }
    if (el.bgTol) el.bgTol.disabled = !!bgAiMask;
    if (el.bgModePick) el.bgModePick.disabled = !!bgAiMask;
    var bloqueColor = RAIZ.getElementById('bg-engine-color');
    if (bloqueColor) bloqueColor.classList.toggle('apagado', !!bgAiMask);
  }

  function pintarColorFondo() {
    var c = bgPickColor || { r: 255, g: 255, b: 255 };
    var hex = '#' + hex2(c.r) + hex2(c.g) + hex2(c.b);
    if (el.bgColorChip) el.bgColorChip.style.background = hex;
    if (el.bgColorHex) el.bgColorHex.textContent = hex.toUpperCase();
  }
  var bgShowMarks = false;   // keep the green/red tint visible after a stroke
  var bgStroking = false;    // a brush stroke is in progress (marks always show then)
  var bgUndoStack = [];      // per-dialog history of the brush masks
  var bgRedoStack = [];
  var BG_BASE_MAX = 900;     // working resolution for the preview (crisp on zoom)
  var BG_HISTORY_MAX = 20;


  function openBgDialog(src) {
    // Cinturon y tirantes: aunque la etiqueta no se pinte, cualquier otro camino que
    // llegue aqui con la funcion apagada se queda fuera.
    if (!QUITAR_FONDO) return;
    if (!src || src.isVector) return;
    if (!el.bgOverlay) { removeBackground(src); return; }   // sin dialogo: hazlo y ya
    var reEdit = !!src.bgRemoved;   // re-opening an already-removed source
    bgDialogSrc = src;
    // Color de partida: el detectado al subir, o el que se uso si se esta reeditando.
    // Sin ninguno de los dos (el cliente abrio el dialogo a mano) se asume blanco,
    // que es el caso mas comun.
    bgPickColor = src.bgUsedColor || src.bgColor || { r: 255, g: 255, b: 255 };
    bgAiMask = (src.bgAi && src.bgAi.a) ? src.bgAi.a : null;
    pintarColorFondo();
    pintarEstadoIa();
    // sample the original ONCE at a decent resolution; the flood fill re-runs
    // on a copy per slider tick (fast), and zoom just redraws from bgResult
    var s = Math.min(1, BG_BASE_MAX / Math.max(src.nativeW, src.nativeH));
    var bw = Math.max(1, Math.round(src.nativeW * s));
    var bh = Math.max(1, Math.round(src.nativeH * s));
    var bc = document.createElement('canvas');
    bc.width = bw; bc.height = bh;
    bc.getContext('2d').drawImage(src.imgEl, 0, 0, bw, bh);
    bgBase = { data: bc.getContext('2d').getImageData(0, 0, bw, bh), w: bw, h: bh };
    bgResult = document.createElement('canvas');
    bgResult.width = bw; bgResult.height = bh;
    bgKeep = new Uint8Array(bw * bh);
    bgRemove = new Uint8Array(bw * bh);
    bgErase = new Uint8Array(bw * bh);
    bgOverlayCanvas = document.createElement('canvas');
    bgOverlayCanvas.width = bw; bgOverlayCanvas.height = bh;
    bgView = { scale: 1, cx: bw / 2, cy: bh / 2 };
    bgUndoStack = []; bgRedoStack = [];
    bgStroking = false;
    bgShowMarks = el.bgShowMarks ? el.bgShowMarks.checked : false;
    updateBgHistoryButtons();
    setBgMode('pan');
    // when re-editing, start from the settings the customer used last time so
    // they can tweak instead of redoing it from scratch
    var seedTol = (reEdit && src.bgTol != null) ? src.bgTol : BG_TOL_DEFAULT;
    el.bgTol.value = String(seedTol);
    el.bgTolVal.textContent = seedTol;
    if (reEdit && src.bgMasks && src.bgMasks.w === bw && src.bgMasks.h === bh) {
      bgKeep.set(src.bgMasks.keep);
      bgRemove.set(src.bgMasks.remove);
      if (src.bgMasks.erase) bgErase.set(src.bgMasks.erase);
      rebuildBgOverlay();
    }
    if (el.bgTitle) el.bgTitle.textContent = reEdit ? 'Edit background' : 'Remove background';
    // "Apply", no "Remove background". El titulo del dialogo ya dice "Remove
    // background" y la vista previa YA ensena el fondo quitado, asi que repetirlo en
    // el boton hace pensar que aun no ha pasado nada y que el boton lo hara otra vez.
    // Lo que hace es llevarse a la hoja lo que se esta viendo.
    if (el.bgApply) el.bgApply.textContent = 'Apply';
    recomputeBgResult();
    drawBgView();
    el.bgOverlay.classList.add('show');
  }

  function bgHasMarks() {
    if (!bgKeep) return false;
    for (var i = 0; i < bgKeep.length; i++) if (bgKeep[i] || bgRemove[i] || bgErase[i]) return true;
    return false;
  }

  // colors for the three brush marks (green keep / blue remove-white / red erase)
  var BG_MARK = {
    keep: { css: '#12b76a', rgb: [18, 183, 106] },
    remove: { css: '#2e90fa', rgb: [46, 144, 250] },
    erase: { css: '#e0402f', rgb: [224, 64, 47] }
  };

  // Re-run the flood fill at the current sensitivity + brush masks into bgResult.
  function recomputeBgResult() {
    if (!bgBase) return;
    var work = new ImageData(bgBase.w, bgBase.h);
    work.data.set(bgBase.data.data);
    if (bgAiMask) {
      applyAiAlpha(work, bgAiMask, bgKeep, bgRemove, bgErase);
    } else {
      floodClearBg(work, parseInt(el.bgTol.value, 10), bgPickColor, bgKeep, bgRemove, bgErase);
    }
    bgResult.getContext('2d').putImageData(work, 0, 0);
  }

  function bgFitScale() {
    var c = el.bgPreview;
    return Math.min(c.width / bgBase.w, c.height / bgBase.h);
  }

  function clampBgCenter() {
    bgView.cx = clamp(bgView.cx, 0, bgBase.w);
    bgView.cy = clamp(bgView.cy, 0, bgBase.h);
  }

  // Draw the checkerboard, then bgResult and the mask tint, zoomed/panned.
  function drawBgView() {
    if (!bgResult) return;
    var c = el.bgPreview, ctx = c.getContext('2d');
    var W = c.width, H = c.height;
    var sq = 8;
    for (var y = 0; y < H; y += sq) {
      for (var x = 0; x < W; x += sq) {
        ctx.fillStyle = ((x + y) / sq) % 2 ? '#eef1f5' : '#ffffff';
        ctx.fillRect(x, y, sq, sq);
      }
    }
    var eff = bgFitScale() * bgView.scale;
    clampBgCenter();
    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.clip();
    ctx.translate(W / 2, H / 2);
    ctx.scale(eff, eff);
    ctx.translate(-bgView.cx, -bgView.cy);
    ctx.imageSmoothingEnabled = bgView.scale <= 1;  // crisp pixels when zoomed in
    ctx.drawImage(bgResult, 0, 0);
    // brush marks show only while actively painting (so you always see the
    // clean result afterward), unless "Show marks" is on
    if (bgShowMarks || bgStroking) {
      ctx.globalAlpha = 0.45;
      ctx.drawImage(bgOverlayCanvas, 0, 0);          // green/red brush marks
      ctx.globalAlpha = 1;
    }
    ctx.restore();

    // Circulo del pincel, al tamano REAL que va a pintar. Va fuera del transform
    // para que el trazo del borde no se deforme con el zoom, asi que el radio se
    // convierte a mano de pixeles de imagen a pixeles de canvas.
    if (bgCursor && bgMode !== 'pan' && bgMode !== 'pick') {
      var TINTES = { keep: '#16a34a', remove: '#2f7fd4', erase: '#dc2626' };
      var col = TINTES[bgMode] || '#2f7fd4';
      var rp = Math.max(1.5, bgBrush * eff);
      ctx.save();
      // doble trazo: uno blanco por debajo, para que se vea sobre arte oscuro y claro
      ctx.beginPath(); ctx.arc(bgCursor.x, bgCursor.y, rp, 0, 7);
      ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.stroke();
      ctx.lineWidth = 1.5; ctx.strokeStyle = col; ctx.stroke();
      ctx.restore();
    }

    if (el.bgZoomVal) el.bgZoomVal.textContent = Math.round(bgView.scale * 100) + '%';
  }

  // Ensena el circulo en el centro un momento, para juzgar el tamano sin mover el
  // raton. Es lo que faltaba al arrastrar el deslizador.
  function mostrarPincelUnMomento() {
    if (!el.bgPreview || bgMode === 'pan') return;
    bgCursor = { x: el.bgPreview.width / 2, y: el.bgPreview.height / 2 };
    drawBgView();
    if (bgCursorTimer) clearTimeout(bgCursorTimer);
    bgCursorTimer = setTimeout(function () {
      bgCursor = null;
      drawBgView();
    }, 1100);
  }

  function setBgZoom(mult, pivotX, pivotY) {
    var c = el.bgPreview;
    var eff = bgFitScale() * bgView.scale;
    // world (base) point under the pivot, before zoom
    var wx = bgView.cx + (pivotX - c.width / 2) / eff;
    var wy = bgView.cy + (pivotY - c.height / 2) / eff;
    bgView.scale = clamp(bgView.scale * mult, 1, 8);
    var eff2 = bgFitScale() * bgView.scale;
    // keep that world point under the pivot after zoom
    bgView.cx = wx - (pivotX - c.width / 2) / eff2;
    bgView.cy = wy - (pivotY - c.height / 2) / eff2;
    drawBgView();
  }

  function setBgMode(mode) {
    bgMode = mode;
    [['pan', el.bgModePan], ['pick', el.bgModePick], ['keep', el.bgModeKeep],
     ['remove', el.bgModeRemove], ['erase', el.bgModeErase]].forEach(function (p) {
      if (p[1]) p[1].classList.toggle('active', bgMode === p[0]);
    });
    if (el.bgPreview) el.bgPreview.classList.toggle('painting', mode !== 'pan' && mode !== 'pick');
    if (el.bgPreview) el.bgPreview.classList.toggle('picking', mode === 'pick');
    // el circulo del pincel solo tiene sentido en los modos que pintan
    if (mode === 'pan' || mode === 'pick') { bgCursor = null; if (bgResult) drawBgView(); }
    else mostrarPincelUnMomento();
  }

  // Canvas pixel coords -> base image coords, inverting the view transform.
  function bgCanvasToBase(clientX, clientY) {
    var c = el.bgPreview, r = c.getBoundingClientRect();
    var px = (clientX - r.left) * (c.width / r.width);
    var py = (clientY - r.top) * (c.height / r.height);
    var eff = bgFitScale() * bgView.scale;
    return {
      x: bgView.cx + (px - c.width / 2) / eff,
      y: bgView.cy + (py - c.height / 2) / eff
    };
  }

  // Paint a brush dab into the active mask + the tint overlay. The three marks
  // are mutually exclusive per pixel, so painting one clears the others there.
  function bgPaint(bx, by) {
    var r = bgBrush, w = bgBase.w, h = bgBase.h;
    var mode = bgMode; // 'keep' | 'remove' | 'erase'
    var x0 = Math.max(0, Math.floor(bx - r)), x1 = Math.min(w - 1, Math.ceil(bx + r));
    var y0 = Math.max(0, Math.floor(by - r)), y1 = Math.min(h - 1, Math.ceil(by + r));
    for (var y = y0; y <= y1; y++) {
      for (var x = x0; x <= x1; x++) {
        var dx = x - bx, dy = y - by;
        if (dx * dx + dy * dy > r * r) continue;
        var idx = y * w + x;
        bgKeep[idx] = mode === 'keep' ? 1 : 0;
        bgRemove[idx] = mode === 'remove' ? 1 : 0;
        bgErase[idx] = mode === 'erase' ? 1 : 0;
      }
    }
    // update the tint overlay: erase any prior tint under the dab, then fill
    var octx = bgOverlayCanvas.getContext('2d');
    octx.save();
    octx.globalCompositeOperation = 'destination-out';
    octx.beginPath(); octx.arc(bx, by, r, 0, 7); octx.fill();
    octx.restore();
    octx.fillStyle = (BG_MARK[mode] || BG_MARK.remove).css;
    octx.beginPath(); octx.arc(bx, by, r, 0, 7); octx.fill();
  }

  function clearBgMarks() {
    if (!bgKeep) return;
    pushBgHistory();
    bgKeep.fill(0); bgRemove.fill(0); bgErase.fill(0);
    bgOverlayCanvas.getContext('2d').clearRect(0, 0, bgBase.w, bgBase.h);
    recomputeBgResult();
    drawBgView();
  }

  // --- brush history (dialog-local undo/redo of the keep/remove/erase masks) ---
  function snapBgMasks() {
    return { k: bgKeep.slice(), r: bgRemove.slice(), e: bgErase.slice() };
  }

  function pushBgHistory() {
    if (!bgKeep) return;
    bgUndoStack.push(snapBgMasks());
    if (bgUndoStack.length > BG_HISTORY_MAX) bgUndoStack.shift();
    bgRedoStack.length = 0;
    updateBgHistoryButtons();
  }

  // Rebuild the tint overlay canvas from the current masks (after undo/redo).
  function rebuildBgOverlay() {
    var w = bgBase.w, h = bgBase.h;
    var octx = bgOverlayCanvas.getContext('2d');
    var img = octx.createImageData(w, h);
    var dd = img.data;
    function tint(i, c) { dd[i * 4] = c[0]; dd[i * 4 + 1] = c[1]; dd[i * 4 + 2] = c[2]; dd[i * 4 + 3] = 255; }
    for (var i = 0; i < w * h; i++) {
      if (bgKeep[i]) tint(i, BG_MARK.keep.rgb);
      else if (bgRemove[i]) tint(i, BG_MARK.remove.rgb);
      else if (bgErase[i]) tint(i, BG_MARK.erase.rgb);
    }
    octx.putImageData(img, 0, 0);
  }

  function restoreBgMasks(state) {
    bgKeep.set(state.k);
    bgRemove.set(state.r);
    bgErase.set(state.e || new Uint8Array(bgKeep.length));
    rebuildBgOverlay();
    recomputeBgResult();
    drawBgView();
  }

  function bgUndoAction() {
    if (!bgUndoStack.length) return;
    bgRedoStack.push(snapBgMasks());
    restoreBgMasks(bgUndoStack.pop());
    updateBgHistoryButtons();
  }

  function bgRedoAction() {
    if (!bgRedoStack.length) return;
    bgUndoStack.push(snapBgMasks());
    restoreBgMasks(bgRedoStack.pop());
    updateBgHistoryButtons();
  }

  function updateBgHistoryButtons() {
    if (el.bgUndo) el.bgUndo.disabled = !bgUndoStack.length;
    if (el.bgRedo) el.bgRedo.disabled = !bgRedoStack.length;
  }

  function closeBgDialog() {
    if (el.bgOverlay) el.bgOverlay.classList.remove('show');
    bgDialogSrc = null;
    bgBase = null;
    bgResult = null;
    bgKeep = bgRemove = bgErase = null;
    bgOverlayCanvas = null;
    bgUndoStack = []; bgRedoStack = [];
    bgStroking = false;
  }

  function applyBgDialog() {
    var src = bgDialogSrc;
    var tol = parseInt(el.bgTol.value, 10);
    var masks = bgHasMarks() ? { keep: bgKeep, remove: bgRemove, erase: bgErase, w: bgBase.w, h: bgBase.h } : null;
    closeBgDialog();
    if (src) {
      removeBackground(src, tol, masks, bgPickColor,
        bgAiMask ? { a: bgAiMask, w: bgBase.w, h: bgBase.h } : null);
    }
  }


  // --------------------------------------------------------------- export
  // Yield between export strips so the progress bar can repaint. setTimeout(0)
  // looks like the obvious way, but browsers clamp timers to once per second in
  // a background tab: a 300" sheet is 176 strips, so a customer who switches
  // tabs while it renders would wait minutes instead of seconds. MessageChannel
  // is a macrotask too (the UI still gets its turn) and is not throttled.
  var tickChan = (typeof MessageChannel !== 'undefined') ? new MessageChannel() : null;
  var tickQueue = [];
  if (tickChan) {
    tickChan.port1.onmessage = function () {
      var r = tickQueue.shift();
      if (r) r();
    };
  }

  function nextTick() {
    if (!tickChan) return new Promise(function (r) { setTimeout(r, 0); });
    return new Promise(function (r) {
      tickQueue.push(r);
      tickChan.port2.postMessage(0);
    });
  }

  function downloadBlob(blob, filename) {
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // el navegador lee el archivo al empezar la descarga: 60 s sobran, y con varias hojas de 168" seguidas
    // tenerlas 5 minutos en memoria podia cerrar la pestana en un iPhone (auditoria 23-oct)
    setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
  }

  function setProgress(frac) {
    var pct = Math.round(frac * 100);
    el.progressFill.style.width = pct + '%';
    el.progressText.textContent = pct + '%';
  }

  // El overlay se reutiliza para render y para subida, y cada fase dice lo suyo.
  // Con null vuelve a los textos de fabrica, que son los del render.
  var OVERLAY_BASE = null;
  function setOverlayText(titulo, sub) {
    if (!el.overlayTitle || !el.overlaySub) return;
    if (!OVERLAY_BASE) {
      OVERLAY_BASE = { titulo: el.overlayTitle.textContent, sub: el.overlaySub.textContent };
    }
    el.overlayTitle.textContent = titulo || OVERLAY_BASE.titulo;
    el.overlaySub.textContent = sub || OVERLAY_BASE.sub;
  }

  // Reconstruye el <select> de largos para el TIPO elegido. Se llama al arrancar y en
  // cada cambio de tipo, porque cada producto vende su propia escalera: el ORGB para
  // en 96", el glitter empieza en 24", el gang sheet llega a 300".
  // separateType lo fija init(): con selector de tipo la opcion dice solo el largo;
  // sin el, mantiene la etiqueta 'ancho x largo' para que una copia vieja de
  // index.html sin selector siga leyendose bien.
  var separateType = false;

  function rebuildHeightOptions() {
    SHEET_HEIGHTS = heightsForType(currentType());
    el.sheetSelect.innerHTML = '';
    SHEET_HEIGHTS.forEach(function (h) {
      var opt = document.createElement('option');
      opt.value = h;
      opt.textContent = separateType ? h + '"' : (SHEET_W_IN + '" × ' + h + '"');
      // Agotada en la tienda: se deja elegir (armar y descargar es gratis), pero se
      // dice ya en la lista para que nadie arme una hoja que luego no puede pedir.
      var o = sheetOffer(h);
      if (o && !o.available) opt.textContent += ' (sold out)';
      el.sheetSelect.appendChild(opt);
    });
    sheetHIn = nearestHeight(sheetHIn);
    // "Auto" va primero: es el modo por defecto
    var auto = document.createElement('option');
    auto.value = 'auto';
    el.sheetSelect.insertBefore(auto, el.sheetSelect.firstChild);
    if (largoAuto) sheetHIn = largoQueToca();
    auto.textContent = textoOpcionAuto();
    el.sheetSelect.value = largoAuto ? 'auto' : String(sheetHIn);
  }

  // El ancho se ENSENA, no se elige: sale del tipo.
  function updateWidthLabel() {
    if (el.sheetWidth) el.sheetWidth.textContent = SHEET_W_IN + '" wide';
  }

  // Cuantos disenos se saldrian si la hoja pasara a medir wIn de ancho. Sirve para
  // avisar ANTES de cambiar de tipo, no despues de haber roto el trabajo del cliente.
  function offWidthCount(wIn) {
    var n = 0;
    itemNodes().forEach(function (nd) {
      if (nodeBBox(nd).maxX > wIn + OVERLAP_EPS) n++;
    });
    return n;
  }

  function setSheetHeight(h) {
    largoAuto = false;
    sheetHIn = h;
    el.sheetSelect.value = String(h);
    applyZoom(zoom);
    refreshAll();
    pushHistory();
  }

  // Nota "Sheet sizes": se arma con la propia lista de tipos, asi que no puede
  // desincronizarse del desplegable. Antes salia de una BRAND.widthGuide escrita a
  // mano que habia que acordarse de actualizar.
  function buildSheetInfo() {
    if (!el.sheetInfoList) return;
    if (!TYPES.length) {
      if (el.sheetInfoBtn) el.sheetInfoBtn.style.display = 'none';
      return;
    }
    el.sheetInfoList.innerHTML = '';
    TYPES.forEach(function (t) {
      var li = document.createElement('li');
      var nom = document.createElement('b');
      nom.textContent = t.label;
      li.appendChild(nom);
      li.appendChild(document.createTextNode(': ' + (t.widthIn || '?') + '" wide'));
      var hs = heightsForType(t);
      if (t.porTamano) {
        var spanT = document.createElement('span');
        spanT.className = 'len-range';
        spanT.textContent = ' (priced per design by size, from ' + money(t.tramosTam[0].price / t.unidadIn2).replace(/0+$/, '') + ' per sq in)';
        li.appendChild(spanT);
      } else if (t.mayoreo) {
        var spanM = document.createElement('span');
        spanM.className = 'len-range';
        spanM.textContent = ' (sold by total length: ' + (t.minTotal / 12) + ' to ' + (t.maxTotal / 12) + ' ft across sheets, from ' +
                            money(t.escalaTotal[0][1]) + ')';
        li.appendChild(spanM);
      } else if (hs.length) {
        var span = document.createElement('span');
        span.className = 'len-range';
        span.textContent = ' (' + hs[0] + '" to ' + hs[hs.length - 1] + '" long, from ' +
                           money(t.lengths[0][1]) + ')' + (t.agotado ? ', temporarily sold out' : '');
        li.appendChild(span);
      }
      el.sheetInfoList.appendChild(li);
    });
  }

  // Coloca un popover bajo su boton, sin salirse por la derecha de la ventana.
  function anclarPopover(pop, btn, desplaza) {
    var r = btn.getBoundingClientRect();
    pop.hidden = false;                       // medir con el ya visible
    var w = pop.offsetWidth;
    var izq = Math.max(8, r.left - (desplaza || 0));
    izq = Math.min(izq, Math.max(8, window.innerWidth - w - 8));
    pop.style.top = (r.bottom + 6) + 'px';
    pop.style.left = izq + 'px';
  }

  function togglePopover(pop, btn, desplaza) {
    if (!pop || !btn) return;
    var abierto = !pop.hidden;
    // solo uno abierto a la vez
    [el.sheetInfoPop, el.arrangePop, el.garmentPop].forEach(function (o) { if (o) o.hidden = true; });
    [el.sheetInfoBtn, el.arrangeSettingsBtn, el.garmentBtn].forEach(function (b) {
      if (b) b.setAttribute('aria-expanded', 'false');
    });
    if (abierto) return;
    anclarPopover(pop, btn, desplaza);
    btn.setAttribute('aria-expanded', 'true');
  }

  function toggleSheetInfo() { togglePopover(el.sheetInfoPop, el.sheetInfoBtn, 40); }

  // ---------------------------------------------------- color de prenda
  function aplicarFondo(f) {
    fondo = f;
    pintarFondoEn(el.garmentChip, 7);
    if (el.garmentBtn) {
      el.garmentBtn.title = fondoEsColor()
        ? 'Previewing on ' + fondo.c + '. The background is never printed'
        : CUADROS[fondo.i].n + ' background. Pick a garment color to preview your art on it';
    }
    if (el.garmentSwatches) {
      Array.prototype.forEach.call(el.garmentSwatches.children, function (b) {
        var suyo = b.dataset.tipo === 'color'
          ? (fondoEsColor() && b.dataset.c === fondo.c)
          : (!fondoEsColor() && +b.dataset.i === fondo.i);
        b.setAttribute('aria-pressed', suyo ? 'true' : 'false');
      });
    }
    rebuildGrid();
    updateStatusbar();
  }

  function buildGarmentSwatches() {
    if (!el.garmentSwatches) return;
    el.garmentSwatches.innerHTML = '';

    // Los cuadros van PRIMERO y son muestras como las demas: asi volver al fondo
    // transparente es un clic en el mismo sitio donde se eligio el color, y no hay
    // que buscar un boton aparte (ni recargar la pagina, que es lo que pasaba).
    CUADROS.forEach(function (g, i) {
      // los dos de inspeccion se separan: no significan "transparente", sirven para
      // cazar fondos y recortes malos, y mezclarlos con los otros confundiria
      if (i === 3) {
        var sepRev = document.createElement('span');
        sepRev.className = 'swatch-sep';
        sepRev.textContent = 'Spot leftover backgrounds';
        el.garmentSwatches.appendChild(sepRev);
      }
      var b = document.createElement('button');
      b.type = 'button';
      b.dataset.tipo = 'cuadros';
      b.dataset.i = i;
      var css = cssCuadros(g, 8);
      Object.keys(css).forEach(function (k) { b.style[k] = css[k]; });
      b.title = g.n;
      b.setAttribute('aria-label', g.n);
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function () { aplicarFondo({ tipo: 'cuadros', i: i }); });
      el.garmentSwatches.appendChild(b);
    });

    var sep = document.createElement('span');
    sep.className = 'swatch-sep';
    sep.textContent = 'Garment colors';
    el.garmentSwatches.appendChild(sep);

    COLORES_PRENDA.forEach(function (g) {
      var b = document.createElement('button');
      b.type = 'button';
      b.dataset.tipo = 'color';
      b.dataset.c = g.c;
      b.style.background = g.c;
      b.title = g.n;
      b.setAttribute('aria-label', 'Preview on ' + g.n);
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function () { aplicarFondo({ tipo: 'color', c: g.c }); });
      el.garmentSwatches.appendChild(b);
    });
  }

  // One dialog with everything worth knowing before the download, instead of
  // a chain of confirm() popups: size, usage, and every warning in one place.
  function openExportSummary() {
    if (exporting) return;
    if (!itemNodes().length && !disenosEnOtrasHojas()) { toast('Add at least one design first.'); return; }
    buildExportSummary();
    el.summaryOverlay.classList.add('show');
  }

  function closeExportSummary() {
    el.summaryOverlay.classList.remove('show');
    if (currentType() && currentType().porTamano) pintarHojas();   // el corte pudo cambiar en el recibo
  }

  // Visita cada hoja poniendola en el lienzo (sin historial) y vuelve a la de partida.
  // Asi los avisos y el render usan el mismo codigo que la hoja que se ve.
  function recorrerHojas(fn) {
    guardarHojaActual();
    var origen = hojaActual;
    var res = [];
    for (var i = 0; i < hojas.length; i++) {
      if (i !== hojaActual) { hojaActual = i; cargarHoja(hojas[i]); }
      res.push(fn(i));
      guardarHojaActual();   // el largo pudo recalcularse al cargarla
    }
    if (hojaActual !== origen) { hojaActual = origen; cargarHoja(hojas[origen]); }
    pintarHojas();
    return res;
  }

  async function recorrerHojasAsync(fn) {
    guardarHojaActual();
    var origen = hojaActual;
    var res = [];
    try {
      for (var i = 0; i < hojas.length; i++) {
        if (i !== hojaActual) { hojaActual = i; cargarHoja(hojas[i]); }
        res.push(await fn(i));
        guardarHojaActual();
      }
    } finally {
      if (hojaActual !== origen) { hojaActual = origen; cargarHoja(hojas[origen]); }
      pintarHojas();
    }
    return res;
  }

  // Tras cambiar de producto, recalcula el largo de las otras hojas con su escalera.
  function refrescarHojas() { if (hojas.length > 1) recorrerHojas(function () { return null; }); }

  // Dibuja una hoja A ESCALA (esc = px por pulgada) con su arte sobre la pelicula
  // transparente, para el resumen. Mismo dibujo que la miniatura de la columna de hojas.
  function dibujarHojaEn(cv, h, esc) {
    var w = Math.max(10, Math.round(SHEET_W_IN * esc)), hh = Math.max(10, Math.round(h.sheetH * esc));
    var dpr = window.devicePixelRatio || 1;
    cv.width = Math.round(w * dpr); cv.height = Math.round(hh * dpr);
    cv.style.width = w + 'px'; cv.style.height = hh + 'px';
    var ctx = cv.getContext('2d');
    ctx.scale(dpr, dpr);
    // pelicula transparente: cuadros suaves, como en el lienzo
    var c = Math.max(4, Math.round(esc * 0.5));
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, hh);
    ctx.fillStyle = '#eef2f6';
    for (var yy = 0; yy < hh; yy += c) {
      for (var xx = ((yy / c) % 2) * c; xx < w; xx += 2 * c) ctx.fillRect(xx, yy, c, c);
    }
    h.items.forEach(function (it) {
      var src = sources.get(it.s);
      if (!src || !src.previewCanvas) return;
      ctx.save();
      ctx.translate(it.x * esc, it.y * esc);
      ctx.rotate((it.r || 0) * Math.PI / 180);
      try { ctx.drawImage(src.previewCanvas, 0, 0, it.w * esc, it.h * esc); } catch (e) { /* aun cargando */ }
      ctx.restore();
    });
  }

  var datosResumen = [];       // una fila por hoja: lo que vio el cliente en el resumen
  var preciosEnHojas = [];     // [{ el, k }] el precio bajo cada hoja; cambia con la entrega
  var refrescarOferta = null;  // la funcion que repinta el recibo (la ponen buildOffer)


  // Caja de una pieza guardada (x, y, w, h, r), con el giro de Konva alrededor de su esquina.
  function cajaDeItem(it) {
    var a = (it.r || 0) * Math.PI / 180, c = Math.cos(a), s2 = Math.sin(a);
    var xs = [0, it.w * c, it.w * c - it.h * s2, -it.h * s2];
    var ys = [0, it.w * s2, it.w * s2 + it.h * c, it.h * c];
    var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
    var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
    return { x: it.x + minX, y: it.y + minY, w: maxX - minX, h: maxY - minY };
  }

  // Disenos de una hoja agrupados (mismo arte y misma medida): para la lista del resumen y
  // para saber cual es el que mas se repite.
  function gruposDeHoja(h) {
    var g = {};
    h.items.forEach(function (it) {
      var k = it.s + '|' + it.w.toFixed(2) + 'x' + it.h.toFixed(2);
      if (!g[k]) g[k] = { s: it.s, w: it.w, h: it.h, n: 0, it: it };
      g[k].n++;
    });
    return Object.keys(g).map(function (k) { return g[k]; });
  }

  // Disenos de varias hojas agrupados (by size cuenta las piezas de cada diseno en todo el pedido).
  function gruposDeHojas(lista) {
    var g = {};
    lista.forEach(function (h) {
      if (!h || !h.items.length) return;
      gruposDeHoja(h).forEach(function (x) {
        var k = x.s + '|' + x.w.toFixed(2) + 'x' + x.h.toFixed(2);
        if (!g[k]) g[k] = { s: x.s, w: x.w, h: x.h, n: 0 };
        g[k].n += x.n;
      });
    });
    return Object.keys(g).map(function (k) { return g[k]; });
  }

  // Cuantas copias mas del diseno 'grupo' caben en la hoja h SIN alargarla (mismo motor que
  // "Fill sheet": los disenos de la hoja son obstaculos y se permite girar).
  function huecoPara(h, grupo) {
    var eng = Packing.create({ width: SHEET_W_IN - 2 * MARGIN_IN, height: h.sheetH - 2 * MARGIN_IN, gap: getSpacing() });
    h.items.forEach(function (it) {
      var b = cajaDeItem(it);
      eng.occupy(b.x - MARGIN_IN, b.y - MARGIN_IN, b.w, b.h);
    });
    var n = 0;
    while (n < FILL_MAX && eng.place(grupo.w, grupo.h, true)) n++;
    return n;
  }

  // Llena el hueco de la hoja i con copias del diseno grupo y vuelve al resumen.
  function llenarHueco(i, grupo) {
    if (i !== hojaActual) irAHoja(i);
    var objetivo = null;
    itemNodes().forEach(function (n) {
      if (objetivo || n.getAttr('sourceId') !== grupo.s) return;
      if (Math.abs(n.width() * n.scaleX() - grupo.w) < 0.01 && Math.abs(n.height() * n.scaleY() - grupo.h) < 0.01) objetivo = n;
    });
    if (!objetivo) return;
    setSelection([objetivo]);
    fillSheet();
    setSelection([]);
    buildExportSummary();
  }

  var ICONO = {
    aviso: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d="M8 2.2 14.3 13H1.7L8 2.2z"/><path d="M8 6.6v3.1M8 11.4v.1" stroke-linecap="round"/></svg>',
    info: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="8" cy="8" r="6.2"/><path d="M8 7.2v3.9M8 4.9v.1" stroke-linecap="round"/></svg>',
    ok: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.3 8.4 6.6 11.6 12.8 4.9"/></svg>'
  };

  function buildExportSummary() {
    // by size: lo que se pide sale siempre en filas (pegar o recortar pudo dejar algo suelto)
    if (currentType() && currentType().porTamano) rehacerTamano(tarjetasTamano(), true);
    // Se visitan TODAS las hojas: los avisos valen para el pedido entero.
    datosResumen = recorrerHojas(function (i) {
      var nodes = itemNodes();
      computeOverlaps();
      var area = 0, out = 0, low = 0, str = 0;
      nodes.forEach(function (n) {
        area += (n.width() * n.scaleX()) * (n.height() * n.scaleY());
        if (isOutOfSheet(n)) out++;
        if (isStretched(n)) str++;
        var dpi = effectiveDpi(n);
        if (dpi !== Infinity && dpi < 150) low++;
      });
      return { i: i, n: nodes.length, h: sheetHIn, auto: largoAuto, area: area, out: out,
               over: overlapSet.size, low: low, str: str, fit: largoAuto ? null : smallestFittingHeight() };
    });
    computeOverlaps();   // de vuelta en la hoja de pantalla
    var conDisenos = datosResumen.filter(function (x) { return x.n; });
    var varias = conDisenos.length > 1;
    var titulo = RAIZ.getElementById('summary-title');
    // by size: sin hojas a la vista tampoco aqui; manda la lista de disenos con su precio
    var tamR = !!(currentType() && currentType().porTamano);
    if (el.summaryOverlay) el.summaryOverlay.classList.toggle('sum-tamano', tamR);
    if (titulo) titulo.textContent = tamR ? 'Review your order' : (varias ? 'Your ' + conDisenos.length + ' sheets are ready' : 'Your gang sheet is ready');

    // ---- las hojas, a escala y con una regla en pulgadas (el lenguaje de la landing)
    el.summaryRows.innerHTML = '';
    preciosEnHojas = [];
    var mayor = 1;
    conDisenos.forEach(function (x) { mayor = Math.max(mayor, x.h); });
    var movil = window.matchMedia && window.matchMedia('(max-width: 760px)').matches;
    var ALTO = movil ? 170 : 300, ANCHO_MAX = movil ? 120 : 190;
    var esc = Math.min(ALTO / mayor, ANCHO_MAX / SHEET_W_IN);
    var altoPx = Math.round(mayor * esc);

    var regla = document.createElement('div');
    regla.className = 'sum-ruler';
    regla.style.height = altoPx + 'px';
    regla.setAttribute('aria-hidden', 'true');
    var paso = [6, 12, 24, 48, 96, 192].filter(function (v) { return v * esc >= 30; })[0] || 192;
    for (var v = 0; v <= mayor + 1e-9; v += paso) {
      var marca = document.createElement('span');
      marca.style.top = Math.round(v * esc) + 'px';
      marca.textContent = v + '"';
      regla.appendChild(marca);
    }
    el.summaryRows.appendChild(regla);

    conDisenos.forEach(function (x, k) {
      var fig = document.createElement('figure');
      fig.className = 'sum-sheet';
      var marco = document.createElement('div');
      marco.className = 'sum-film';
      var cv = document.createElement('canvas');
      dibujarHojaEn(cv, hojas[x.i] || estadoHojaActual(), esc);
      marco.appendChild(cv);
      // avisos de ESTA hoja, encima de ella
      var chips = [];
      if (x.out) chips.push(['bad', x.out + ' cropped']);
      if (x.over) chips.push(['warn', x.over + ' overlap']);
      if (x.low) chips.push(['warn', x.low + ' low DPI']);
      if (x.str) chips.push(['warn', x.str + ' stretched']);
      if (chips.length) {
        var pila = document.createElement('div');
        pila.className = 'sum-chips';
        chips.forEach(function (c) {
          var ch = document.createElement('span');
          ch.className = 'sum-chip ' + c[0];
          ch.textContent = c[1];
          pila.appendChild(ch);
        });
        marco.appendChild(pila);
      }
      fig.appendChild(marco);
      var pie = document.createElement('figcaption');
      if (varias) {
        var nom = document.createElement('span');
        nom.className = 'sum-name';
        nom.textContent = 'Sheet ' + (x.i + 1);
        pie.appendChild(nom);
      }
      var med = document.createElement('span');
      med.className = 'sum-size';
      med.textContent = SHEET_W_IN + ' × ' + x.h + ' in';
      var n = document.createElement('span');
      n.className = 'sum-count';
      n.textContent = x.n + ' design' + (x.n === 1 ? '' : 's') + (x.auto ? ' · auto length' : '');
      var pr = document.createElement('strong');
      pr.className = 'sum-price';
      pie.appendChild(med); pie.appendChild(n); pie.appendChild(pr);
      preciosEnHojas.push({ el: pr, k: k, i: x.i });
      fig.appendChild(pie);
      // Copias de ESTA hoja (para quien reimprime el mismo diseno): cantidad de la linea. En
      // Wholesale cada copia suma su largo al total (y sirve para llegar al minimo de 30 ft). En by
      // size no: alli se piden piezas de cada diseno, no hojas.
      if (currentType() && currentType().porTamano) { fig.appendChild(pie); el.summaryRows.appendChild(fig); return; }
      var qty = document.createElement('div');
      qty.className = 'sum-qty';
      qty.setAttribute('role', 'group');
      qty.setAttribute('aria-label', 'Copies of sheet ' + (x.i + 1));
      var menos = document.createElement('button');
      menos.type = 'button';
      menos.setAttribute('aria-label', 'One copy less');
      menos.textContent = '−';
      var val = document.createElement('span');
      val.className = 'sum-qty-val';
      var mas = document.createElement('button');
      mas.type = 'button';
      mas.setAttribute('aria-label', 'One copy more');
      mas.textContent = '+';
      function pintarQty() {
        var c = copiasDe(x.i);
        val.textContent = '× ' + c;
        menos.disabled = c <= 1;
        qty.title = c > 1 ? c + ' copies of this sheet' : 'Need this exact sheet more than once? Add copies.';
      }
      menos.addEventListener('click', function () { ponerCopias(x.i, copiasDe(x.i) - 1); pintarQty(); pintarHojas(); scheduleSave(); if (refrescarOferta) refrescarOferta(); });
      mas.addEventListener('click', function () { ponerCopias(x.i, copiasDe(x.i) + 1); pintarQty(); pintarHojas(); scheduleSave(); if (refrescarOferta) refrescarOferta(); });
      qty.appendChild(menos); qty.appendChild(val); qty.appendChild(mas);
      pintarQty();
      pie.appendChild(qty);
      el.summaryRows.appendChild(fig);
    });

    // ---- lista de disenos: miniatura, nombre, medida, copias y calidad (DPI)
    var vieja = el.summaryRows.parentNode.querySelector('.sum-designs');
    if (vieja) vieja.remove();
    var todos = {};
    conDisenos.forEach(function (x) {
      gruposDeHoja(hojas[x.i] || estadoHojaActual()).forEach(function (g) {
        var k = g.s + '|' + g.w.toFixed(2) + 'x' + g.h.toFixed(2);
        if (!todos[k]) todos[k] = { s: g.s, w: g.w, h: g.h, n: 0 };
        todos[k].n += g.n;
      });
    });
    var filasD = Object.keys(todos).map(function (k) { return todos[k]; })
      .sort(function (a, b) { return b.n - a.n || b.w * b.h - a.w * a.h; });
    var tT = currentType();
    if (tT && tT.porTamano && filasD.length) precioPorTamano(tT, filasD, false);   // pone tramo y precio a cada fila
    if (filasD.length) {
      var lista = document.createElement('details');
      lista.className = 'sum-designs';
      if (filasD.length <= 4 || (tT && tT.porTamano)) lista.open = true;
      if (tT && tT.porTamano) lista.classList.add('sum-designs--precio');
      var sumario = document.createElement('summary');
      var piezasD = 0;
      filasD.forEach(function (g) { piezasD += g.n; });
      sumario.textContent = (tT && tT.porTamano)
        ? filasD.length + ' design' + (filasD.length === 1 ? '' : 's') + ' \u00b7 ' + piezasD + ' piece' + (piezasD === 1 ? '' : 's')
        : filasD.length === 1 ? '1 design on ' + (varias ? 'these sheets' : 'this sheet')
        : filasD.length + ' designs on ' + (varias ? 'these sheets' : 'this sheet');
      lista.appendChild(sumario);
      var ul = document.createElement('ul');
      filasD.forEach(function (g) {
        var src = sources.get(g.s);
        var li = document.createElement('li');
        var img = document.createElement('img');
        img.alt = '';
        if (src && src.thumbUrl) img.src = src.thumbUrl;
        var nom = document.createElement('span');
        nom.className = 'sd-name';
        nom.textContent = src ? src.name : 'Design';
        nom.title = nom.textContent;
        var med = document.createElement('span');
        med.className = 'sd-size';
        med.textContent = g.w.toFixed(2).replace(/\.?0+$/, '') + ' \u00d7 ' + g.h.toFixed(2).replace(/\.?0+$/, '') + ' in';
        var cop = document.createElement('span');
        cop.className = 'sd-copies';
        cop.textContent = '\u00d7 ' + g.n;
        var dpi = !src ? 0 : (src.isVector ? Infinity : Math.min(src.artW / g.w, src.artH / g.h));
        var q = document.createElement('span');
        q.className = 'sd-dpi ' + (dpi >= 300 ? 'ok' : (dpi >= 150 ? 'mid' : 'low'));
        q.textContent = dpi === Infinity ? 'Vector' : Math.round(dpi) + ' DPI';
        li.appendChild(img); li.appendChild(nom); li.appendChild(med); li.appendChild(cop); li.appendChild(q);
        if (tT && tT.porTamano && g.tramo) {
          var dsc = document.createElement('span');
          dsc.className = 'sd-tier';
          dsc.textContent = g.desc > 0 ? '\u2212' + g.desc + '%' : '';
          var pv = document.createElement('span');
          pv.className = 'sd-price';
          pv.textContent = money(g.precio);
          li.appendChild(dsc); li.appendChild(pv);
        }
        ul.appendChild(li);
      });
      lista.appendChild(ul);
      el.summaryRows.parentNode.insertBefore(lista, el.summaryWarnings);
    }

    // ---- avisos con su explicacion, debajo de las hojas
    el.summaryWarnings.innerHTML = '';
    function addWarning(text, cls) {
      var w = document.createElement('div');
      w.className = 'summary-warning ' + (cls || 'warn');
      var ic = document.createElement('span');
      ic.className = 'sw-icon';
      ic.innerHTML = cls === 'ok' ? ICONO.ok : (cls === 'info' ? ICONO.info : ICONO.aviso);
      var tx = document.createElement('span');
      tx.className = 'sw-text';
      tx.textContent = text;
      w.appendChild(ic); w.appendChild(tx);
      el.summaryWarnings.appendChild(w);
      return tx;
    }
    function enHojas(campo) {
      var total = 0, cuales = [];
      conDisenos.forEach(function (x) { if (x[campo]) { total += x[campo]; cuales.push(x.i + 1); } });
      return { n: total, txt: varias && cuales.length ? ' (sheet ' + cuales.join(', ') + ')' : '' };
    }
    var o1 = enHojas('out'), o2 = enHojas('over'), o3 = enHojas('low'), o4 = enHojas('str');
    if (o1.n) addWarning(o1.n + ' design' + (o1.n === 1 ? ' goes' : 's go') + ' past the edge and will be cut off' + o1.txt + '.', 'bad');
    if (o2.n) addWarning(o2.n + ' designs overlap' + o2.txt + '. They print merged; move them apart or group them if that is the idea.', 'warn');
    if (o3.n) addWarning(o3.n + ' design' + (o3.n === 1 ? ' is' : 's are') + ' under 150 DPI and may print pixelated' + o3.txt + '.', 'warn');
    if (o4.n) addWarning(o4.n + ' design' + (o4.n === 1 ? ' is' : 's are') + ' stretched out of shape' + o4.txt + '. Use "Stretched · fix" on the card.', 'warn');
    if (fondoEsColor()) addWarning('The garment color is only a preview. Your sheet prints on clear film.', 'info');
    if ((SHEET_W_IN * EXPORT_DPI) * (mayor * EXPORT_DPI) / 1e6 > 250) {
      addWarning('Long sheets make big files and take a minute to build. Best on a computer.', 'info');
    }
    // Largo elegido a mano y todo cabe en uno mas corto: se ofrece cambiarlo (la de pantalla).
    var actual = datosResumen[hojaActual];
    if (actual && actual.n && !actual.out && actual.fit && actual.fit < actual.h) {
      var tx = addWarning('Everything fits on a shorter ' + SHEET_W_IN + ' × ' + actual.fit + ' in sheet.', 'info');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sw-action';
      btn.textContent = 'Switch';
      btn.addEventListener('click', function () { setSheetHeight(actual.fit); buildExportSummary(); });
      tx.parentNode.appendChild(btn);
    }
    // Film que ya se paga y queda vacio: cuantas copias mas del diseno que mas se repite caben
    // sin alargar la hoja. Un clic las anade (como "Fill sheet") y vuelve aqui.
    if (!(currentType() && currentType().porTamano)) conDisenos.forEach(function (x) {
      var hh = hojas[x.i] || estadoHojaActual();
      var gs = gruposDeHoja(hh).sort(function (a, b) { return b.n - a.n || b.w * b.h - a.w * a.h; });
      if (!gs.length) return;
      var cabe = huecoPara(hh, gs[0]);
      if (cabe < 1) return;
      var src = sources.get(gs[0].s);
      var nombre = src ? src.name.replace(/\.[a-z0-9]+$/i, '') : 'this design';
      if (nombre.length > 28) nombre = nombre.slice(0, 26) + '\u2026';
      var txt = addWarning('Room for ' + cabe + ' more cop' + (cabe === 1 ? 'y' : 'ies') + ' of "' + nombre + '"' +
        (varias ? ' on sheet ' + (x.i + 1) : '') + ' at no extra cost.', 'info');
      txt.parentNode.classList.add('room');
      var bt = document.createElement('button');
      bt.type = 'button';
      bt.className = 'sw-action';
      bt.textContent = 'Add ' + cabe;
      bt.addEventListener('click', function () { llenarHueco(x.i, gs[0]); });
      txt.parentNode.appendChild(bt);
    });
    if (!o1.n && !o2.n && !o3.n && !o4.n) {
      var lleno = conDisenos.length === 1 ? Math.min(999, Math.round(100 * conDisenos[0].area / (SHEET_W_IN * conDisenos[0].h))) : null;
      addWarning(lleno !== null && lleno >= 90
        ? 'Nicely packed: ' + lleno + '% of the sheet is used. Ready to print.'
        : (varias ? 'No issues found. The sheets are ready to print.' : 'No issues found. Ready to print.'), 'ok');
    }

    buildOffer(conDisenos);
  }

  // Textos de Easify en las fichas ("Your artwork"), adaptados a una hoja armada aqui.
  var ARTE = {
    asis: { titulo: 'Print As Is', nota: 'Goes straight to the printer, exactly as you built it.' },
    review: { titulo: 'Artwork Review', nota: 'We check it and email you if anything needs fixing before it prints.' }
  };

  // Nota CORTA del same-day para la ficha de entrega (la larga queda en el title).
  function notaCortaSameDay() {
    var t = ahoraET();
    if (!t) return 'Order by 12 PM ET, Mon to Fri';
    var habil = t.dia >= 1 && t.dia <= 5;
    if (habil && t.min < CORTE_ENVIO) {
      var f = CORTE_ENVIO - t.min, hh = Math.floor(f / 60), mm = f % 60;
      return 'Ships today · ' + (hh ? hh + 'h ' + mm + 'm' : mm + ' min') + ' left';
    }
    if (habil && t.min < CORTE_RECOGIDA) return 'Pickup today in Kendall · until 3 PM ET';
    return 'Order by 12 PM ET ' + NOMBRE_DIA[proximoHabil(t.dia)].slice(0, 3) + ' · ships that day';
  }

  function notaCortaEstandar(t) {
    if (t && t.plazo) return t.plazo.replace(/\.$/, '');
    return 'Ships within 24 hours, Monday to Friday';   // redaccion unica (brand-qa 7)
  }

  // El recibo: entrega, arte, aprobacion, total y boton. La descarga sigue gratis y a mano.
  function buildOffer(conDisenos) {
    if (!el.summaryOffer) return;
    refrescarOferta = null;   // sin oferta (agotado, sin precio) las copias no repintan nada
    el.summaryOffer.innerHTML = '';
    var varias = conDisenos.length > 1;
    el.summaryDownload.textContent = varias ? 'Download PNGs' : 'Download PNG';
    el.summaryDownload.className = 'sum-download sum-download--solo';
    if (!conDisenos.length) return;

    // El total se recalcula con las copias de cada hoja (cambian sin rehacer el recibo).
    function totalDe(op) {
      if (op.mayoreo) return op.ofertas[0].price;   // un precio por el largo total
      var t2 = 0;
      op.ofertas.forEach(function (o, k) { t2 += o.price * copiasDe(conDisenos[k].i); });
      return Math.round(t2 * 100) / 100;
    }
    function opcion(tipo, ofertas, titulo, nota, notaLarga) {
      if (!tipo || ofertas.some(function (o) { return !o; })) return null;
      var op = { tipo: tipo, ofertas: ofertas, titulo: titulo, nota: nota,
                 notaLarga: notaLarga || nota, available: ofertas.every(function (o) { return o.available; }) };
      op.total = totalDe(op);
      return op;
    }
    var t = currentType();
    if (t && t.porTamano) { reciboPorTamano(t, conDisenos); return; }
    if (t && t.mayoreo) {
      // Las copias cambian el largo total y por tanto la variante: el recibo se rehace entero.
      refrescarOferta = function () { buildOffer(conDisenos); };
      var totalIn = 0, base = 0;
      conDisenos.forEach(function (x) { totalIn += x.h * copiasDe(x.i); base += x.h; });
      var ftTxt = (totalIn / 12).toFixed(1).replace(/\.0$/, '') + ' ft (' + Math.round(totalIn) + ' in)';
      preciosEnHojas.forEach(function (pp) { pp.el.textContent = ''; });
      var avisoM = null;
      if (totalIn < t.minTotal - 1e-9) {
        var falta = t.minTotal - totalIn;
        avisoM = 'Wholesale starts at ' + (t.minTotal / 12) + ' ft. Your sheets add up to ' + ftTxt + ': add about ' +
          Math.ceil(falta / 12) + ' ft more designs, or pick Custom DTF Gang Sheets in the product menu.';
      } else if (totalIn > t.maxTotal + 1e-9) {
        avisoM = 'Your sheets add up to ' + ftTxt + ', more than ' + (t.maxTotal / 12) + ' ft. Split it into two orders or contact us.';
      }
      var oM = avisoM ? null : ofertaMayoreo(t, totalIn);
      if (oM && !oM.available) avisoM = t.label + ' in ' + (oM.facturadoIn / 12) + ' ft is temporarily sold out.';
      if (avisoM) {
        // Por debajo del minimo: un clic pone las copias justas de cada hoja para llegar a el.
        var accionM = null;
        if (totalIn < t.minTotal - 1e-9 && base > 0) {
          var kM = Math.ceil((t.minTotal - 1e-9) / base);
          var oK = base * kM <= t.maxTotal + 1e-9 ? ofertaMayoreo(t, base * kM) : null;
          if (oK && oK.available) {
            accionM = document.createElement('button');
            accionM.type = 'button';
            accionM.className = 'btn btn-primary offer-btn';
            accionM.textContent = 'Add copies to reach ' + (t.minTotal / 12) + ' ft \u00b7 \u00d7 ' + kM + ' = ' +
              (base * kM / 12).toFixed(1).replace(/\.0$/, '') + ' ft \u00b7 ' + money(oK.price);
            accionM.addEventListener('click', function () {
              conDisenos.forEach(function (x) { ponerCopias(x.i, kM); });
              pintarHojas();
              buildExportSummary();
            });
          }
        }
        var cabM = document.createElement('div');
        cabM.className = 'rc-head';
        var prodM = document.createElement('h3');
        prodM.textContent = t.label;
        var quienM = document.createElement('p');
        quienM.textContent = 'Printed and shipped by DTF Transfers Now';
        cabM.appendChild(prodM); cabM.appendChild(quienM);
        el.summaryOffer.appendChild(cabM);
        var pM = document.createElement('p');
        pM.className = 'offer-soldout';
        pM.textContent = avisoM + ' You can still download your sheets for free.';
        el.summaryOffer.appendChild(pM);
        if (accionM) {
          el.summaryOffer.appendChild(accionM);
          el.summaryDownload.className = 'sum-download';
          el.summaryDownload.textContent = (conDisenos.length > 1 ? 'Download the PNGs' : 'Download the PNG') + ' only \u00b7 free';
        }
        return;
      }
      var std = { tipo: t, ofertas: [oM], total: oM.price, titulo: 'Wholesale', mayoreo: true,
                  nota: 'Your sheets: ' + ftTxt + ' · billed as ' + (oM.facturadoIn / 12) + ' ft · ' + notaCortaEstandar(t),
                  notaLarga: plazoDe(t), available: true };
      var sd = null;
    } else {
    var std = opcion(t, conDisenos.map(function (x) { return ofertaHoja(t, x.h); }),
                     t && t.sameDay ? 'Same-day' : 'Standard',
                     t && t.sameDay ? notaCortaSameDay() : notaCortaEstandar(t), plazoDe(t));
    var sdT = sameDayType();
    var sd = sdT ? opcion(sdT, conDisenos.map(function (x) { return sameDayOffer(x.h); }), 'Same-day',
                          notaCortaSameDay(), estadoSameDay().nota) : null;
    }

    var cab = document.createElement('div');
    cab.className = 'rc-head';
    var prod = document.createElement('h3');
    prod.textContent = t ? t.label : '';
    var quien = document.createElement('p');
    quien.textContent = 'Printed and shipped by DTF Transfers Now';
    cab.appendChild(prod); cab.appendChild(quien);
    el.summaryOffer.appendChild(cab);

    function pintarPreciosHojas(op) {
      if (op && op.mayoreo) {   // en mayoreo cada hoja es parte del total
        preciosEnHojas.forEach(function (p) {
          var c = copiasDe(p.i);
          p.el.textContent = conDisenos[p.k].h + ' in' + (c > 1 ? ' \u00d7 ' + c : '') + ' of ' + op.ofertas[0].facturadoIn;
        });
        return;
      }
      preciosEnHojas.forEach(function (p) {
        var o = op && op.ofertas[p.k];
        var c = copiasDe(p.i);
        p.el.textContent = o ? (c > 1 ? money(o.price) + ' \u00d7 ' + c + ' = ' + money(Math.round(o.price * c * 100) / 100) : money(o.price)) : '';
      });
    }

    if (!std && !sd) { pintarPreciosHojas(null); return; }   // sin precio verificado: solo descarga
    var opciones = [std, sd].filter(Boolean);
    if (opciones.length === 1 && !opciones[0].available) {
      pintarPreciosHojas(opciones[0]);
      var agot = document.createElement('p');
      agot.className = 'offer-soldout';
      agot.textContent = (t ? t.label : 'This product') + ' is temporarily sold out' +
        (varias ? ' in one of these lengths' : ' in this length') + '. You can still download your sheet for free.';
      el.summaryOffer.appendChild(agot);
      return;
    }
    opciones = opciones.filter(function (o) { return o.available; });
    var barata = opciones[0];
    opciones.forEach(function (o) { if (o.total < barata.total) barata = o; });
    var elegida = barata;
    var arte = 'asis';

    // ---- entrega
    var gEnt = document.createElement('div');
    gEnt.className = 'rc-group';
    gEnt.setAttribute('role', 'radiogroup');
    gEnt.setAttribute('aria-label', 'Delivery');
    var lEnt = document.createElement('span');
    lEnt.className = 'rc-label';
    lEnt.textContent = 'Delivery';
    gEnt.appendChild(lEnt);
    var tiles = document.createElement('div');
    tiles.className = 'rc-tiles' + (opciones.length === 1 ? ' rc-tiles--one' : '');
    var botonesEnt = [];
    opciones.forEach(function (o) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rc-tile';
      if (o.tipo.sameDay) b.setAttribute('data-sd', '1');
      b.setAttribute('role', 'radio');
      b.title = o.notaLarga;
      var tt = document.createElement('span');
      tt.className = 'rc-tile-title';
      tt.textContent = o.titulo;
      var pr = document.createElement('span');
      pr.className = 'rc-tile-price';
      // El precio TOTAL de cada forma de imprimirla, no la diferencia: el cliente compara dos
      // precios de un vistazo, sin tener que sumar (pedido del usuario, 21-oct-2026).
      pr.textContent = money(o.total);
      var nt = document.createElement('span');
      nt.className = 'rc-tile-note';
      nt.textContent = o.nota;
      b.appendChild(tt); b.appendChild(pr); b.appendChild(nt);
      b.addEventListener('click', function () { elegida = o; refrescar(); });
      botonesEnt.push({ b: b, o: o });
      tiles.appendChild(b);
    });
    gEnt.appendChild(tiles);
    el.summaryOffer.appendChild(gEnt);

    // ---- arte: control segmentado, como en la ficha (Print As Is / Artwork Review)
    var gArte = document.createElement('div');
    gArte.className = 'rc-group';
    var lArte = document.createElement('span');
    lArte.className = 'rc-label';
    lArte.textContent = 'Your artwork';
    gArte.appendChild(lArte);
    var seg = document.createElement('div');
    seg.className = 'rc-seg';
    seg.setAttribute('role', 'radiogroup');
    seg.setAttribute('aria-label', 'Your artwork');
    var botonesArte = {};
    ['asis', 'review'].forEach(function (k) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.className = 'rc-seg-btn';
      var nom = document.createElement('span');
      nom.textContent = ARTE[k].titulo;
      var gr = document.createElement('em');
      gr.textContent = 'Free';
      b.appendChild(nom); b.appendChild(gr);
      b.addEventListener('click', function () { arte = k; refrescar(); });
      botonesArte[k] = b;
      seg.appendChild(b);
    });
    gArte.appendChild(seg);
    var notaArte = document.createElement('p');
    notaArte.className = 'rc-note';
    gArte.appendChild(notaArte);
    el.summaryOffer.appendChild(gArte);

    var aprueba = document.createElement('label');
    aprueba.className = 'offer-approve';
    var caja = document.createElement('input');
    caja.type = 'checkbox';
    var textoAprueba = document.createElement('span');
    aprueba.appendChild(caja); aprueba.appendChild(textoAprueba);
    caja.addEventListener('change', refrescar);
    el.summaryOffer.appendChild(aprueba);

    // ---- total
    var tot = document.createElement('div');
    tot.className = 'rc-total';
    var tl = document.createElement('span');
    tl.className = 'rc-total-label';
    var tlb = document.createElement('b');
    tlb.textContent = 'Total';
    var tls = document.createElement('small');
    var tlp = document.createElement('small');
    tlp.className = 'rc-perprint';
    tl.appendChild(tlb); tl.appendChild(tlp); tl.appendChild(tls);
    var tv = document.createElement('span');
    tv.className = 'rc-total-value';
    tot.appendChild(tl); tot.appendChild(tv);
    el.summaryOffer.appendChild(tot);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-primary offer-btn';
    el.summaryOffer.appendChild(btn);

    var anclas = document.createElement('p');
    anclas.className = 'offer-anchors';
    anclas.textContent = 'No minimums · No setup fees · If we misprint it, we reprint it free';
    el.summaryOffer.appendChild(anclas);

    function refrescar() {
      // Same-Day se imprime tal cual (su regla); Wide 30 cobra la revision y aqui no se puede.
      var conRevision = elegida.tipo.revision !== false && !elegida.tipo.sameDay;
      if (!conRevision) arte = 'asis';
      botonesEnt.forEach(function (x) {
        var on = x.o === elegida;
        x.b.classList.toggle('on', on);
        x.b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      Object.keys(botonesArte).forEach(function (k) {
        var on = k === arte;
        botonesArte[k].classList.toggle('on', on);
        botonesArte[k].setAttribute('aria-checked', on ? 'true' : 'false');
      });
      botonesArte.review.hidden = !conRevision;
      seg.classList.toggle('rc-seg--one', !conRevision);
      notaArte.textContent = conRevision ? ARTE[arte].nota : ARTE.asis.nota + ' Same-day sheets always print as built.';
      aprueba.hidden = arte !== 'asis';
      textoAprueba.textContent = elegida.ofertas[0].approve;
      opciones.forEach(function (o) { o.total = totalDe(o); });
      botonesEnt.forEach(function (x) { x.b.querySelector('.rc-tile-price').textContent = money(x.o.total); });
      var hojasTot = 0, piezas = 0;
      conDisenos.forEach(function (x) { var c = copiasDe(x.i); hojasTot += c; piezas += x.n * c; });
      tls.textContent = (hojasTot > 1 ? hojasTot + ' sheets · ' : '') + 'shipping and taxes at checkout';
      // Lo que decide un revendedor: cuanto le sale cada pieza impresa.
      tlp.textContent = piezas > 1 ? '\u2248 ' + money(elegida.total / piezas) + ' per print · ' + piezas + ' prints' : '';
      tv.textContent = money(elegida.total);
      btn.textContent = 'Add to cart · ' + money(elegida.total);
      btn.disabled = arte === 'asis' && !caja.checked;
      pintarPreciosHojas(elegida);
    }
    refrescarOferta = std && std.mayoreo ? function () { buildOffer(conDisenos); } : refrescar;
    refrescar();
    // La promesa de Same-Day se calcula al abrir el resumen; si el corte de las 12 PM / 3 PM ET pasa
    // con el resumen abierto, al pulsar se rehace en vez de pedir con el texto viejo (auditoria 23-oct).
    var sameDayAlAbrir = estadoSameDay().titulo;

    btn.addEventListener('click', function () {
      if (arte === 'asis' && !caja.checked) return;
      if ((elegida === sd || (t && t.sameDay)) && estadoSameDay().titulo !== sameDayAlAbrir) {
        buildExportSummary();
        toast('The same-day cutoff just changed. Please check the delivery before ordering.');
        return;
      }
      if (!checkoutDisponible()) {
        // OJO con el orden: la pestana se abre DENTRO del clic o el bloqueador la mata.
        window.open(elegida.ofertas[0].url, '_blank', 'noopener');
        closeExportSummary();
        toast(currentType() && currentType().porTamano ? 'Order page open. Your print file is downloading now, upload it there.' : 'Order page open. Your sheet is downloading now, upload it there.');
        runExport();
        return;
      }
      closeExportSummary();
      pedirYPagar(elegida, arte, conDisenos);
    });

    // con oferta, la descarga pasa a enlace secundario, siempre gratis
    el.summaryDownload.className = 'sum-download';
    el.summaryDownload.textContent = (varias ? 'Download the PNGs' : 'Download the PNG') + ' only · free';
  }

  // Recibo BY SIZE: precio por diseno (con su tramo), corte opcional, arte, aprobacion y total.
  function reciboPorTamano(t, conDisenos) {
    var lista = conDisenos.map(function (x) { return hojas[x.i] || estadoHojaActual(); });
    var grupos = gruposDeHojas(lista);
    var conCorte = corteTamano, arte = 'asis';
    var cab = document.createElement('div');
    cab.className = 'rc-head';
    var prod = document.createElement('h3');
    prod.textContent = t.label;
    var quien = document.createElement('p');
    quien.textContent = 'Printed and shipped by DTF Transfers Now';
    cab.appendChild(prod); cab.appendChild(quien);
    el.summaryOffer.appendChild(cab);
    preciosEnHojas.forEach(function (pp) { pp.el.textContent = ''; });
    if (t.agotado) {
      var ag = document.createElement('p');
      ag.className = 'offer-soldout';
      ag.textContent = t.label + ' is temporarily sold out. You can still download your sheets for free.';
      el.summaryOffer.appendChild(ag);
      return;
    }
    var base = t.tramosTam[0].price / t.unidadIn2;
    var maxDesc = Math.round((1 - t.tramosTam[t.tramosTam.length - 1].price / t.tramosTam[0].price) * 100);
    var nota = document.createElement('p');
    nota.className = 'rc-note rc-note--top';
    nota.textContent = 'Priced by size: ' + money(base).replace(/0+$/, '') + ' per sq in, less up to ' + maxDesc + '% from ' +
      (t.tramosTam[1] ? t.tramosTam[1].min : 15) + ' pieces of the same design. The builder lays them out on sheets for printing.';
    el.summaryOffer.appendChild(nota);

    // corte: ficha que se activa y desactiva
    var gCorte = null, bCorte = null;
    if (t.corte) {
      gCorte = document.createElement('div');
      gCorte.className = 'rc-group';
      var lC = document.createElement('span');
      lC.className = 'rc-label';
      lC.textContent = 'Finishing';
      gCorte.appendChild(lC);
      bCorte = document.createElement('button');
      bCorte.type = 'button';
      bCorte.className = 'rc-tile rc-tile--toggle';
      bCorte.setAttribute('aria-pressed', 'false');
      var tC = document.createElement('span');
      tC.className = 'rc-tile-title';
      tC.textContent = 'Precut each design';
      var pC = document.createElement('span');
      pC.className = 'rc-tile-price';
      pC.textContent = '+' + money(t.corte.price) + ' / piece';
      var nC = document.createElement('span');
      nC.className = 'rc-tile-note';
      nC.textContent = 'Cut out and ready to press. Adds 24 hours: ships within 48 hours, Monday to Friday.';
      bCorte.appendChild(tC); bCorte.appendChild(pC); bCorte.appendChild(nC);
      bCorte.addEventListener('click', function () { conCorte = !conCorte; corteTamano = conCorte; refrescar(); });
      gCorte.appendChild(bCorte);
      el.summaryOffer.appendChild(gCorte);
    }

    var gArte = document.createElement('div');
    gArte.className = 'rc-group';
    var lArte = document.createElement('span');
    lArte.className = 'rc-label';
    lArte.textContent = 'Your artwork';
    gArte.appendChild(lArte);
    var seg = document.createElement('div');
    seg.className = 'rc-seg';
    seg.setAttribute('role', 'radiogroup');
    seg.setAttribute('aria-label', 'Your artwork');
    var botones = {};
    ['asis', 'review'].forEach(function (k) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.className = 'rc-seg-btn';
      var nom = document.createElement('span');
      nom.textContent = ARTE[k].titulo;
      var gr = document.createElement('em');
      gr.textContent = 'Free';
      b.appendChild(nom); b.appendChild(gr);
      b.addEventListener('click', function () { arte = k; refrescar(); });
      botones[k] = b;
      seg.appendChild(b);
    });
    gArte.appendChild(seg);
    var notaArte = document.createElement('p');
    notaArte.className = 'rc-note';
    gArte.appendChild(notaArte);
    el.summaryOffer.appendChild(gArte);

    var aprueba = document.createElement('label');
    aprueba.className = 'offer-approve';
    var caja = document.createElement('input');
    caja.type = 'checkbox';
    var tA = document.createElement('span');
    tA.textContent = t.approve || APROBACION_TAMANO;
    aprueba.appendChild(caja); aprueba.appendChild(tA);
    caja.addEventListener('change', function () { refrescar(); });
    el.summaryOffer.appendChild(aprueba);

    var tot = document.createElement('div');
    tot.className = 'rc-total';
    var tl = document.createElement('span');
    tl.className = 'rc-total-label';
    var tlb = document.createElement('b');
    tlb.textContent = 'Total';
    var tlp = document.createElement('small');
    tlp.className = 'rc-perprint';
    var tls = document.createElement('small');
    tl.appendChild(tlb); tl.appendChild(tlp); tl.appendChild(tls);
    var tv = document.createElement('span');
    tv.className = 'rc-total-value';
    tot.appendChild(tl); tot.appendChild(tv);
    el.summaryOffer.appendChild(tot);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-primary offer-btn';
    el.summaryOffer.appendChild(btn);
    var anclas = document.createElement('p');
    anclas.className = 'offer-anchors';
    anclas.textContent = 'No minimums · No setup fees · If we misprint it, we reprint it free';
    el.summaryOffer.appendChild(anclas);

    var calc = null;
    function refrescar() {
      calc = precioPorTamano(t, grupos, conCorte);
      if (bCorte) {
        bCorte.classList.toggle('on', conCorte);
        bCorte.setAttribute('aria-pressed', conCorte ? 'true' : 'false');
      }
      Object.keys(botones).forEach(function (k) {
        botones[k].classList.toggle('on', k === arte);
        botones[k].setAttribute('aria-checked', k === arte ? 'true' : 'false');
      });
      notaArte.textContent = ARTE[arte].nota;
      aprueba.hidden = arte !== 'asis';
      tlp.textContent = calc.piezas > 1 ? '\u2248 ' + money(calc.total / calc.piezas) + ' per print · ' + calc.piezas + ' prints' : '';
      tls.textContent = (conCorte ? 'Ships within 48 hours, Monday to Friday' : notaCortaEstandar(t)) + ' · shipping and taxes at checkout';
      tv.textContent = money(calc.total);
      btn.textContent = 'Add to cart · ' + money(calc.total);
      btn.disabled = !calc.disponible || (arte === 'asis' && !caja.checked);
    }
    refrescarOferta = refrescar;
    refrescar();
    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      if (!checkoutDisponible()) {
        window.open((BRAND.store || 'https://dtftransfersnow.com') + t.url, '_blank', 'noopener');
        closeExportSummary();
        toast(currentType() && currentType().porTamano ? 'Order page open. Your print file is downloading now, upload it there.' : 'Order page open. Your sheet is downloading now, upload it there.');
        runExport();
        return;
      }
      closeExportSummary();
      pedirYPagar({ tipo: t, porTamano: true, calc: calc, conCorte: conCorte, total: calc.total,
                    ofertas: [{ subida: t.subida || 'file', approve: t.approve || APROBACION_TAMANO,
                                url: (BRAND.store || 'https://dtftransfersnow.com') + t.url }] }, arte, conDisenos);
    });
    el.summaryDownload.className = 'sum-download';
    el.summaryDownload.textContent = 'Download the print layout only (PNG) · free';
  }

  function nombreArchivo(i, n) {
    // La marca viaja en el nombre: este archivo se reenvia por correo y por WhatsApp.
    return 'dtftransfersnow-gangsheet-' + SHEET_W_IN + 'x' + sheetHIn + '-300dpi' +
      (n > 1 ? '-sheet' + (i + 1) : '') + '.png';
  }

  // Renderiza la hoja a PNG y devuelve el blob. NO descarga, NO toca el overlay y NO
  // marca 'exporting': de eso se encarga quien lo llama. Existe separado porque el
  // mismo render sirve para dos finales distintos, descargar el archivo o mandarlo
  // con el pedido, y no queremos dos copias del bucle de tiras.
  async function renderSheetBlob() {
    var nodes = itemNodes();
    if (!nodes.length) throw new Error('nothing to export');

    var W = SHEET_W_IN * EXPORT_DPI;   // 6600
    var H = sheetHIn * EXPORT_DPI;
    var enc = PngEncoder.create({ width: W, height: H, dpi: EXPORT_DPI, software: BRAND.software });

    // precompute the draw list in print pixels; the source rectangle is the
    // trimmed (visible) part of the original file
    var drawList = nodes.map(function (n) {
      var src = sources.get(n.getAttr('sourceId'));
      var b = nodeBBox(n);
      return {
        img: src.printSrc || src.imgEl,   // el procesado manda; si no hay, el original
        sx: src.artX, sy: src.artY, sw: src.artW, sh: src.artH,
        x: n.x() * EXPORT_DPI,
        y: n.y() * EXPORT_DPI,
        w: n.width() * n.scaleX() * EXPORT_DPI,
        h: n.height() * n.scaleY() * EXPORT_DPI,
        rad: Konva.getAngle(n.rotation()),
        minYPx: b.minY * EXPORT_DPI,
        maxYPx: b.maxY * EXPORT_DPI
      };
    });

    var strip = document.createElement('canvas');
    strip.width = W;
    strip.height = EXPORT_STRIP_ROWS;
    var ctx = strip.getContext('2d', { willReadFrequently: true });

    for (var y0 = 0; y0 < H; y0 += EXPORT_STRIP_ROWS) {
      if (exportCancelled) throw new Error('cancelled');
      var rows = Math.min(EXPORT_STRIP_ROWS, H - y0);
      ctx.clearRect(0, 0, W, EXPORT_STRIP_ROWS);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      for (var i = 0; i < drawList.length; i++) {
        var d = drawList[i];
        if (d.maxYPx <= y0 || d.minYPx >= y0 + rows) continue;
        ctx.save();
        ctx.translate(d.x, d.y - y0);
        ctx.rotate(d.rad);
        ctx.drawImage(d.img, d.sx, d.sy, d.sw, d.sh, 0, 0, d.w, d.h);
        ctx.restore();
      }

      var data = ctx.getImageData(0, 0, W, rows).data;
      await enc.addRows(data, rows);
      setProgress((y0 + rows) / H);
      await nextTick();
    }

    return await enc.finish();
  }

  // GUIA DE CORTE (21-oct-2026, pedido del usuario): con corte, un PNG aparte del MISMO tamano y DPI
  // que la hoja, transparente, con un recuadro rojo por pieza y la etiqueta PRECUT. Del mismo tamano
  // para que en Photoshop caiga justo encima del archivo de impresion. Cada recuadro va a media
  // separacion del diseno, asi que dos piezas vecinas comparten la linea: un solo corte las separa.
  // Es solo una guia para la oficina: no pide espacio extra en la hoja (decision del usuario), asi
  // que la etiqueta va en el margen, arriba o abajo, donde haya mas sitio. No es para imprimir.
  function nombreGuia(i, n) {
    return 'PRECUT-cut-guide-' + SHEET_W_IN + 'x' + sheetHIn + (n > 1 ? '-sheet' + (i + 1) : '') + '-not-for-printing.png';
  }

  async function renderGuiaCorteBlob(k, n) {
    var nodes = itemNodes();
    if (!nodes.length) throw new Error('nothing to export');
    var W = SHEET_W_IN * EXPORT_DPI;
    var H = sheetHIn * EXPORT_DPI;
    var enc = PngEncoder.create({ width: W, height: H, dpi: EXPORT_DPI, software: BRAND.software });
    var media = getSpacing() / 2 * EXPORT_DPI;
    var GROSOR = 6;   // 0.02" a 300 DPI
    var cajas = nodes.map(function (nd) {
      var b = nodeBBox(nd);
      return { x: b.minX * EXPORT_DPI - media, y: b.minY * EXPORT_DPI - media,
               w: b.w * EXPORT_DPI + 2 * media, h: b.h * EXPORT_DPI + 2 * media };
    });
    // la etiqueta, en el margen con mas sitio (arriba o abajo de los recuadros)
    var arriba = Infinity, abajo = -Infinity;
    cajas.forEach(function (c) { arriba = Math.min(arriba, c.y); abajo = Math.max(abajo, c.y + c.h); });
    var hueco = Math.max(arriba, H - abajo) - GROSOR - 6;
    var letra = Math.max(14, Math.min(60, Math.floor(hueco)));
    var yEtiqueta = arriba >= H - abajo ? Math.max(2, (arriba - GROSOR - letra) / 2)
      : abajo + GROSOR + Math.max(2, (H - abajo - GROSOR - letra) / 2);
    var etiqueta = 'PRECUT · cut on the red lines · ' + nodes.length + ' piece' + (nodes.length === 1 ? '' : 's') +
      (n > 1 ? ' · sheet ' + (k + 1) + ' of ' + n : '') + ' · cut guide only, do not print on film';
    var strip = document.createElement('canvas');
    strip.width = W;
    strip.height = EXPORT_STRIP_ROWS;
    var ctx = strip.getContext('2d', { willReadFrequently: true });
    for (var y0 = 0; y0 < H; y0 += EXPORT_STRIP_ROWS) {
      if (exportCancelled) throw new Error('cancelled');
      var rows = Math.min(EXPORT_STRIP_ROWS, H - y0);
      ctx.clearRect(0, 0, W, EXPORT_STRIP_ROWS);
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = GROSOR;
      for (var i = 0; i < cajas.length; i++) {
        var c = cajas[i];
        if (c.y + c.h + GROSOR < y0 || c.y - GROSOR > y0 + rows) continue;
        ctx.strokeRect(c.x, c.y - y0, c.w, c.h);
      }
      if (yEtiqueta + letra >= y0 && yEtiqueta < y0 + rows) {
        ctx.fillStyle = '#ff0000';
        ctx.font = 'bold ' + letra + 'px Arial, Helvetica, sans-serif';
        ctx.textBaseline = 'top';
        ctx.fillText(etiqueta, MARGIN_IN * EXPORT_DPI, yEtiqueta - y0);
      }
      var data = ctx.getImageData(0, 0, W, rows).data;
      await enc.addRows(data, rows);
      setProgress((y0 + rows) / H);
      await nextTick();
    }
    return await enc.finish();
  }

  // Envuelve una tarea larga con el overlay de progreso. Un unico sitio pone y quita
  // el overlay, asi no puede quedarse pegado si la tarea revienta a mitad.
  async function conOverlay(titulo, sub, tarea, sePuedeCancelar) {
    exporting = true;
    exportCancelled = false;
    updateToolbarState();
    setProgress(0);
    setOverlayText(titulo, sub);
    // Un boton Cancel que no cancela nada es peor que no tenerlo: solo se ensena
    // cuando la tarea de verdad mira exportCancelled (el render del PNG lo hace;
    // recolocar los disenos, no, porque es un solo trabajo del navegador).
    if (el.cancelExportBtn) el.cancelExportBtn.hidden = (sePuedeCancelar === false);
    try {
      el.overlay.classList.add('show');
      return await tarea();
    } finally {
      exporting = false;
      el.overlay.classList.remove('show');
      setOverlayText(null, null);
      if (el.cancelExportBtn) el.cancelExportBtn.hidden = false;
      updateToolbarState();
    }
  }

  function fallo(err, queHacia) {
    if (err && err.message === 'cancelled') { toast(queHacia + ' cancelled.'); return; }
    console.error(err);
    window.alert(queHacia + ' failed: ' + (err && err.message ? err.message : err));
  }

  // Descarga gratis: un PNG por hoja con disenos.
  async function runExport() {
    if (exporting) return;
    guardarHojaActual();
    var conDisenos = [];
    hojas.forEach(function (h, i) { if (h && h.items.length) conDisenos.push(i); });
    if (!conDisenos.length) return;
    try {
      var hechos = await conOverlay(null, null, function () {
        return recorrerHojasAsync(async function (i) {
          if (conDisenos.indexOf(i) === -1) return null;
          var k = conDisenos.indexOf(i);
          if (conDisenos.length > 1) setOverlayText('Building sheet ' + (k + 1) + ' of ' + conDisenos.length, null);
          var blob = await renderSheetBlob();
          var filename = nombreArchivo(k, conDisenos.length);
          downloadBlob(blob, filename);
          return { filename: filename, mb: blob.size / 1048576 };
        });
      });
      var listos = hechos.filter(Boolean);
      toast(listos.length > 1
        ? 'Done. ' + listos.length + ' PNG files at 300 DPI. If your browser asks, allow multiple downloads.'
        : 'Done. ' + listos[0].filename + ' (' + listos[0].mb.toFixed(1) + ' MB, 300 DPI)');
    } catch (err) {
      fallo(err, 'The download');
    }
  }

  // ------------------------------------------------- ya esta en el carrito
  // El carrito de Shopify es la bandeja: guarda cada hoja con su arte enlazado, suma
  // el total y sobrevive a recargas. No hacia falta construir una a mano.
  function mostrarAnadida(op, n) {
    if (!el.addedOverlay) { location.href = '/cart'; return; }
    el.addedWhat.textContent = op.tipo.label + ' · ' +
      (op.porTamano ? op.calc.lineas.length + ' design' + (op.calc.lineas.length === 1 ? '' : 's') + ', ' + op.calc.piezas + ' piece' + (op.calc.piezas === 1 ? '' : 's')
        : n > 1 ? n + ' sheets' : SHEET_W_IN + '" × ' + datosResumen.filter(function (x) { return x.n; })[0].h + '"') +
      ' · ' + money(op.total);
    // by size no habla de hojas: se siguen anadiendo disenos al mismo carrito
    var nota = RAIZ.getElementById('added-note');
    if (nota) {
      nota.textContent = 'Your artwork is attached to the order, so there is nothing else to upload. ' +
        (op.porTamano ? 'Add more designs and they join the same cart.' : 'Build another sheet and it joins the same cart.');
    }
    if (el.addedMore) el.addedMore.textContent = op.porTamano ? 'Add more designs' : 'Build another sheet';
    el.addedCart.textContent = '';
    el.addedOverlay.classList.add('show');
    // Cuantas cosas lleva y cuanto suma, leido del carrito de verdad.
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || !el.addedCart) return;
        var k = c.item_count || 0;
        el.addedCart.textContent = k + (k === 1 ? ' item' : ' items') +
          ' in your cart · ' + money((c.total_price || 0) / 100);
        // Lo que cobra el carrito por estas lineas frente a lo que se enseno (un precio pudo cambiar con
        // la pagina abierta desde hace horas). Solo avisa, no bloquea (auditoria 23-oct).
        if (op.uuids && op.total != null) {
          var cobrado = 0, hay = false;
          (c.items || []).forEach(function (it) {
            var u = it.properties && it.properties._gsb_uuid;
            if (u && op.uuids.indexOf(String(u)) !== -1) { cobrado += it.final_line_price || 0; hay = true; }
          });
          if (hay && Math.abs(cobrado - Math.round(op.total * 100)) > 1) {
            el.addedCart.textContent += ' \u00b7 Heads up: your cart shows ' + money(cobrado / 100) +
              ' for this, not ' + money(op.total) + ', because a price changed since you opened the builder.';
          }
        }
      })
      .catch(function () { /* sin conexion al carrito no se inventa un total */ });
  }

  function cerrarAnadida() {
    if (el.addedOverlay) el.addedOverlay.classList.remove('show');
  }

  // "Armar otra hoja": se empieza de cero. Lo anterior ya esta a salvo en el carrito.
  function empezarOtraHoja() {
    cerrarAnadida();
    setSelection([]);
    itemNodes().forEach(function (n) { n.destroy(); });
    hojas = [null];
    hojaActual = 0;
    copiasActual = 1;
    largoAuto = true;
    sheetHIn = largoQueToca();
    rebuildHeightOptions();
    applyZoom(zoom);
    renderList();
    refreshAll();
    pushHistory();
    toast('Cart kept. Build your next sheet.');
  }

  // ------------------------------------------------- pedir sin descargar (via B)
  // El cliente ya no descarga el PNG para volver a subirlo en la ficha: el archivo viaja
  // con el pedido (ver gsb-checkout.js). Si algo falla, se cae al camino de siempre
  // (descargar + abrir la ficha): nadie se queda sin poder comprar.
  function checkoutDisponible() {
    var c = BRAND.checkout;
    if (!c || !c.ucKey || typeof GSBCheckout === 'undefined') return false;
    // desde file:// no hay carrito de Shopify contra el que hablar
    return location.protocol === 'https:' || location.protocol === 'http:';
  }

  function caerAlCaminoViejo(url, motivo) {
    if (motivo) console.warn('checkout directo no disponible:', motivo);
    window.open(url, '_blank', 'noopener');
    toast(currentType() && currentType().porTamano ? 'Order page open. Your print file is downloading now, upload it there.' : 'Order page open. Your sheet is downloading now, upload it there.');
    runExport();
  }

  // Si algo falla al pedir (corte de red, algo agotado...), nada de abrir pestanas solas (los navegadores
  // las bloquean si no hay clic) ni de volver a renderizar todo: se reabre el resumen con el motivo; su
  // boton reintenta sin volver a subir lo ya subido, y queda a mano el camino de siempre (auditoria 23-oct).
  var cancelPedido = null;     // AbortController del pedido en curso: Cancel corta las subidas de verdad
  var subidasHechas = {};      // hojas ya subidas, por su forma: un reintento no las vuelve a subir
  var firmaEnCarrito = null;   // lo ultimo que entro al carrito, para avisar si se anade otra vez lo mismo

  function motivoError(err) {
    var m = (err && err.message) || '';
    if (/ 422|sold out|only add|not available|unavailable/i.test(m)) {
      return 'We couldn\u2019t add it to your cart: something in this order just sold out or is no longer available. Your designs are safe.';
    }
    return 'We couldn\u2019t add it to your cart: the connection dropped while sending your files. Your designs are safe. ' +
      'Check your internet and press the button again: what already uploaded won\u2019t upload twice.';
  }

  function mostrarErrorPedido(op, err) {
    openExportSummary();
    if (!el.summaryOffer || !el.summaryOverlay.classList.contains('show')) { toast(motivoError(err)); return; }
    var caja = document.createElement('div');
    caja.className = 'offer-error';
    caja.setAttribute('role', 'alert');
    var tx = document.createElement('p');
    tx.textContent = motivoError(err);
    var otro = document.createElement('button');
    otro.type = 'button';
    otro.className = 'offer-error-alt';
    otro.textContent = 'Or download your file and order it from the product page';
    otro.addEventListener('click', function () {   // con clic: la pestana nueva no se bloquea
      closeExportSummary();
      caerAlCaminoViejo(op.ofertas[0].url, err && err.message);
    });
    caja.appendChild(tx);
    caja.appendChild(otro);
    el.summaryOffer.insertBefore(caja, el.summaryOffer.firstChild);
  }

  // Sube cada hoja y mete TODAS en el carrito en una sola llamada: o entran todas o
  // ninguna, para no dejar un pedido a medias.
  async function pedirYPagar(op, arte, conDisenos) {
    if (exporting) return;
    var n = conDisenos.length;
    var lineas;
    var firmaPedido = typeId + '|' + arte + '|' + firmaHojas(todasLasHojas()) + '|' +
      todasLasHojas().map(function (h) { return h.copias; }).join(',') + '|' + (op.conCorte ? 'corte' : '');
    if (firmaPedido === firmaEnCarrito &&
        !window.confirm('This same order is already in your cart. Add it again?')) return;
    cancelPedido = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var senal = cancelPedido ? cancelPedido.signal : null;
    try {
      lineas = await conOverlay(null, null, function () {
        return recorrerHojasAsync(async function (i) {
          var k = -1;
          conDisenos.forEach(function (x, j) { if (x.i === i) k = j; });
          if (k === -1) return null;
          var oferta = (op.mayoreo || op.porTamano) ? op.ofertas[0] : op.ofertas[k];
          var de = n > 1 ? (op.porTamano ? 'Print layout ' : 'Sheet ') + (k + 1) + ' of ' + n + ': ' : '';
          // la misma hoja (mismo producto, largo y disenos) ya subida en un intento anterior: no se repite
          var clave = [typeId, oferta.variantId, sheetHIn, JSON.stringify(itemNodes().map(snapOfNode))].join('|');
          var subido = subidasHechas[clave];
          if (!subido) {
            setOverlayText(de + (op.porTamano ? 'building your print file' : 'building your sheet'), 'Rendering at 300 DPI.');
            setProgress(0);
            var blob = await renderSheetBlob();
            setOverlayText(de + 'uploading', 'Sending ' + (blob.size / 1048576).toFixed(0) + ' MB. Keep this tab open.');
            setProgress(0);
            // El enlace lleva las medidas en pixeles y el uploader de UploadKit de este producto.
            subido = await GSBCheckout.subir(BRAND.checkout, blob, nombreArchivo(k, n), function (f) {
              if (exportCancelled) throw new Error('cancelled');
              setProgress(f);
            }, { wi: SHEET_W_IN * EXPORT_DPI, he: sheetHIn * EXPORT_DPI, subida: oferta.subida, signal: senal });
            subidasHechas[clave] = subido;
          }
          // Mismas propiedades, en el mismo orden, que una linea pedida desde la ficha con
          // Easify + UploadKit: el taller lee 'artwork-handling' (y 'Approve') y el enlace del
          // archivo donde lo busca siempre. Las que empiezan por "_" no las ve el cliente.
          var extra = {};
          if (oferta.propsEasify) {
            Object.keys(oferta.propsEasify).forEach(function (kk) {
              if (kk.charAt(0) !== '_') extra[kk] = oferta.propsEasify[kk];
            });
          }
          extra['artwork-handling'] = arte === 'review' ? 'Artwork Review' : 'Print As Is';
          if (arte !== 'review') extra['Approve'] = oferta.approve;
          if (oferta.propsEasify) {
            Object.keys(oferta.propsEasify).forEach(function (kk) {
              if (kk.charAt(0) === '_') extra[kk] = oferta.propsEasify[kk];
            });
          }
          extra['_gsb_sheet'] = SHEET_W_IN + 'x' + sheetHIn + ' in, 300 DPI' + (n > 1 ? ', sheet ' + (k + 1) + ' of ' + n : '');
          // By size con corte: la guia de corte de esta hoja, aparte (ver renderGuiaCorteBlob)
          var guia = null;
          if (op.porTamano && op.conCorte) {
            var gSub = subidasHechas[clave + '|guia'];
            if (!gSub) {
              setOverlayText(de + 'building the precut guide', 'Red cut lines for our team.');
              setProgress(0);
              var gBlob = await renderGuiaCorteBlob(k, n);
              setOverlayText(de + 'uploading the precut guide', 'Keep this tab open.');
              setProgress(0);
              gSub = await GSBCheckout.subir(BRAND.checkout, gBlob, nombreGuia(k, n), function (f) {
                if (exportCancelled) throw new Error('cancelled');
                setProgress(f);
              }, { wi: SHEET_W_IN * EXPORT_DPI, he: sheetHIn * EXPORT_DPI, subida: oferta.subida, signal: senal });
              subidasHechas[clave + '|guia'] = gSub;
            }
            guia = gSub.url;
          }
          return { variantId: oferta.variantId, url: subido.url, uuid: subido.uuid, extra: extra, subida: oferta.subida,
                   quantity: copiasDe(i), guia: guia };
        });
      });
      lineas = lineas.filter(Boolean);
      if (op.porTamano) {
        // By size: una linea por tramo de piezas (cantidad = unidades de 10 in2), con la lista de
        // disenos que cobra; las hojas ya armadas van adjuntas en la primera. Y el corte, aparte.
        var urlsT = lineas.map(function (l) { return l.url; });
        var uuidT = lineas.map(function (l) { return l.uuid; }).join(',');
        var arteT = arte === 'review' ? { 'artwork-handling': 'Artwork Review' } : { 'artwork-handling': 'Print As Is', 'Approve': op.ofertas[0].approve };
        var nuevas = op.calc.lineas.map(function (ln, j) {
          var extra = {};
          Object.keys(arteT).forEach(function (kk) { extra[kk] = arteT[kk]; });
          extra['Designs'] = ln.disenos.map(function (g) {
            var src = sources.get(g.s);
            return (src ? src.name : 'design') + ' ' + g.w.toFixed(2).replace(/\.?0+$/, '') + ' x ' +
              g.h.toFixed(2).replace(/\.?0+$/, '') + ' in x ' + g.n;
          }).join('; ');
          if (op.conCorte) extra['Precut'] = 'Yes';
          extra['_gsb_sheet'] = 'by size · ' + conDisenos.length + ' sheet' + (conDisenos.length === 1 ? '' : 's') + ' attached to the first line';
          return { variantId: ln.tramo.variantId, quantity: ln.unidades, subida: op.ofertas[0].subida,
                   urls: j === 0 ? urlsT : null, uuid: uuidT, extra: extra };
        });
        if (op.calc.corte) {
          // la guia de corte de cada hoja va en la linea del corte, con un nombre que no es el de un
          // archivo de impresion ("Uploaded ..."), para que nadie la imprima en film
          var extraC = { 'For': 'Precut of the by-size designs in this order' };
          var guias = lineas.map(function (l) { return l.guia; }).filter(Boolean);
          if (guias.length === 1) extraC['PRECUT guide'] = guias[0];
          else guias.forEach(function (u, g) { extraC['PRECUT guide ' + (g + 1)] = u; });
          nuevas.push({ variantId: op.tipo.corte.variantId, quantity: op.calc.corte.piezas, uuid: uuidT, extra: extraC });
        }
        lineas = nuevas;
      } else if (op.mayoreo) {
        // Wholesale: UNA linea por el largo total, con todas las hojas adjuntas (como los pedidos
        // de su ficha con varios archivos: "Uploaded required 1", "Uploaded required 2"...).
        var oM = op.ofertas[0];
        var medidas = conDisenos.map(function (x) {
          var c = copiasDe(x.i);
          return SHEET_W_IN + 'x' + x.h + (c > 1 ? ' x' + c : '');
        }).join(', ');
        var totalIn = 0, urls = [], copiasTxt = [];
        conDisenos.forEach(function (x, k) {
          var c = copiasDe(x.i);
          totalIn += x.h * c;
          for (var j = 0; j < c; j++) urls.push(lineas[k].url);   // una entrada por hoja a imprimir
          if (c > 1) copiasTxt.push('Sheet ' + (k + 1) + ' \u00d7 ' + c);
        });
        var extraM = { 'artwork-handling': 'Print As Is', 'Approve': oM.approve };
        if (copiasTxt.length) extraM['Sheet copies'] = copiasTxt.join(', ') + ' (same file repeated)';
        extraM['_gsb_sheet'] = urls.length + ' sheet' + (urls.length === 1 ? '' : 's') + ' (' + medidas + ' in) = ' +
          Math.round(totalIn) + ' in, billed as ' + oM.facturadoIn + ' in, 300 DPI';
        lineas = [{
          variantId: oM.variantId, subida: oM.subida, quantity: 1, urls: urls,
          uuid: lineas.map(function (l) { return l.uuid; }).join(','),
          extra: extraM
        }];
      }
      // Cancel pulsado mientras terminaba la ultima subida: no se anade nada
      if (exportCancelled) throw new Error('cancelled');
      op.uuids = lineas.map(function (l) { return String(l.uuid); });
      await conOverlay('Adding to your cart', 'Almost there.', function () {
        setProgress(1);
        return GSBCheckout.aniadirLineas(BRAND.checkout, lineas);
      }, false);
    } catch (err) {
      cancelPedido = null;
      if (err && err.message === 'cancelled') { toast('Order cancelled. Nothing was added to your cart.'); return; }
      console.error(err);
      mostrarErrorPedido(op, err);
      return;
    }
    cancelPedido = null;
    subidasHechas = {};
    firmaEnCarrito = firmaPedido;
    // Ya esta en el carrito. Se borra el autoguardado: si no, al volver le saldria
    // "restaurar tu hoja guardada" con la que acaba de comprar.
    try { Autosave.clearAll(); storedSourceIds = {}; } catch (e) { /* sin guardado, nada que borrar */ }
    mostrarAnadida(op, conDisenos.length);
  }

  // -------------------------------------------------------------- autosave
  var storageOk = false;
  var restorePending = false;
  var savedData = null;
  var storedSourceIds = {};
  var saveTimer = null;

  function scheduleSave() {
    // while the restore banner is open with an empty sheet, don't overwrite
    // the stored project; the first real change counts as "start fresh"
    if (restorePending) {
      if (!itemLayer.getChildren().length) return;
      dismissRestoreBanner(false);
    }
    clearTimeout(saveTimer);
    saveTimer = setTimeout(doSave, 1200);
  }

  function doSave() {
    var snap = snapshotState();
    var meta = { v: 1, at: Date.now(), sheetW: snap.sheetW, sheetH: snap.sheetH, spacing: getSpacing(), items: snap.items,
                 typeId: snap.typeId, auto: snap.auto, hoja: snap.hoja, hojas: snap.hojas };
    var refs = {};
    snap.hojas.forEach(function (h) { h.items.forEach(function (it) { refs[it.s] = true; }); });
    // Reload rebuilds each source from its ORIGINAL file, so record the exact
    // art rectangle of trimmed ones to replay the crop on restore.
    meta.artRects = {};
    Object.keys(refs).forEach(function (id) {
      var s = sources.get(id);
      if (s && s.trimmed) meta.artRects[id] = { x: s.artX, y: s.artY, w: s.artW, h: s.artH };
    });

    // Quitar el fondo tambien se rehace desde el original: se guarda CON QUE se hizo
    // (sensibilidad, color y las marcas del pincel), no el resultado. Es determinista,
    // asi que reproducirlo da exactamente la misma imagen y ocupa mucho menos que
    // guardar un PNG procesado por cada arte.
    meta.bgRemoved = {};
    Object.keys(refs).forEach(function (id) {
      var s = sources.get(id);
      if (!s || !s.bgRemoved) return;
      var r = { tol: s.bgTol, color: s.bgUsedColor || null };
      // La mascara del modelo tambien, o al recargar el recorte de una foto se
      // rehace por color y sale otra cosa. IndexedDB clona los typed arrays tal cual.
      if (s.bgAi) r.ai = { w: s.bgAi.w, h: s.bgAi.h, a: s.bgAi.a };
      if (s.bgMasks) {
        r.masks = {
          w: s.bgMasks.w, h: s.bgMasks.h,
          keep: s.bgMasks.keep, remove: s.bgMasks.remove, erase: s.bgMasks.erase
        };
      }
      meta.bgRemoved[id] = r;
    });
    var jobs = [Autosave.saveMeta(meta)];
    Object.keys(refs).forEach(function (id) {
      if (storedSourceIds[id]) return;
      var src = sources.get(id);
      if (!src || !src.file) return;
      storedSourceIds[id] = true;
      jobs.push(Autosave.putSource(id, {
        name: src.name,
        type: src.file.type || '',
        isVector: !!src.isVector,
        blob: src.file
      }));
    });
    Object.keys(storedSourceIds).forEach(function (id) {
      if (!refs[id]) {
        delete storedSourceIds[id];
        jobs.push(Autosave.deleteSource(id));
      }
    });
    Promise.all(jobs).then(function (results) {
      storageOk = results[0] === true;
    });
  }

  function dismissRestoreBanner(clearStored) {
    restorePending = false;
    savedData = null;
    el.restoreBanner.hidden = true;
    if (clearStored) {
      storedSourceIds = {};
      Autosave.clearAll();
    }
  }

  function initAutosave() {
    Autosave.loadAll().then(function (data) {
      if (!data) return;
      if (itemLayer.getChildren().length) return; // user already started working
      restorePending = true;
      savedData = data;
      var n = 0;
      (data.meta.hojas || [data.meta]).forEach(function (h) { n += (h.items || []).length; });
      var when = data.meta.at ? new Date(data.meta.at).toLocaleString() : '';
      el.restoreText.textContent = 'You have a saved sheet with ' + n + ' design' + (n === 1 ? '' : 's') +
        (when ? ' from ' + when : '') + '.';
      el.restoreBanner.hidden = false;
    });
  }

  function restoreSavedProject() {
    var data = savedData;
    if (!data) { dismissRestoreBanner(false); return; }
    var loads = Object.keys(data.sources).map(function (id) {
      var rec = data.sources[id];
      var f;
      try {
        f = new File([rec.blob], rec.name || 'artwork.png', { type: rec.type || rec.blob.type || '' });
      } catch (e) {
        f = rec.blob; // very old browsers: Blob without File constructor
        try { f.name = rec.name; } catch (e2) { /* readonly, keep going */ }
      }
      return loadSource(f, id).then(function (src) {
        sources.set(id, src);
      }, function () { /* broken source: its items are skipped below */ });
    });
    Promise.all(loads).then(function () {
      if (data.meta.spacing != null) el.spacingInput.value = data.meta.spacing;
      // Rehacer primero el quitado de fondo: cambia la imagen de impresion y por
      // tanto el rectangulo util. El recorte de abajo se aplica despues y manda.
      var bgs = data.meta.bgRemoved || {};
      Object.keys(bgs).forEach(function (id) {
        var s = sources.get(id);
        if (!s) return;
        var r = bgs[id];
        var masks = r.masks ? {
          w: r.masks.w, h: r.masks.h,
          keep: r.masks.keep, remove: r.masks.remove, erase: r.masks.erase
        } : null;
        applyBgRemoval(s, r.tol, masks, r.color, r.ai || null);
      });

      // replay each trimmed source's exact art rectangle
      var artRects = data.meta.artRects || {};
      Object.keys(artRects).forEach(function (id) {
        var s = sources.get(id);
        if (!s) return;
        var r = artRects[id];
        s.artX = r.x; s.artY = r.y; s.artW = r.w; s.artH = r.h;
        s.trimmed = true;
        rebuildDerived(s);
      });
      // 3) legacy saves (before artRects) recorded only trimmedIds
      (data.meta.trimmedIds || []).forEach(function (id) {
        var s = sources.get(id);
        if (s && !artRects[id]) applySourceTrim(s);
      });
      restoreState({ typeId: tipoDeEntrada || data.meta.typeId, sheetW: data.meta.sheetW || SHEET_W_IN, sheetH: data.meta.sheetH || sheetHIn,
                     auto: data.meta.auto, items: data.meta.items, hoja: data.meta.hoja, hojas: data.meta.hojas });
      undoStack.length = 0;
      redoStack.length = 0;
      if (currentType() && currentType().porTamano) rehacerTamano(tarjetasTamano());   // hojas limpias, que no se ven
      pushHistory(); // restored project becomes the undo baseline
      storedSourceIds = {};
      Object.keys(data.sources).forEach(function (id) { storedSourceIds[id] = true; });
      dismissRestoreBanner(false);
      fitZoom();
      // Decirlo si algo no volvio (auditoria 23-oct): un producto que ya no se vende, o arte que el
      // navegador no llego a guardar (cuota llena). Antes decia "Restored" igual.
      var guardadas = 0, faltan = 0;
      (data.meta.hojas || [{ items: data.meta.items || [] }]).forEach(function (h) {
        (h.items || []).forEach(function (it) { guardadas++; if (!sources.get(it.s)) faltan++; });
      });
      var avisos = [];
      if (!tipoDeEntrada && data.meta.typeId && !typeById(data.meta.typeId) && currentType()) {
        avisos.push('the product of your saved sheet is not available now, so it opened as ' + currentType().label);
      }
      if (faltan) avisos.push(faltan + ' of ' + guardadas + ' designs could not be restored: please upload them again');
      toast(avisos.length ? 'Restored your saved sheet, but ' + avisos.join(', and ') + '.' : 'Restored your saved sheet.');
    });
  }

  // -------------------------------------------------------------- wiring
  el.uploadBtn.addEventListener('click', function () { el.fileInput.click(); });
  el.fileInput.addEventListener('change', function () {
    addFiles(el.fileInput.files);
    el.fileInput.value = '';
  });

  // Drag and drop anywhere over the app. dragenter/dragleave fire on every
  // child element crossed, so a depth counter (the classic pattern) decides
  // when the pointer really left the window. Browsers disagree about
  // relatedTarget on dragleave (WebKit reports null inside the page too), so
  // the safety net for aborted drags is a timer instead: while a file drag
  // hovers the window the browser fires dragover about every 350 ms, and when
  // those stop (left the window, dropped elsewhere, Esc) the overlay clears.
  var dragDepth = 0;
  var dragIdleTimer = null;
  function isFileDrag(e) {
    var types = e.dataTransfer && e.dataTransfer.types;
    return !!types && Array.prototype.indexOf.call(types, 'Files') !== -1;
  }
  function clearDragState() {
    clearTimeout(dragIdleTimer);
    dragIdleTimer = null;
    dragDepth = 0;
    CAPA.classList.remove('dragging');
  }
  function pokeDragState() {
    CAPA.classList.add('dragging');
    clearTimeout(dragIdleTimer);
    dragIdleTimer = setTimeout(clearDragState, 600);
  }
  document.addEventListener('dragenter', function (e) {
    if (enPausa() || !isFileDrag(e)) return;
    e.preventDefault();
    dragDepth++;
    pokeDragState();
  });
  document.addEventListener('dragover', function (e) {
    if (enPausa() || !isFileDrag(e)) return;
    e.preventDefault();
    pokeDragState();
  });
  document.addEventListener('dragleave', function (e) {
    if (enPausa() || !isFileDrag(e)) return;
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) clearDragState();
  });
  document.addEventListener('drop', function (e) {
    if (enPausa()) return;
    e.preventDefault();
    clearDragState();
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
      addFiles(e.dataTransfer.files);
    }
  });

  // Paste: images from the system clipboard become uploads (screenshots!),
  // otherwise Ctrl+V pastes the internal clipboard (Ctrl+C on a selection).
  document.addEventListener('paste', function (e) {
    if (enPausa()) return;
    var t = activo();
    if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
    if (exporting) return;
    // Our marker in the system clipboard means the last copy was in-app:
    // paste the internal clipboard even though no image file is present.
    var txt = '';
    try { txt = e.clipboardData ? e.clipboardData.getData('text/plain') : ''; } catch (err) {}
    if (txt === CLIP_MARKER && clipboard && clipboard.items.length) {
      e.preventDefault();
      pasteClipboard();
      return;
    }
    var files = [];
    var items = e.clipboardData && e.clipboardData.items;
    if (items) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          var f = items[i].getAsFile();
          if (f && acceptFile(f)) files.push(f);
        }
      }
    }
    if (files.length) {
      e.preventDefault();
      addFiles(files);
      return;
    }
    if (clipboard) {
      e.preventDefault();
      pasteClipboard();
    }
  });

  el.sheetSelect.addEventListener('change', function () {
    if (el.sheetSelect.value === 'auto') {
      largoAuto = true;
      sheetHIn = largoQueToca();
      toast('Auto length: the sheet now fits your designs and grows as you add more.');
    } else {
      largoAuto = false;   // un largo elegido a mano manda
      sheetHIn = parseInt(el.sheetSelect.value, 10);
    }
    applyZoom(zoom);
    refreshAll();
    pushHistory();
  });

  if (el.typeSelect) {
    el.typeSelect.addEventListener('change', function () {
      var nuevo = typeById(el.typeSelect.value);
      if (!nuevo || nuevo.id === typeId) return;
      var anchoNuevo = nuevo.widthIn || SHEET_W_IN;

      // Si la hoja nueva es mas estrecha, avisar ANTES de aplicar: cambiar de tipo
      // puede dejar arte colgando fuera, y descubrirlo despues es peor.
      if (anchoNuevo < SHEET_W_IN) {
        var fuera = offWidthCount(anchoNuevo);
        if (fuera) {
          var falta = SHEET_W_IN - anchoNuevo;
          var ok = window.confirm(
            nuevo.label + ' is ' + falta + '" narrower: ' + anchoNuevo + '" instead of ' +
            SHEET_W_IN + '".\n\n' + fuera + (fuera === 1 ? ' design would' : ' designs would') +
            ' hang off the sheet.\n\nSwitch anyway? You can drag them back in or use Auto-arrange.');
          if (!ok) { el.typeSelect.value = typeId; return; }   // deshacer la eleccion
        }
      }

      typeId = nuevo.id;
      SHEET_W_IN = anchoNuevo;
      if (nuevo.porTamano) largoAuto = true;   // sin selector de largo en by size
      var prevH = sheetHIn;
      rebuildHeightOptions();   // cada tipo vende su propia escalera de largos
      updateWidthLabel();
      if (sheetHIn !== prevH && !largoAuto) {
        toast(nuevo.label + ' does not come in ' + prevH + '". Switched to ' + sheetHIn + '".');
      }
      fitZoom();          // la hoja cambio de ancho; reencuadrar para que se vea entera
      refreshAll();       // recalcula los avisos de "fuera de hoja" con el ancho nuevo
      refrescarHojas();   // las otras hojas, con la escalera de largos del producto nuevo
      // by size: el cliente ya no ve la hoja, asi que se rehace limpia (sin solapes ni nada fuera)
      if (nuevo.porTamano && (itemNodes().length || disenosEnOtrasHojas())) { rehacerTamano(tarjetasTamano()); return; }
      pushHistory();
    });
  }

  if (el.sizeGuideBtn) {
    el.sizeGuideBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleSizeGuide();
    });
    document.addEventListener('click', function (e) {
      if (el.sizeGuidePop && !el.sizeGuidePop.hidden &&
          objetivo(e) !== el.sizeGuideBtn && !el.sizeGuidePop.contains(objetivo(e)) &&
          !el.sizeGuideBtn.contains(objetivo(e))) {
        el.sizeGuidePop.hidden = true;
      }
    });
  }

  if (el.garmentBtn && el.garmentPop) {
    buildGarmentSwatches();
    el.garmentBtn.setAttribute('aria-expanded', 'false');
    el.garmentBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      togglePopover(el.garmentPop, el.garmentBtn, 80);
    });
    if (el.garmentCustom) {
      el.garmentCustom.addEventListener('input', function () {
        aplicarFondo({ tipo: 'color', c: el.garmentCustom.value });
      });
    }
    aplicarFondo(fondo);   // deja el boton y las muestras coherentes al arrancar
  }

  if (el.arrangeSettingsBtn && el.arrangePop) {
    el.arrangeSettingsBtn.setAttribute('aria-expanded', 'false');
    el.arrangeSettingsBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      togglePopover(el.arrangePop, el.arrangeSettingsBtn, 150);
    });
  }

  if (el.sheetInfoBtn) {
    el.sheetInfoBtn.setAttribute('aria-expanded', 'false');
    el.sheetInfoBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleSheetInfo();
    });
    document.addEventListener('click', function (e) {
      var pares = [[el.sheetInfoPop, el.sheetInfoBtn], [el.arrangePop, el.arrangeSettingsBtn],
                   [el.garmentPop, el.garmentBtn]];
      for (var iP = 0; iP < pares.length; iP++) {
        var po = pares[iP][0], bo = pares[iP][1];
        if (po && !po.hidden && bo && !bo.contains(objetivo(e)) && !po.contains(objetivo(e))) {
          po.hidden = true;
          bo.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  el.spacingInput.addEventListener('change', function () { scheduleSave(); });

  el.arrangeBtn.addEventListener('click', autoArrange);
  el.undoBtn.addEventListener('click', undo);
  el.redoBtn.addEventListener('click', redo);

  el.rotateBtn.addEventListener('click', rotateSelection90);
  el.dupBtn.addEventListener('click', duplicateSelection);

  el.fillBtn.addEventListener('click', fillSheet);
  el.alignLeftBtn.addEventListener('click', function () { alignSelection('left'); });
  el.alignTopBtn.addEventListener('click', function () { alignSelection('top'); });
  el.centerBtn.addEventListener('click', centerSelection);
  if (el.distHBtn) el.distHBtn.addEventListener('click', function () { distributeSelection('h'); });
  if (el.distVBtn) el.distVBtn.addEventListener('click', function () { distributeSelection('v'); });
  if (el.helpBtn) el.helpBtn.addEventListener('click', function () { el.helpOverlay.classList.add('show'); });
  if (el.helpClose) el.helpClose.addEventListener('click', function () { el.helpOverlay.classList.remove('show'); });
  if (el.helpOverlay) el.helpOverlay.addEventListener('click', function (e) {
    if (e.target === el.helpOverlay) el.helpOverlay.classList.remove('show');
  });
  el.groupBtn.addEventListener('click', function () {
    if (unitsOfSelection().length >= 2) groupSelection();
    else ungroupSelection();
  });

  el.deleteBtn.addEventListener('click', function () {
    if (selection.length) deleteNodes(selection.slice());
  });

  if (el.addedMore) el.addedMore.addEventListener('click', empezarOtraHoja);
  if (el.addedCheckout) {
    el.addedCheckout.addEventListener('click', function () { location.href = '/checkout'; });
  }

  if (el.bgOverlay) {
    el.bgTol.addEventListener('input', function () {
      el.bgTolVal.textContent = el.bgTol.value;
      recomputeBgResult();
      drawBgView();
    });
    el.bgApply.addEventListener('click', applyBgDialog);
    el.bgCancel.addEventListener('click', closeBgDialog);
    if (el.bgClose) el.bgClose.addEventListener('click', closeBgDialog);
    el.bgOverlay.addEventListener('click', function (e) {
      if (e.target === el.bgOverlay) closeBgDialog();
    });
    var mid = function () { return { x: el.bgPreview.width / 2, y: el.bgPreview.height / 2 }; };
    if (el.bgZoomIn) el.bgZoomIn.addEventListener('click', function () { var m = mid(); setBgZoom(1.4, m.x, m.y); });
    if (el.bgZoomOut) el.bgZoomOut.addEventListener('click', function () { var m = mid(); setBgZoom(1 / 1.4, m.x, m.y); });
    if (el.bgZoomFit) el.bgZoomFit.addEventListener('click', function () {
      bgView.scale = 1; bgView.cx = bgBase.w / 2; bgView.cy = bgBase.h / 2; drawBgView();
    });
    if (el.bgModePan) el.bgModePan.addEventListener('click', function () { setBgMode('pan'); });
    if (el.bgModePick) el.bgModePick.addEventListener('click', function () { setBgMode('pick'); });

    // Recorte automatico. Es una ACCION, no un modo: se ejecuta y deja un resultado
    // que luego se retoca con los pinceles de siempre.
    if (el.bgAi) {
      el.bgAi.addEventListener('click', async function () {
        if (!bgBase || el.bgAi.disabled) return;
        if (typeof GSBAiBg === 'undefined') {
          toast('The automatic cut-out is not available in this build.');
          return;
        }
        // Segunda pulsacion: quitar el recorte y volver al de siempre. La mascara
        // del modelo no cabe en el historial del pincel (hasta 810 KB por paso, y
        // el historial guarda 20), asi que deshacerla es esto.
        if (bgAiMask) {
          bgAiMask = null;
          recomputeBgResult();
          drawBgView();
          pintarEstadoIa();
          toast('Back to the color-based removal.');
          return;
        }
        var etiqueta = el.bgAi.textContent;
        el.bgAi.disabled = true;
        el.bgAi.classList.add('trabajando');
        var aviso = function (t) { el.bgAi.textContent = t; toast(t, 4000); };
        try {
          var work = new ImageData(bgBase.w, bgBase.h);
          work.data.set(bgBase.data.data);
          bgAiMask = await GSBAiBg.mascara((BRAND.ai || {}), work, aviso);
          recomputeBgResult();
          drawBgView();
          pintarEstadoIa();
          toast('Cut out automatically. Use the brushes to fix anything it missed.', 5000);
        } catch (err) {
          console.error(err);
          // El flood sigue ahi: que falle esto no deja al cliente sin salida.
          window.alert('The automatic cut-out could not run: ' +
            (err && err.message ? err.message : err) +
            '\n\nYou can still remove a solid background with the sensitivity slider and the brushes.');
        } finally {
          el.bgAi.disabled = false;
          el.bgAi.classList.remove('trabajando');
          el.bgAi.textContent = etiqueta;
        }
      });
    }
    if (el.bgModeKeep) el.bgModeKeep.addEventListener('click', function () { setBgMode('keep'); });
    if (el.bgModeRemove) el.bgModeRemove.addEventListener('click', function () { setBgMode('remove'); });
    if (el.bgModeErase) el.bgModeErase.addEventListener('click', function () { setBgMode('erase'); });
    if (el.bgClearMarks) el.bgClearMarks.addEventListener('click', clearBgMarks);
    if (el.bgUndo) el.bgUndo.addEventListener('click', bgUndoAction);
    if (el.bgRedo) el.bgRedo.addEventListener('click', bgRedoAction);
    if (el.bgShowMarks) el.bgShowMarks.addEventListener('change', function () {
      bgShowMarks = el.bgShowMarks.checked;
      drawBgView();
    });
    // Deslizador y campo numerico son dos vistas del MISMO valor. Por dentro se
    // guarda el radio; fuera se ensena el diametro, que es como se piensa el tamano
    // de un pincel. Un solo sitio hace la conversion para que no puedan discrepar.
    function fijarPincel(radio, quienEscribe) {
      var min = parseInt(el.bgBrushSize.min, 10) || 2;
      var max = parseInt(el.bgBrushSize.max, 10) || 120;
      bgBrush = Math.max(min, Math.min(max, Math.round(radio) || min));
      if (quienEscribe !== 'slider') el.bgBrushSize.value = String(bgBrush);
      if (quienEscribe !== 'num' && el.bgBrushVal) el.bgBrushVal.value = String(bgBrush * 2);
      mostrarPincelUnMomento();   // verlo mientras se ajusta, no al pintar
    }

    if (el.bgBrushSize) el.bgBrushSize.addEventListener('input', function () {
      fijarPincel(parseInt(el.bgBrushSize.value, 10), 'slider');
    });

    if (el.bgBrushVal) {
      // 'input' mientras teclea: si escribe 7 y todavia no ha acabado, no se le
      // corrige a 8 en mitad del numero. El ajuste al rango se hace al salir.
      el.bgBrushVal.addEventListener('input', function () {
        var d = parseInt(el.bgBrushVal.value, 10);
        if (isNaN(d)) return;
        fijarPincel(d / 2, 'num');
      });
      el.bgBrushVal.addEventListener('change', function () {
        var d = parseInt(el.bgBrushVal.value, 10);
        fijarPincel(isNaN(d) ? bgBrush : d / 2, null);
      });
      // las flechas del teclado suben de 2 en 2 (1 px de radio), que es el paso fino
      el.bgBrushVal.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        fijarPincel(bgBrush + (e.key === 'ArrowUp' ? 1 : -1), null);
      });
    }
    // El circulo sigue al puntero, como en cualquier editor de imagen: asi el tamano
    // del pincel se ve SIEMPRE, no solo cuando ya has pintado.
    el.bgPreview.addEventListener('mousemove', function (e) {
      if (!bgResult || bgMode === 'pan' || bgMode === 'pick') return;
      var r = el.bgPreview.getBoundingClientRect();
      bgCursor = {
        x: (e.clientX - r.left) * (el.bgPreview.width / r.width),
        y: (e.clientY - r.top) * (el.bgPreview.height / r.height)
      };
      if (bgCursorTimer) { clearTimeout(bgCursorTimer); bgCursorTimer = null; }
      drawBgView();
    });
    el.bgPreview.addEventListener('mouseleave', function () {
      if (!bgCursor) return;
      bgCursor = null;
      drawBgView();
    });

    // scroll to zoom at the pointer
    el.bgPreview.addEventListener('wheel', function (e) {
      if (!bgResult) return;
      e.preventDefault();
      var r = el.bgPreview.getBoundingClientRect();
      var px = (e.clientX - r.left) * (el.bgPreview.width / r.width);
      var py = (e.clientY - r.top) * (el.bgPreview.height / r.height);
      setBgZoom(e.deltaY < 0 ? 1.15 : 1 / 1.15, px, py);
    }, { passive: false });
    // pointer drag: paint (brush modes) or pan (pan mode). Painting updates the
    // marks live; the flood result re-runs when the stroke ends (fast enough).
    var panning = null;
    el.bgPreview.addEventListener('mousedown', function (e) {
      if (!bgResult) return;
      e.preventDefault();
      if (bgMode === 'pick') {
        // Se lee del ORIGINAL (bgBase), no del resultado: en el resultado el fondo
        // ya esta borrado y solo se leerian ceros.
        var q = bgCanvasToBase(e.clientX, e.clientY);
        var bx = Math.max(0, Math.min(bgBase.w - 1, Math.round(q.x)));
        var by = Math.max(0, Math.min(bgBase.h - 1, Math.round(q.y)));
        var i = (by * bgBase.w + bx) * 4, dd = bgBase.data.data;
        bgPickColor = { r: dd[i], g: dd[i + 1], b: dd[i + 2] };
        pintarColorFondo();
        recomputeBgResult();
        drawBgView();
        setBgMode('pan');        // un solo uso: se vuelve a navegar
        return;
      }
      if (bgMode === 'pan') {
        if (bgView.scale <= 1) return;
        panning = { x: e.clientX, y: e.clientY };
        el.bgPreview.classList.add('grabbing');
      } else {
        pushBgHistory();          // one undo entry per stroke
        bgStroking = true;         // marks stay visible during the stroke
        var p = bgCanvasToBase(e.clientX, e.clientY);
        bgPaint(p.x, p.y);
        drawBgView();
      }
    });
    window.addEventListener('mousemove', function (e) {
      if (panning) {
        var r = el.bgPreview.getBoundingClientRect();
        var eff = bgFitScale() * bgView.scale * (r.width / el.bgPreview.width);
        bgView.cx -= (e.clientX - panning.x) / eff;
        bgView.cy -= (e.clientY - panning.y) / eff;
        panning = { x: e.clientX, y: e.clientY };
        drawBgView();
      } else if (bgStroking) {
        var p = bgCanvasToBase(e.clientX, e.clientY);
        bgPaint(p.x, p.y);
        drawBgView();
      }
    });
    window.addEventListener('mouseup', function () {
      if (bgStroking) {
        bgStroking = false;        // stroke done -> marks hide, clean result shows
        recomputeBgResult();
        drawBgView();
      }
      panning = null;
      el.bgPreview.classList.remove('grabbing');
    });
  }

  el.clearBtn.addEventListener('click', function () {
    var otras = disenosEnOtrasHojas();
    if (!itemLayer.getChildren().length && !otras) return;
    if (otras || hojas.length > 1) {
      if (!window.confirm('Remove all designs from all ' + hojas.length + ' sheets and start over?')) return;
      setSelection([]);
      itemNodes().forEach(function (n) { n.destroy(); });
      hojas = [null];
      hojaActual = 0;
      copiasActual = 1;
      renderList();
      refreshAll();
      pushHistory();
      return;
    }
    if (!window.confirm('Remove all designs from the sheet?')) return;
    deleteNodes(itemNodes());
  });

  el.zoomInBtn.addEventListener('click', function () { applyZoom(zoom * 1.25); });
  el.zoomOutBtn.addEventListener('click', function () { applyZoom(zoom / 1.25); });
  el.zoomFitBtn.addEventListener('click', fitZoom);
  el.zoomLevelBtn.addEventListener('click', function () { applyZoom(96); });

  // Ctrl + wheel zoom, anchored to the pointer. Works in world coordinates so
  // the scroll padding, the centering margin, and the zoom-dependent stage
  // padding are all accounted for exactly.
  el.scroll.addEventListener('wheel', function (e) {
    if (!e.ctrlKey) return;
    e.preventDefault();
    var srect = el.scroll.getBoundingClientRect();
    var hrect = el.holder.getBoundingClientRect();
    // world inches under the pointer, before the zoom change
    var wx = (e.clientX - hrect.left - padIn * zoom) / zoom;
    var wy = (e.clientY - hrect.top - padIn * zoom) / zoom;
    applyZoom(zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
    // the holder may have moved (margin auto recenters); measure it again
    var hrect2 = el.holder.getBoundingClientRect();
    var ox = hrect2.left - srect.left + el.scroll.scrollLeft;
    var oy = hrect2.top - srect.top + el.scroll.scrollTop;
    el.scroll.scrollLeft = ox + (padIn + wx) * zoom - (e.clientX - srect.left);
    el.scroll.scrollTop = oy + (padIn + wy) * zoom - (e.clientY - srect.top);
  }, { passive: false });

  el.exportBtn.addEventListener('click', openExportSummary);
  el.summaryCancel.addEventListener('click', closeExportSummary);
  el.summaryDownload.addEventListener('click', function () {
    closeExportSummary();
    runExport();
  });
  el.cancelExportBtn.addEventListener('click', function () {
    exportCancelled = true;
    if (cancelPedido) cancelPedido.abort();   // corta tambien las subidas que estan en el aire
  });

  el.restoreYes.addEventListener('click', restoreSavedProject);
  el.restoreNo.addEventListener('click', function () { dismissRestoreBanner(true); });

  if (el.trimOverlay) {
    el.trimYes.addEventListener('click', function () { closeTrimAsk(true); });
    el.trimNo.addEventListener('click', function () { closeTrimAsk(false); });
  }

  document.addEventListener('keydown', function (e) {
    if (enPausa()) return;
    var t = objetivo(e);
    if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
    // By size no ensena la hoja: los atajos del lienzo (borrar, mover, cortar...) actuarian a ciegas.
    if (currentType() && currentType().porTamano && e.key !== 'Escape' &&
        !((e.ctrlKey || e.metaKey) && /^[zyv]$/i.test(e.key))) return;

    // Con el quitador de fondo abierto el teclado es SUYO. Sin esto, Esc no lo
    // cerraba (y el boton de cerrar lo promete), Ctrl+Z deshacia movimientos de la
    // HOJA detras del dialogo en vez de pinceladas, y Supr borraba el diseno
    // seleccionado sin que el cliente lo viera. Nada de la hoja recibe teclas aqui.
    if (el.bgOverlay && el.bgOverlay.classList.contains('show')) {
      if (e.key === 'Escape') { e.preventDefault(); closeBgDialog(); return; }
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        var kb = e.key.toLowerCase();
        if (kb === 'z') { e.preventDefault(); if (e.shiftKey) bgRedoAction(); else bgUndoAction(); return; }
        if (kb === 'y') { e.preventDefault(); bgRedoAction(); return; }
      }
      return;
    }

    if (e.key === 'Escape') {
      if (el.addedOverlay && el.addedOverlay.classList.contains('show')) { cerrarAnadida(); return; }
      // un popover abierto se cierra antes que nada: es lo ultimo que abrio el cliente
      var abiertos = [[el.garmentPop, el.garmentBtn], [el.arrangePop, el.arrangeSettingsBtn],
                      [el.sheetInfoPop, el.sheetInfoBtn]];
      for (var iE = 0; iE < abiertos.length; iE++) {
        if (abiertos[iE][0] && !abiertos[iE][0].hidden) {
          abiertos[iE][0].hidden = true;
          if (abiertos[iE][1]) abiertos[iE][1].setAttribute('aria-expanded', 'false');
          return;
        }
      }
      if (el.helpOverlay && el.helpOverlay.classList.contains('show')) { el.helpOverlay.classList.remove('show'); return; }
      if (el.trimOverlay && el.trimOverlay.classList.contains('show')) { closeTrimAsk(false); return; }
      if (el.summaryOverlay.classList.contains('show')) { closeExportSummary(); return; }
      if (marquee) { cancelMarquee(); return; }
      setSelection([]);
      return;
    }
    if (e.key === '?' && el.helpOverlay) {
      e.preventDefault();
      el.helpOverlay.classList.toggle('show');
      return;
    }
    if (exporting) return;

    var mod = e.ctrlKey || e.metaKey;
    if (mod && !e.altKey) {
      var k = e.key.toLowerCase();
      if (k === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      if (k === 'y') { e.preventDefault(); redo(); return; }
      if (k === 'c') {
        var nCopied = copySelection();
        if (nCopied) toast('Copied ' + nCopied + ' design' + (nCopied === 1 ? '' : 's') + '. Ctrl+V pastes.');
        return;
      }
      if (k === 'x') { e.preventDefault(); cutSelection(); return; }
      if (k === 'd') { e.preventDefault(); duplicateSelection(); return; }
      if (k === 'g') {
        e.preventDefault();
        if (e.shiftKey) ungroupSelection(); else groupSelection();
        return;
      }
      if (k === 'a') {
        e.preventDefault();
        setSelection(itemNodes().filter(function (n) { return !isLocked(n); }));
        return;
      }
      return; // let Ctrl+V reach the paste event above
    }

    if ((e.key === 'Delete' || e.key === 'Backspace') && selection.length) {
      e.preventDefault();
      deleteNodes(selection.slice());
      return;
    }
    if ((e.key === 'r' || e.key === 'R') && selection.length && !e.altKey) {
      e.preventDefault();
      rotateSelection90();
      return;
    }
    if (selection.length && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) !== -1) {
      e.preventDefault();
      var step = e.shiftKey ? 1 : 0.1;
      var dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      var dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
      selection.forEach(function (n) { n.move({ x: dx, y: dy }); });
      refreshAll();
      pushHistorySoon();
    }
  });

  window.addEventListener('beforeunload', function (e) {
    // once the autosave works, closing the tab is safe: no nagging dialog
    if (exporting || (itemLayer.getChildren().length && !storageOk)) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  // ---------------------------------------------------------------- init
  (function init() {
    // Selector de TIPO: que producto se esta armando. El ancho sale de ahi y se
    // pinta al lado como dato. Cuando hay selector, la opcion de largo dice solo el
    // largo; sin el (copia vieja de index.html) mantiene la etiqueta 'ancho x largo'.
    // Antes de pintar nada: si venimos de una ficha con ?variant=, ese es el tipo y
    // el largo que el cliente ya eligio alla. Se aplica aqui para que los selectores
    // nazcan bien puestos, sin un parpadeo de "24x24 -> ah no, 24x96".
    var deFicha = tipoYLargoDeVariante(varianteDeLaURL());
    if (deFicha) {
      typeId = deFicha.type.id;
      tipoDeEntrada = deFicha.type.id;
      SHEET_W_IN = deFicha.type.widthIn || SHEET_W_IN;
      if (deFicha.lenIn) {
        sheetHIn = deFicha.lenIn;
        largoAuto = false;   // el largo que eligio en la ficha manda; "Auto" sigue en la lista
      }
    }

    separateType = !!el.typeSelect && TYPES.length > 0;
    if (separateType) {
      TYPES.forEach(function (t) {
        var opt = document.createElement('option');
        opt.value = t.id;
        opt.textContent = t.label + (t.agotado ? ' (temporarily sold out)' : '');
        el.typeSelect.appendChild(opt);
      });
      el.typeSelect.value = typeId;
    }
    updateWidthLabel();
    rebuildHeightOptions();
    buildSheetInfo();
    buildSizeGuide();
    fitZoom();
    refreshAll();
    renderList();
    pintarHojas();
    pushHistory();   // baseline empty state for undo
    initAutosave();
    if (deFicha) {
      toast(deFicha.type.porTamano ? 'Set to ' + deFicha.type.label + ': priced per design, by size.'
        : 'Set to ' + deFicha.type.label + ', ' + SHEET_W_IN + '" \u00d7 ' + sheetHIn + '".');
    }
  })();
})();
